/**
 * EGA Collection Filter System v2
 * Pure vanilla JS — no dependencies, no app required.
 *
 * Tag prefix conventions (single underscore, matching tag-filters-v2.py):
 *   style_       Style/Period
 *   color_       Color Family
 *   mat_         Material
 *   avail_       Availability
 *   customizable Boolean tag (no prefix)
 *   Stock Items  In Stock (existing Shopify tag)
 *   shape_       Table/dining shape
 *   seats_       Seating capacity
 *   extension_   Extension yes/no (dining only)
 *   top_         Top material (dining only)
 *   base_        Base type (dining only)
 *   upholstery_  Upholstery (seating)
 *   finish_      Finish (storage)
 *   hw_          Hardware (storage)
 *   size_        Bed size
 *   hb_          Headboard type
 *   light_       Fixture type (lighting)
 *   lfinish_     Lighting finish
 *   mshape_      Mirror shape
 *   mtype_       Mirror type
 *   mframe_      Mirror frame
 */
(function () {
  'use strict';

  // JS-only chrome (toolbar chips, drawer, active-chips row) is hidden by CSS until
  // this class lands, so a no-JS render shows only the server grid.
  document.documentElement.classList.add('ega-cf-js');

  // ── Tag group definitions ───────────────────────────────────────────────���
  // includeCollectionTypes: only show when collection handle contains one of these keywords
  // excludeCollectionTypes: hide when collection handle contains one of these keywords
  // type 'boolean': single checkbox (no prefix matching)
  // type 'prefix': show all tags starting with group.prefix

  /* ── Style grouping: map granular style tags → broad categories ── */
  /* Tags use double underscore (style__) and hyphenated names */
  /* Also match century tags and keywords per Dan's direction */
  var STYLE_TRADITIONAL = [
    'style__georgian','style__chippendale','style__regency','style__neoclassical',
    'style__gothic','style__baroque','style__louis-xv','style__louis-xvi',
    'style__french-provincial','style__gustavian','style__english','style__country',
    'style__traditional','style__classic',
    // Single underscore variants (legacy)
    'style_georgian','style_chippendale','style_regency','style_neoclassical',
    'style_gothic','style_baroque','style_louisxv','style_louisxvi',
    'style_frenchprovincial','style_gustavian','style_english','style_country',
    'style_traditional',
    // NOTE: Century tags ('18th century', '19th century') removed — they describe manufacturing era, not style.
    // Traditional pieces are identified by their style tags instead.
  ];
  var STYLE_MODERN = [
    'style__modern','style__mid-century','style__scandinavian','style__art-deco',
    'style__hollywood-regency','style__organic-modern','style__industrial',
    'style__brutalist','style__coastal','style__transitional','style__contemporary',
    // Single underscore variants (legacy)
    'style_midcenturymodern','style_scandinavianmodern','style_artdeco',
    'style_hollywoodregency','style_organicmodern','style_industrial',
    'style_brutalist','style_coastal','style_transitional',
    // NOTE: '20th century' removed — it's a manufacturing era tag, not a style indicator.
    // Many traditional reproductions (Regency, French Provincial) are tagged 20th Century.
  ];
  var STYLE_RUSTIC = [
    'style__rustic','style__farmhouse','style__country','style__provincial',
    'style_rustic','style_farmhouse','style_country'
  ];

  // Finish classification: map color tags to finish categories
  var FINISH_LIGHT_WOOD = ['color__oak','color__birch','color__pine','color__maple','color__ash','color__teak','color__natural','color__blonde','color__driftwood','color__whitewash','color__cerused','color__limed','color__light','light__wood','material__oak','material__pine','material__birch','material__ash','material__teak','material__maple'];
  /* Cane & Rattan pulled out of Light Wood into its own bucket (Dino, 2026-08-07):
     ~40 pieces catalogue-wide and a look customers shop for. Sits above Painted so a
     rattan piece with a painted frame or an upholstered cushion still reads as rattan. */
  var FINISH_CANE = ['material__rattan','material__cane','material__bamboo','color__rattan','color__cane','finish__rattan'];
  var FINISH_DARK_WOOD = ['color__walnut','color__fruitwood','color__ebony','color__mahogany','color__espresso','color__chocolate','color__cherry','color__charcoal','color__dark','color__rosewood','color__mink','dark__wood','material__walnut','material__mahogany','material__cherry','material__solid-hardwood'];
  var FINISH_PAINTED = ['color__white','color__grey','color__green','color__blue','color__cream','color__sage','color__gray','color__black','color__painted','color__lacquer','color__gold-leaf','color__silver-leaf','color__ivory','color__red','color__navy','material__painted','finish__painted','painted__finish','material__velvet','material__upholstered'];
  var FINISH_LINEN = ['material__linen','finish__linen','color__linen'];
  var FINISH_STONE = ['color__stone','color__marble','material__marble'];
  var FINISH_METAL = ['color__metal','color__gold','color__silver','color__bronze','color__brass','material__brass','material__iron'];
  var FINISH_LEATHER = ['color__leather','material__leather-wrapped','finish__leather','material__leather'];

  /* ── Finish/Material overrides — Dino's photo review, 2026-08-07 ──
     Source of truth: worksheets/finish-review-2026-08-07.csv (regenerate this
     block from it; don't hand-edit). 500 pieces the tag inference below gets
     wrong. Two rules the tags cannot express: a seat's finish is the WOOD, not
     the fabric it is upholstered in; and the spurious color__red / mahogany-on-oak
     / ebony-on-painted tags in the catalogue lie about the piece.
     Keyed by product handle. Clearing an entry falls back to inference. */
  var EGA_FINISH_OVERRIDES = {
    '19th-century-style-curved-back-sofa':'linen', '5-drawer-transitional-chest-in-maple':'light',
    'american-empire-armchair':'painted', 'american-empire-side-chair':'painted',
    'anglo-indian-hand-carved-storage-trunk-64-inches':'dark',
    'antique-bronzed-console-table-with-brown-leather':'leather', 'antique-style-chest-of-drawers':'dark',
    'antique-style-shield-back-dining-arm-chair':'painted', 'antique-style-shield-back-dining-side-chair':'painted',
    'antiqued-17th-century-style-writing-table':'dark', 'antiqued-17th-century-style-writing-table-open-box':'dark',
    'antiqued-bistro-table-with-scrolled-pedestal-42-inch':'painted',
    'antiqued-italian-trestle-dining-table-84-turned-pedestal-base':'light',
    'antiqued-oak-bistro-table-with-turned-pedestal-33-inch':'light', 'aqua-mist-three-drawer-chest':'painted',
    'arched-country-wood-king-bed':'dark', 'arctic-white-upholstered-king-size-bed':'linen',
    'arctic-white-upholstered-queen-size-bed':'linen', 'art-deco-burl-chest':'light',
    'art-deco-carved-credenza-dark-walnut-with-gold-leaf-doors':'painted', 'art-deco-dining-armchair':'dark',
    'art-deco-dining-side-chair':'dark', 'art-deco-shagreen-side-chest-in-leaf-green-on-brass':'leather',
    'art-deco-shagreen-writing-desk-in-white-with-brass-ring-pulls':'leather',
    'art-deco-style-burl-wood-dresser':'dark', 'art-deco-style-desk':'light', 'art-deco-style-dining-chair-1':'dark',
    'art-deco-style-scroll-sofa':'linen', 'art-deco-upholstered-bed':'dark', 'arts-crafts-oak-coffee-table':'light',
    'batavia-black-rustic-country-sideboard':'painted', 'betanie-upholstered-sofa':'linen',
    'bistro-dining-chairs-in-black-bentwood-with-cane-seats-sets-of-2':'painted',
    'black-art-deco-dining-armchair':'painted', 'black-art-deco-dining-side-chair':'painted',
    'black-chevron-tufted-upholstered-king-bed':'linen', 'black-chevron-tufted-upholstered-queen-bed':'linen',
    'black-french-sideboard':'painted', 'black-mid-century-modern-dining-armchair':'painted',
    'bleached-oak-expanding-jupe-dining-table-85-1-2':'light', 'block-front-shell-carved-chest-of-drawers':'dark',
    'blond-pine-trestle-side-table':'light', 'blue-leather-wingback-dining-chairs-high-back-sets-of-2':'light',
    'bobbin-leg-console-table-in-dark-walnut-finish':'dark', 'bohemian-rattan-side-chair':'cane',
    'brass-round-cocktail-table-on-wheels-tiered-glass':'metal', 'burl-wood-chest-of-drawers':'dark',
    'callisto-upholstered-sofa':'linen', 'campaign-form-dresser':'leather',
    'campaign-nesting-tables-tooled-leather-iron-set-of-3':'leather', 'campaign-style-desk':'dark',
    'campaign-x-frame-bench-saddle-leather-iron-faux-bamboo':'leather',
    'campaign-x-frame-writing-desk-in-stone-grey':'painted',
    'cane-back-dining-chairs-in-gray-oak-with-taupe-seats-sets-of-2':'painted',
    'cane-back-dining-chairs-in-white-with-taupe-velvet-sets-of-2':'painted',
    'cantilever-dining-chairs-in-canvas-and-steel-sets-of-2':'metal',
    'caramel-leather-sofa-with-low-back-71-inch':'leather', 'carved-scallop-sideboard-in-warm-tan-79-inches':'light',
    'cerused-grey-pine-side-table-with-open-shelf':'painted', 'chest-of-drawers-antique-italian-style':'dark',
    'chevron-tufted-upholstered-king-bed':'linen', 'chevron-tufted-upholstered-queen-bed':'linen',
    'chinese-chippendale-cockpen-dining-chairs':'dark', 'chinese-chippendale-dining-chair':'dark',
    'chinese-chippendale-faux-bamboo-dining-chairs-in-white-sets-of-2':'painted',
    'chinoiserie-cockpen-dining-chairs':'dark', 'chippendale-bamboo-dining-chair-ebonized':'painted',
    'chippendale-chinoiserie-serpentine-chest':'painted', 'chippendale-faux-bamboo-dining-chair':'dark',
    'classic-antique-dining-chair':'dark', 'classic-art-deco-dining-chair':'dark',
    'classic-black-display-cabinet':'painted', 'classic-bombe-chest':'light',
    'classic-chinese-brown-wood-chair':'light', 'classic-dining-chair':'light',
    'classic-english-tall-dresser':'dark', 'classic-french-black-desk':'painted',
    'classic-french-dining-chair':'light', 'classic-french-dining-chair-in-cherry-finish':'light',
    'classic-modern-upholstered-queen-bed-with-wings':'linen', 'classic-narrow-side-table-mahogany-finish-1':'dark',
    'classic-rustic-console-table-mahogany-finish-1':'dark', 'classic-skirted-dining-chair':'linen',
    'classic-trellis-dining-armchair':'dark', 'classic-trellis-dining-side-chair':'painted',
    'classic-tufted-3-seater-sofa':'linen', 'classic-tufted-love-seat':'linen',
    'classic-upholstered-dining-armchair':'dark', 'classic-upholstered-dining-chair':'dark',
    'classic-upholstered-scoop-back-armchair':'light', 'classic-upholstered-scoop-back-side-chair':'light',
    'classic-upholstered-scoop-back-side-chair-dark-rowan':'dark',
    'classic-white-paneled-buffet-75-inches-wide-15-deep':'painted', 'classical-dining-chair':'dark',
    'coastal-bamboo-coffee-table':'light', 'coastal-bamboo-console-table':'light',
    'coastal-bamboo-end-table':'light', 'coastal-blue-upholstered-storage-bench':'linen',
    'coastal-breeze-desk':'painted', 'coastal-breeze-dresser':'painted', 'coastal-rattan-dining-chair':'cane',
    'coastal-rattan-scalloped-side-table':'cane', 'coastal-rattan-settee':'cane',
    'coastal-rattan-settee-with-botanical-upholstery':'cane',
    'coastal-rattan-settee-with-moss-green-upholstery':'cane',
    'coastal-slipcovered-dining-armchair-green-upholstery':'linen',
    'coastal-slipcovered-dining-armchair-white-upholstery':'linen',
    'coastal-slipcovered-dining-chair-white-upholstery':'linen', 'coastal-sofa-in-textured-chenille':'linen',
    'contemporary-blue-leather-sofa-with-track-arms':'leather',
    'contemporary-carved-credenza-white-oak-with-silver-leaf-doors':'light',
    'contemporary-coastal-rattan-barrel-dining-chair':'cane',
    'contemporary-credenza-94-embossed-white-leather-on-iron-base':'leather', 'contemporary-dining-chair':'light',
    'contemporary-parquet-sideboard-86-warm-brown-walnut-with-square-brass-tone-pulls':'dark',
    'contemporary-reeded-credenza-bleached-oak-with-slim-bar-pulls':'light',
    'contemporary-reeded-credenza-soft-white-washed-oak-with-slim-bar-pulls':'painted',
    'contemporary-round-jupe-dining-table-medium-brown-oak-with-stainless-steel-trim':'dark',
    'contemporary-rustic-wood-armchair-with-upholstered-seat':'dark',
    'contemporary-upholstered-cal-king-bed-with-wood-base':'linen',
    'contemporary-upholstered-king-bed-with-wood-base':'linen',
    'contemporary-upholstered-queen-bed-with-wood-base':'linen', 'contemporary-walnut-chair':'dark',
    'contemporary-white-wood-chair':'light', 'continental-buffet-cabinet-in-fruitwood-finish':'dark',
    'country-accent-table-with-storage':'dark', 'country-dining-ladder-back-chair':'light',
    'country-nine-drawer-chest':'dark', 'country-round-side-table':'dark', 'country-rustic-dining-chair':'dark',
    'country-wood-king-bed-with-paneled-headboard':'dark', 'cream-chevron-tufted-upholstered-king-bed':'linen',
    'cream-chevron-tufted-upholstered-queen-bed':'linen', 'custom-tufted-sofa-102':'linen',
    'dark-european-ash-trestle-end-dining-table':'painted', 'dark-stained-oak-upholstered-dining-chair':'dark',
    'deco-klismos-dining-chairs-in-white-with-cream-seats-sets-of-2':'painted', 'directoire-armchair':'dark',
    'directoire-side-chair':'dark', 'directoire-three-drawer-commode-in-warm-brown':'dark',
    'distressed-gray-pine-trunk-side-table-with-storage':'painted', 'draper-carlyle-desk':'dark',
    'draper-chinese-chippendale-dresser':'painted', 'early-english-upholstered-armchair':'light',
    'early-english-upholstered-side-chair':'light',
    'embossed-leather-storage-box-on-faux-bamboo-iron-stand':'leather',
    'english-coastal-modern-chest-of-drawers':'light', 'english-coastal-modern-dresser':'light',
    'english-neoclassical-carved-console-table-in-greyed-oak':'light',
    'english-oyster-veneer-chest-of-drawers-william-and-mary-style':'dark',
    'english-regency-dining-arm-chair':'dark', 'english-regency-dining-chair':'dark',
    'english-rolled-arm-leather-sofa-in-chestnut-brown-82':'leather', 'english-rustic-chest-of-drawers':'dark',
    'english-track-arm-sofa-rich-brown-leather-84-inch':'leather', 'european-black-rustic-desk':'painted',
    'european-greyed-oak-chevron-california-king-bed':'light', 'european-modern-dining-chair':'painted',
    'european-rustic-desk':'light', 'faded-oak-narrow-solid-wood-console-table':'light',
    'farmhouse-black-rustic-sideboard':'painted', 'farmhouse-dining-table-in-antiqued-pine-89-inches':'painted',
    'farmhouse-dining-table-in-reclaimed-pine-with-turned-legs':'light',
    'farmhouse-double-x-base-console-table-in-pine-93-inch':'light',
    'farmhouse-eight-door-sideboard-122-distressed-blue-grey-reclaimed-pine':'painted',
    'farmhouse-eight-door-sideboard-122-whitewashed-reclaimed-pine-with-tall-panel-do':'painted',
    'farmhouse-grand-console-cabinet-in-washed-grey-112-inch':'painted',
    'farmhouse-pine-bookcase-with-glass-doors-and-weathered-wood-base':'painted',
    'farmhouse-plank-bench-wheat-acacia-gunmetal-iron-80-inch':'light', 'faux-bamboo-3-drawer-chest':'painted',
    'flared-modern-fully-upholstered-king-bed-light':'linen',
    'flared-modern-fully-upholstered-king-bed-oatmeal':'linen',
    'flared-modern-fully-upholstered-queen-bed-light':'linen', 'flared-modern-upholstered-bed-king':'linen',
    'floating-modern-desk':'painted', 'french-art-deco-commode':'linen', 'french-burl-demi-lune-dresser':'dark',
    'french-country-4-door-sideboard-88-whitewashed-reclaimed-pine-with-carved-d':'painted',
    'french-country-breakfast-table-in-warm-blond-hardwood-40-inch':'light',
    'french-country-buffet-in-sage-green':'painted', 'french-country-chest-of-drawers-drift':'light',
    'french-country-console-table-with-turned-legs-72-inch':'light',
    'french-country-petite-pedestal-table':'painted', 'french-country-pine-breakfront-credenza':'light',
    'french-country-sideboard-96-antiqued-white':'painted', 'french-country-sideboard-96-white-and-grey':'painted',
    'french-country-sideboard-in-honey-pine-87-inches':'dark',
    'french-empire-dining-chairs-in-brown-leather-sets-of-2':'dark', 'french-ladderback-arm-chair':'light',
    'french-ladderback-arm-chair-chestnut':'light', 'french-ladderback-dining-chair':'light',
    'french-ladderback-dining-chair-chestnut':'light', 'french-louis-xv-marquetry-mechanical-side-table':'light',
    'french-louis-xv-style-parquetry-writing-desk':'light', 'french-louis-xvi-commode':'light',
    'french-louis-xvi-style-commode-42':'light', 'french-louis-xvi-style-commode-natural-52':'light',
    'french-neo-classic-dresser':'dark', 'french-neoclassic-dresser':'dark', 'french-provincial-armchair':'dark',
    'french-provincial-dining-chair':'dark', 'french-provincial-fruitwood-credenza':'light',
    'french-provincial-pine-buffet-sideboard':'light',
    'french-provincial-pine-sideboard-with-antiqued-blue-painted-finish':'painted',
    'french-provincial-pine-sideboard-with-diamond-panel-doors-warm-brown':'dark',
    'french-provincial-reeded-sideboard-78-whitewashed-with-oval-medallions':'painted',
    'french-provincial-rustic-dining-chair':'light',
    'french-provincial-sideboard-with-a-distressed-blue-grey-finish':'painted',
    'french-provincial-style-credenza-fruitwood':'light', 'french-provincial-upholstered-oak-bed-cal-king':'linen',
    'french-provincial-upholstered-oak-bed-queen':'linen', 'french-provincial-upholstered-oak-bed-us-king':'linen',
    'french-provincial-wall-cabinet-antiqued-walnut-with-carved-crest-medallions':'dark',
    'french-provincial-wall-cabinet-with-carved-crest-medallions':'painted',
    'french-provincial-white-wall-cabinet-with-carved-crest-appliqu-s':'painted',
    'french-style-chest-of-drawers':'dark', 'geometric-appeal-rustic-pine-credenza':'dark',
    'geometric-parquetry-sideboard-in-honey-brown':'dark', 'george-iii-bow-front-bedside-dresser':'dark',
    'george-iii-style-giltwood-coffee-table':'light', 'george-iii-style-mahogany-credenza':'dark',
    'george-iii-trellis-back-side-chair':'dark', 'georgian-burl-bowfront-chest':'dark',
    'georgian-kidney-shaped-executive-desk':'dark', 'glam-minimalist-chest-of-drawers':'painted',
    'gray-faux-bois-expanding-jupe-dining-table-70':'painted',
    'gray-oak-expanding-jupe-dining-table-85-1-2':'painted', 'gray-porcelain-modern-dining-table':'stone',
    'gustavian-style-swedish-dresser':'painted', 'haute-hollywood-regency-dining-chair':'painted',
    'herringbone-waterfall-desk':'light', 'hollywood-regency-bench':'linen',
    'hollywood-regency-round-coffee-table-in-antique-gold':'metal', 'hollywood-regency-upholstered-armchair':'dark',
    'hollywood-regency-upholstered-dining-chair':'dark', 'hollywood-regency-upholstered-king-bed':'dark',
    'hollywood-regency-upholstered-king-bed-light-finish':'light',
    'industrial-bistro-table-with-solid-walnut-top-32-inch-round':'dark',
    'industrial-console-table-with-parquet-top-and-iron-trestle':'dark', 'industrial-equestrian-desk':'light',
    'industrial-modern-chest-of-drawers':'dark', 'industrial-round-leather-end-table':'leather',
    'industrial-two-tier-round-coffee-table-in-grey-wood-and-iron':'light', 'irish-chippendale-dining-chairs':'dark',
    'italian-louis-xvi-inlaid-dresser':'dark', 'italian-neo-classic-inlaid-chest-in-fruitwood':'dark',
    'italian-neo-classic-tall-dresser':'dark', 'italian-neo-classic-three-drawer-commode-36':'dark',
    'italian-neo-classic-three-drawer-commode-44':'dark', 'keyhole-back-leather-dining-chairs':'painted',
    'klismos-dining-chairs-in-antiqued-walnut-and-cream-sets-of-2':'dark',
    'klismos-dining-chairs-in-white-with-cream-seats-sets-of-2':'painted',
    'klismos-dining-chairs-with-saber-legs-in-black-sets-of-2':'painted',
    'large-art-deco-round-jupe-dining-table-with-stainless-steel-trim':'dark',
    'large-french-provincial-sideboard-119-distressed-grey-pine-with-natural-wood-top':'painted',
    'leather-wingback-dining-chairs-in-black-sets-of-2':'dark', 'light-ash-modern-desk':'light',
    'light-brown-oak-expanding-jupe-dining-table-71':'light', 'light-hollywood-regency-desk':'light',
    'light-hollywood-regency-upholstered-armchair':'light',
    'light-hollywood-regency-upholstered-dining-chair':'light', 'light-oak-round-side-table':'light',
    'limed-oak-dining-chairs-in-beige-linen-sets-of-2':'light',
    'lodge-chest-of-drawers-with-cast-antler-pulls':'dark',
    'long-faux-bamboo-console-table-fruitwood-finish':'light', 'louis-phillipe-dresser':'light',
    'louis-xv-cane-back-dining-chairs-in-driftwood-sets-of-2':'light',
    'louis-xv-cane-back-dining-chairs-in-gray-oak-sets-of-2':'painted',
    'louis-xv-cane-back-dining-chairs-in-gray-with-taupe-seats-sets-of-2':'painted',
    'louis-xv-cane-back-dining-chairs-in-white-sets-of-2':'painted',
    'louis-xv-dining-chairs-in-cream-with-dark-brown-legs-sets-of-2':'dark',
    'louis-xv-dining-chairs-in-cream-with-silvered-legs-sets-of-2':'painted',
    'louis-xvi-dining-chairs-in-white-with-cream-seats-sets-of-2':'painted',
    'louis-xvi-dining-chairs-with-oval-backs-in-walnut-sets-of-2':'dark',
    'louis-xvi-giltwood-square-coffee-table':'light', 'louis-xvi-style-burl-chest':'dark',
    'louis-xvi-style-cane-back-dining-chairs-whitewashed-sets-of-2':'painted', 'louis-xvi-style-commode':'dark',
    'louis-xvi-style-writing-desk':'dark', 'louis-xvi-three-door-buffet-in-white-and-gold':'painted',
    'louis-xvi-upholstered-bed-king':'dark', 'louis-xvi-upholstered-bed-queen':'dark',
    'mid-century-burl-top-writing-table':'dark', 'mid-century-credenza-fruitwood-finish':'light',
    'mid-century-dining-armchair':'painted', 'mid-century-dining-armchair-walnut-finish-cream-seat':'dark',
    'mid-century-dining-armchair-walnut-finish-dark-green':'dark',
    'mid-century-extendable-dining-table-cherry':'light', 'mid-century-klismos-dining-chair':'painted',
    'mid-century-minimalist-sofa-in-gray-upholstery':'linen', 'mid-century-modern-black-sideboard-buffet':'leather',
    'mid-century-modern-bleached-oak-credenza-bronze-sunburst-pulls':'light',
    'mid-century-modern-cerused-credenza':'light', 'mid-century-modern-dining-armchair-1':'dark',
    'mid-century-modern-dining-chair-1':'dark', 'mid-century-natural-desk':'dark',
    'mid-century-round-dining-table-sun-bleached-cherry':'light',
    'mid-century-round-leather-cocktail-table':'leather', 'mid-century-style-burl-dresser':'dark',
    'mid-century-upholstered-dining-chair':'dark', 'mid-century-upholstered-king-size-bed-1':'linen',
    'mid-century-walnut-upholstered-bed-king':'dark', 'midcentury-upholstered-dining-arm-chair':'dark',
    'midcentury-upholstered-dining-chair':'dark', 'midnight-modern-desk':'painted',
    'midnight-oak-china-cabinet-with-glass-shelves':'painted', 'ming-style-coffee-table-in-warm-brown-pine':'light',
    'ming-style-ebonized-pine-coffee-table':'painted', 'ming-style-low-coffee-table-in-antique-white-pine':'painted',
    'ming-style-square-coffee-table-in-tan-reclaimed-pine':'light',
    'minimalist-a-frame-console-table-in-aged-pine-68-inch':'light',
    'modern-bowfront-walnut-credenza-sculpted-stainless-pulls':'dark', 'modern-brier-upholstered-sofa':'linen',
    'modern-burl-walnut-bowfront-credenza-80-with-square-brass-pulls':'dark',
    'modern-chevron-parquetry-dresser':'light', 'modern-chippendale-desk':'painted',
    'modern-classic-upholstered-king-bed':'linen', 'modern-classic-upholstered-queen-bed':'linen',
    'modern-classical-extension-dining-table-mahogany':'dark', 'modern-coastal-dining-chair':'light',
    'modern-coastal-upholstered-dining-armchair':'light', 'modern-coastal-upholstered-dining-chair':'light',
    'modern-curved-back-dining-chair':'light', 'modern-dining-chair-with-sloped-back-in-cream-cotton-linen':'dark',
    'modern-english-dining-chair':'dark', 'modern-framed-and-upholstered-bed':'linen',
    'modern-framed-and-upholstered-bed-light-oak':'linen', 'modern-french-dining-chairs':'light',
    'modern-glam-white-leather-credenza-on-sled-base':'leather', 'modern-high-back-dining-armchair':'light',
    'modern-iron-and-leather-bench':'leather', 'modern-leather-wrapped-round-coffee-table':'leather',
    'modern-mia-upholstered-sofa':'linen', 'modern-parquetry-sideboard-with-brass-legs':'dark',
    'modern-pine-chest-of-drawers':'light', 'modern-round-drum-side-table-in-white-ash-with-open-niche':'painted',
    'modern-rustic-upholstered-armchair':'light', 'modern-rustic-upholstered-side-chair':'light',
    'modern-sideboard-with-marble-top-72-inches':'light', 'modern-stacked-pine-console-table':'dark',
    'modern-upholstered-king-size-bed':'linen', 'modern-upholstered-sofa-in-arctic-white':'linen',
    'modern-white-ash-console-with-drawers-and-waterfall-frame':'painted', 'natural-rattan-chest-of-drawers':'cane',
    'natural-rattan-coffee-table':'cane', 'natural-rattan-console-table':'cane',
    'natural-rattan-console-table-open-box':'cane', 'natural-rattan-dining-chair':'cane',
    'natural-rattan-king-size-bed-frame':'cane', 'natural-rattan-queen-size-bed-frame':'cane',
    'neo-classic-black-chest-of-drawers':'painted', 'neo-classic-curio-cabinet':'painted',
    'neo-classic-incurved-commode':'light',
    'neoclassical-accent-table-in-cast-aluminum-with-tobacco-leaf-top':'metal',
    'neoclassical-dining-chairs-ribbon-backs-in-walnut-sets-of-2':'dark',
    'neoclassical-dining-chairs-with-ribbon-backs-in-white-sets-of-2':'painted',
    'northern-european-style-commode':'dark', 'oak-mid-century-modern-credenza':'light',
    'oak-modern-side-table':'light', 'oak-parquetry-extension-dining-table':'light',
    'oak-parquetry-upholstered-bench-dark-oak':'dark', 'oak-parquetry-upholstered-bench-light-oak':'light',
    'old-world-marquetry-credenza-86-walnut-and-oak-with-burl-inset-geometric-d':'dark',
    'openwork-steel-arm-chair':'metal', 'openwork-steel-dining-chair':'metal',
    'organic-modern-faceted-drum-cocktail-table-42-inches':'dark', 'organic-modern-madrona-desk':'painted',
    'paris-welt-sofa':'linen', 'parisian-upholstered-king-bed':'linen',
    'parsons-dining-chairs-in-beige-with-medium-brown-legs-sets-of-2':'light',
    'parsons-dining-chairs-in-cream-with-dark-brown-legs-sets-of-2':'painted', 'queen-anne-dining-chair':'dark',
    'rattan-console-table':'cane', 'rattan-wrapped-small-cabinet':'cane', 'rattan-wrapped-tall-cabinet':'cane',
    'reclaimed-pine-credenza':'light', 'reclaimed-pine-mantel-console-table-with-stepped-molding':'dark',
    'reclaimed-pine-provincial-side-cabinet':'light', 'red-leather-and-iron-rustic-small-bench':'leather',
    'reeded-pine-pedestal-desk-in-stone-grey-with-ring-pulls':'painted', 'regency-armchair-1':'dark',
    'regency-brighton-style-dresser':'painted', 'regency-faux-bamboo-side-chair-black-finish':'painted',
    'regency-mahogany-credenza':'dark', 'regency-style-bedside-table':'dark',
    'regency-style-demilune-chest-of-drawers':'dark', 'regency-style-desk':'dark',
    'regency-style-dining-side-chair':'dark',
    'regency-style-glazed-vitrine-in-limed-pine-with-oval-fretwork':'light',
    'regency-style-tufted-lyre-sofa':'linen', 'regency-walnut-credenza':'dark',
    'regency-whitewashed-oak-dining-table':'light', 'regency-writing-table-the-tatham-desk':'dark',
    'round-rattan-coffee-table':'cane', 'round-walnut-drum-coffee-table-with-storage-drawer':'dark',
    'round-white-reeded-pedestal-jupe-dining-table-with-expanding-top':'painted',
    'rustic-carved-leg-console-table-in-natural-pine-90-inch':'light',
    'rustic-carved-moroccan-sideboard-98-burnt-brown-reclaimed-pine-with-geometric-door-panels':'dark',
    'rustic-country-walnut-desk-light-driftwood-finish':'light', 'rustic-european-commode':'light',
    'rustic-european-rush-seat-armchair':'light', 'rustic-european-rush-seat-side-chair':'light',
    'rustic-farmhouse-spool-leg-side-chair':'dark', 'rustic-farmhouse-style-black-bookcase':'painted',
    'rustic-french-carved-sideboard-distressed-finish-6-drawers':'painted',
    'rustic-iron-console-table-with-distressed-marble-top':'stone',
    'rustic-stacked-plank-waterfall-coffee-table-in-stone-grey':'painted',
    'rustic-western-upholstered-armchair':'light', 'rustic-western-upholstered-side-chair':'light',
    'rustic-wood-chest-of-drawers':'light', 'scandinavian-minimalist-bench-with-upholstered-seat':'light',
    'scandinavian-two-tier-drum-coffee-table-in-grey-pine':'painted',
    'sculpted-mid-century-dining-chair-in-cream-with-white-legs':'painted', 'shane-modern-sofa':'linen',
    'sheraton-satinwood-armchair-2':'dark', 'slipcovered-dining-chair':'linen',
    'slipcovered-dining-chair-french-yellow-upholstery':'linen',
    'slipcovered-dining-chair-light-beige-upholstery':'linen', 'slipcovered-dining-chair-white-upholstery':'linen',
    'slipcovered-dining-chairs-in-beige-sailcloth-sets-of-2':'linen',
    'sloped-arm-green-velvet-dining-chair-in-olive-host-chair':'dark', 'small-country-wood-coffee-table':'dark',
    'small-french-louis-xvi-desk':'dark', 'spool-turned-three-drawer-chest':'light',
    'stark-modern-dining-chair':'metal', 'stone-gray-dining-table-with-starburst-pedestal-base':'painted',
    'swedish-carved-trunk-coffee-table-in-antique-grey':'painted',
    'swedish-gustavian-glazed-display-cabinet-in-white-88':'painted',
    'swedish-gustavian-reeded-sideboard-in-soft-gray-95':'painted',
    'swedish-klismos-cane-back-dining-chairs-bleached-oak-sets-of-2':'light',
    'swedish-klismos-cane-back-dining-chairs-in-white-sets-of-2':'painted',
    'swedish-side-chair-bleached-cherry':'light', 'swedish-style-faceted-sideboard-in-antique-gray-79':'painted',
    'sycamore-mid-century-commode':'light', 'tall-chippendale-chest':'dark', 'tall-empire-chest-of-drawers':'dark',
    'tall-rustic-display-bookcase-natural-reclaimed-pine-with-glass-doors':'light',
    'taupe-chevron-tufted-upholstered-king-bed':'linen', 'the-burl-wood-parson-desk':'dark',
    'thomas-seymour-armchair':'dark', 'thomas-seymour-side-chair':'dark', 'three-drawer-modern-dresser':'painted',
    'traditional-credenza-79-driftwood-four-door':'light', 'traditional-curved-back-sofa':'linen',
    'traditional-pine-bookcase-arched-glass-doors':'dark', 'traditional-round-coffee-table-with-glass-inset':'metal',
    'traditional-six-door-credenza-122-distressed-blue-grey-pine-with-paneled-doors':'painted',
    'traditional-square-dining-and-game-table-in-warm-sienna':'light',
    'traditional-tall-bookcase-80-ebonized-walnut-with-glass-display-doors':'painted',
    'traditional-walnut-credenza-fruitwood-finish-fluted-stile':'dark',
    'transitional-81-inch-dining-table-with-porcelain-calacatta-top-and-turned-trestle-base':'painted',
    'transitional-country-antiqued-sideboard-64-reclaimed-wood-with-warm-distressing':'painted',
    'transitional-reclaimed-pine-sideboard-90-soft-grey-two-tone':'painted',
    'transitional-round-jupe-dining-table-large-bleached-oak-with-hammered-iron-base':'light',
    'transitional-round-jupe-dining-table-large-grey-oak-with-hammered-iron-base':'painted',
    'transitional-round-jupe-dining-table-large-grey-oak-with-sculptural-x-form-pedes':'painted',
    'transitional-round-jupe-dining-table-large-light-brown-oak-with-hammered-iron-ba':'dark',
    'transitional-round-jupe-dining-table-large-light-brown-oak-with-sculptural-x-form':'light',
    'transitional-round-jupe-dining-table-large-warm-brown-oak-with-hammered-iron-bas':'dark',
    'transitional-round-jupe-dining-table-large-warm-brown-oak-with-sculptural-x-form':'dark',
    'transitional-round-jupe-dining-table-large-white-oak-with-hammered-iron-base':'painted',
    'transitional-round-jupe-dining-table-large-white-oak-with-sculptural-x-form-pede':'painted',
    'transitional-round-jupe-dining-table-medium-bleached-oak-with-faceted-pedestal-b':'light',
    'transitional-round-jupe-dining-table-medium-bleached-oak-with-hammered-iron-acce':'light',
    'transitional-round-jupe-dining-table-medium-grey-oak-with-faceted-pedestal-base':'painted',
    'transitional-round-jupe-dining-table-medium-grey-oak-with-hammered-iron-accents':'painted',
    'transitional-round-jupe-dining-table-medium-light-brown-oak-with-faceted-pedestal':'light',
    'transitional-round-jupe-dining-table-medium-light-brown-oak-with-hammered-iron-a':'dark',
    'transitional-round-jupe-dining-table-medium-warm-brown-oak-with-faceted-pedestal':'dark',
    'transitional-round-jupe-dining-table-medium-warm-brown-oak-with-hammered-iron-ac':'dark',
    'transitional-round-jupe-dining-table-medium-white-oak-with-faceted-pedestal-base':'painted',
    'transitional-round-jupe-dining-table-medium-white-oak-with-iron-pedestal':'painted',
    'transitional-style-upholstered-king-bed-in-silver':'linen', 'transitional-tufted-sofa':'linen',
    'transitional-upholstered-sofa-1':'linen', 'tufted-chesterfield-double-sided-sofa':'linen',
    'tufted-dining-chairs-in-gray-leather-sets-of-2':'light',
    'tufted-velvet-dining-chairs-in-navy-blue-sets-of-2':'dark',
    'turned-geometric-side-tables-in-natural-elm-set-of-3':'light',
    'unusual-19th-century-style-italian-chest':'dark', 'upholstered-art-deco-bed':'linen',
    'upholstered-classic-dining-chair':'light', 'upholstered-curved-back-dining-chair':'linen',
    'upholstered-host-dining-chair-in-ivory-chenille':'painted', 'upholstered-king-size-minimalist-bed':'linen',
    'upholstered-queen-size-minimalist-bed':'linen', 'upholstered-transitional-bench-in-charcoal':'linen',
    'walnut-art-deco-upholstered-dining-armchair':'dark', 'walnut-art-deco-upholstered-dining-chair':'dark',
    'walnut-burl-four-door-credenza-with-inlaid-panels':'dark',
    'warm-brown-oak-expanding-jupe-dining-table-71':'dark', 'warm-brown-oak-expanding-jupe-dining-table-85':'dark',
    'waterfall-velvet-bench':'linen', 'white-contemporary-round-drum-cocktail-table-56':'painted',
    'white-drum-coffee-table-with-storage-drawer':'painted',
    'white-oak-expanding-jupe-dining-table-85-1-2':'painted',
    'white-reeded-expanding-jupe-dining-table-70':'painted', 'whitewashed-oak-bistro-dining-chair':'light',
    'william-mary-marquetry-cabinet-on-chest':'dark',
    'wingback-dining-chairs-in-beige-linen-with-nailheads-sets-of-2':'dark',
  };

  /* Finish/Material bucket for one product — the ONE place this is decided, used by
     both the facet counts and the filter match so they can never disagree.
     Order: Dino's review override → tag inference by priority → 'other'. */
  var FINISH_ORDER = ['stone', 'metal', 'leather', 'cane', 'linen', 'painted', 'dark', 'light'];
  function finishListFor(key) {
    if (key === 'stone') return FINISH_STONE;
    if (key === 'metal') return FINISH_METAL;
    if (key === 'leather') return FINISH_LEATHER;
    if (key === 'cane') return FINISH_CANE;
    if (key === 'linen') return FINISH_LINEN;
    if (key === 'painted') return FINISH_PAINTED;
    if (key === 'dark') return FINISH_DARK_WOOD;
    return FINISH_LIGHT_WOOD;
  }
  function finishBucketOf(p) {
    var override = p.handle ? EGA_FINISH_OVERRIDES[p.handle] : null;
    if (override) return override;
    for (var i = 0; i < FINISH_ORDER.length; i++) {
      var list = finishListFor(FINISH_ORDER[i]);
      for (var j = 0; j < list.length; j++) {
        if (p.tags.indexOf(list[j]) !== -1) return FINISH_ORDER[i];
      }
    }
    return 'other';
  }

  /* Explicit extension tags win over title wording. Untagged titles can still
     identify an extension table, but cannot establish that a top is fixed. */
  function diningExtensionTypeOf(p) {
    var tags = p.tags || [];
    var extension = tags.indexOf('extension_yes') !== -1 || tags.indexOf('extension dining tables') !== -1;
    var fixed = tags.indexOf('extension_no') !== -1;
    if (extension && fixed) return null; /* conflicting data needs review */
    if (extension) return 'extension';
    if (fixed) return 'fixed';
    var title = (p.title || '').toLowerCase();
    if (title.indexOf('extend') !== -1 || title.indexOf('extension') !== -1 ||
        title.indexOf('jupe') !== -1 || title.indexOf('expanding') !== -1 ||
        title.indexOf('expandable') !== -1 || title.indexOf('draw leaf') !== -1 ||
        title.indexOf('drop leaf') !== -1 || title.indexOf('dropleaf') !== -1) return 'extension';
    return null;
  }

  /* Keep source tags specific while presenting exactly these rare shapes, plus
     intentionally tagged shape__other products, as one customer-facing Other. */
  var SHAPE_GROUPED_OTHER_KEY = 'shape__grouped-other';
  var SHAPE_GROUPED_OTHER_TAGS = ['shape__trefoil', 'shape__rounded-triangle', 'shape__scalloped', 'shape__d-shaped', 'shape__mixed-shapes', 'shape__other'];

  function shapeFacetKey(tag) {
    return SHAPE_GROUPED_OTHER_TAGS.indexOf(tag) !== -1 ? SHAPE_GROUPED_OTHER_KEY : tag;
  }

  function shapeFacetCounts(products) {
    var counts = {};
    products.forEach(function (p) {
      var seen = {};
      p.tags.forEach(function (tag) {
        if (tag.indexOf('shape_') !== 0) return;
        var key = shapeFacetKey(tag);
        if (seen[key]) return;
        seen[key] = true;
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return counts;
  }

  function shapeFacetMatches(tags, value) {
    if (value === SHAPE_GROUPED_OTHER_KEY) {
      return tags.some(function (tag) { return SHAPE_GROUPED_OTHER_TAGS.indexOf(tag) !== -1; });
    }
    /* Preserve the old hidden shared-URL meaning; the visible Other uses its
       own key and never counts untagged products. */
    if (value === 'shape__other') {
      return tags.indexOf('shape__other') !== -1 ||
        !tags.some(function (tag) { return tag.indexOf('shape__') === 0; });
    }
    return tags.indexOf(value) !== -1;
  }

  var TAG_GROUPS = [
    // Availability — merged: In Stock + Customizable MTO in one group
    {
      id: 'availability', label: 'Availability', type: 'custom-availability',
    },
    // Style — simplified to Traditional / Modern
    {
      id: 'style', label: 'Style', type: 'custom-style',
    },
    // Bar & counter stools: seat height class (tags stool_counter / stool_bar)
    {
      id: 'stoolheight', label: 'Seat Height', type: 'prefix', prefix: 'stool_',
      includeCollectionHandles: ['bar-stools'],
    },
    // Shape — before length for dining tables
    {
      id: 'shape', label: 'Shape', type: 'prefix', prefix: 'shape_',
      includeCollectionTypes: ['dining', 'table', 'console', 'nightstand', 'desk', 'mirror', 'coffee'],
    },
    // Dining-specific: Seating capacity (calculated from width)
    {
      id: 'dt-seating', label: 'Seating', type: 'custom-dt-seating',
      includeCollectionTypes: ['dining'],
    },
    // Dining-specific: Length (renamed from width)
    {
      id: 'dt-length', label: 'Length', type: 'custom-dt-length',
      includeCollectionTypes: ['dining'],
    },
    // Dining-specific: Extension vs Fixed
    {
      id: 'dt-extension', label: 'Type', type: 'custom-dt-extension',
      includeCollectionTypes: ['dining'],
    },
    // Universal Finish / Material — shows on ALL collections
    {
      id: 'dt-finish', label: 'Finish / Material', type: 'custom-dt-finish',
    },
    // Width — non-dining
    {
      id: 'dimw', label: 'Width', type: 'custom-width',
      excludeCollectionTypes: ['dining', 'chair', 'seating', 'accent'],
    },
    // Depth — non-dining
    { id: 'dimd', label: 'Depth',  type: 'custom-depth', excludeCollectionTypes: ['dining', 'chair', 'seating', 'accent', 'coffee', 'table', 'console', 'nightstand'] },
    // Height — exclude from sofas/seating/dining
    {
      id: 'dimh', label: 'Height', type: 'prefix', prefix: 'dim_h_',
      excludeCollectionTypes: ['sofa', 'seating', 'chair', 'dining', 'coffee'],
    },
    // Seating capacity — sofas/seating
    {
      id: 'seating', label: 'Seating', type: 'prefix', prefix: 'seat_cap_',
      includeCollectionTypes: ['sofa', 'seating', 'sectional'],
    },
    // Dining-specific
    {
      id: 'seats',     label: 'Seats',         type: 'prefix', prefix: 'seats_',
      includeCollectionTypes: ['dining', 'sofa', 'seating', 'bench', 'chair'],
    },
    {
      id: 'extension', label: 'Extension',      type: 'prefix', prefix: 'extension_',
      includeCollectionTypes: ['dining'],
    },
    {
      id: 'top',       label: 'Top Material',   type: 'prefix', prefix: 'top_',
      includeCollectionTypes: ['dining'],
    },
    {
      id: 'base',      label: 'Base Type',      type: 'prefix', prefix: 'base_',
      includeCollectionTypes: ['dining'],
    },
    // Seating
    {
      id: 'upholstery', label: 'Upholstery', type: 'custom-upholstery',
      includeCollectionTypes: ['seating', 'sofa', 'chair', 'bench', 'ottoman', 'sectional'],
    },
    /* Removed: generic 'finish' prefix group — now handled by custom dt-finish with Linen bucket */
    {
      id: 'hardware', label: 'Hardware', type: 'prefix', prefix: 'hw_',
      includeCollectionTypes: ['storage', 'dresser', 'chest', 'cabinet', 'bookcase', 'sideboard', 'credenza', 'nightstand', 'armoire', 'wardrobe'],
    },
    // Beds
    {
      id: 'bedsize',    label: 'Bed Size',    type: 'prefix', prefix: 'size_',
      includeCollectionTypes: ['bed', 'bedroom'],
    },
    {
      id: 'headboard',  label: 'Headboard',   type: 'prefix', prefix: 'hb_',
      includeCollectionTypes: ['bed', 'bedroom'],
    },
    // Lighting
    {
      id: 'lighttype',   label: 'Fixture Type', type: 'prefix', prefix: 'light_',
      includeCollectionTypes: ['lighting', 'lamp', 'chandelier', 'sconce', 'pendant'],
    },
    {
      id: 'lightfinish', label: 'Finish',        type: 'prefix', prefix: 'lfinish_',
      includeCollectionTypes: ['lighting', 'lamp', 'chandelier', 'sconce', 'pendant'],
    },
    // Mirrors
    {
      id: 'mirrorshape', label: 'Shape',       type: 'prefix', prefix: 'mshape_',
      includeCollectionTypes: ['mirror'],
    },
    {
      id: 'mirrortype',  label: 'Mirror Type', type: 'prefix', prefix: 'mtype_',
      includeCollectionTypes: ['mirror'],
    },
    {
      id: 'mirrorframe', label: 'Frame',        type: 'prefix', prefix: 'mframe_',
      includeCollectionTypes: ['mirror'],
    },
  ];

  // ── Human-readable labels ────────────────────────────────────────────────
  var TAG_LABELS = {
    'avail_mto': 'Made to Order', 'avail_inproduction': 'In Production',
    'stock items': 'Stock Item',
    'dim_w_under-24in': 'Under 24"', 'dim_w_24-48in': '24" – 48"', 'dim_w_48-72in': '48" – 72"', 'dim_w_over-72in': 'Over 72"',
    'dim_w_under-72in': 'Under 72"', 'dim_w_72-plus': 'Over 72"',
    'dim_d_under-18in': 'Under 18"', 'dim_d_18-24in': '18" – 24"', 'dim_d_24-36in': '24" – 36"', 'dim_d_over-36in': 'Over 36"',
    'dim_h_under-18in': 'Under 18"', 'dim_h_18-24in': '18" – 24"', 'dim_h_24-30in': '24" – 30"', 'dim_h_30-36in': '30" – 36"', 'dim_h_36-48in': '36" – 48"', 'dim_h_over-48in': 'Over 48"',
    'seat_cap_2': 'Two Seater', 'seat_cap_3': 'Three Seater', 'seat_cap_other': 'Other',
    'style_artdeco': 'Art Deco', 'style_baroque': 'Baroque', 'style_brutalist': 'Brutalist',
    'style_chippendale': 'Chippendale', 'style_coastal': 'Coastal', 'style_country': 'Country',
    'style_english': 'English', 'style_frenchprovincial': 'French Provincial',
    'style_georgian': 'Georgian', 'style_gothic': 'Gothic', 'style_gustavian': 'Gustavian',
    'style_hollywoodregency': 'Hollywood Regency', 'style_industrial': 'Industrial',
    'style_louisxv': 'Louis XV', 'style_louisxvi': 'Louis XVI',
    'style_midcenturymodern': 'Mid-Century Modern', 'style_neoclassical': 'Neoclassical',
    'style_organicmodern': 'Organic Modern', 'style_regency': 'Regency',
    'style_scandinavianmodern': 'Scandinavian Modern', 'style_traditional': 'Traditional',
    'style_transitional': 'Transitional',
    'mat_mahogany': 'Mahogany', 'mat_walnut': 'Walnut', 'mat_oak': 'Oak',
    'mat_maple': 'Maple', 'mat_cherry': 'Cherry', 'mat_pine': 'Pine',
    'mat_hardwood': 'Hardwood', 'mat_teak': 'Teak', 'mat_marble': 'Marble',
    'mat_stone': 'Stone', 'mat_travertine': 'Travertine', 'mat_brass': 'Brass',
    'mat_iron': 'Iron', 'mat_steel': 'Steel', 'mat_glass': 'Glass',
    'mat_leather': 'Leather', 'mat_linen': 'Linen', 'mat_silk': 'Silk',
    'mat_velvet': 'Velvet', 'mat_rattan': 'Rattan', 'mat_bamboo': 'Bamboo',
    'mat_bone': 'Bone', 'mat_shell': 'Shell', 'mat_mirror': 'Mirrored',
    'color_brown': 'Brown', 'color_tan': 'Tan / Beige', 'color_gold': 'Gold',
    'color_black': 'Black', 'color_white': 'White', 'color_ivory': 'Ivory',
    'color_cream': 'Cream', 'color_grey': 'Grey', 'color_blue': 'Blue',
    'color_green': 'Green', 'color_red': 'Red', 'color_natural': 'Natural',
    'color_silver': 'Silver', 'color_multicolor': 'Multicolor',
    'shape_round': 'Round', 'shape_oval': 'Oval', 'shape_rectangular': 'Rectangular',
    'shape_square': 'Square', 'shape_organic': 'Organic / Live Edge', 'shape__other': 'Other',
    'shape__grouped-other': 'Other',
    'seats_2': '2', 'seats_4': 'Up to 4', 'seats_6': 'Up to 6',
    'seats_8': 'Up to 8', 'seats_10': 'Up to 10', 'seats_12plus': '12+',
    'extension_yes': 'With Extension Leaf', 'extension_no': 'Fixed Top',
    'top_wood': 'Wood', 'top_paintedwood': 'Painted Wood',
    'top_marble': 'Marble / Stone', 'top_glass': 'Glass',
    'base_fourleg': 'Four Leg', 'base_pedestal': 'Pedestal',
    'base_doublepedestal': 'Double Pedestal', 'base_trestle': 'Trestle',
    'upholstery_fabric': 'Fabric', 'upholstery_leather': 'Leather',
    'upholstery_performance': 'Performance Fabric',
    'finish_natural': 'Natural', 'finish_painted': 'Painted',
    'finish_lacquered': 'Lacquered', 'finish_distressed': 'Distressed',
    'finish_gilded': 'Gilded', 'finish_ebonized': 'Ebonized',
    'finish_cerused': 'Cerused / Limed', 'finish_antiqued': 'Antiqued',
    'finish_twotone': 'Two-Tone',
    'hw_brass': 'Brass', 'hw_antiqubrass': 'Antique Brass',
    'hw_nickel': 'Nickel', 'hw_iron': 'Iron', 'hw_none': 'No Hardware',
    'size_twin': 'Twin', 'size_full': 'Full / Double', 'size_queen': 'Queen',
    'size_king': 'King', 'size_calking': 'California King',
    'hb_wood': 'Wood', 'hb_upholsteredfabric': 'Upholstered — Fabric',
    'hb_upholsteredleather': 'Upholstered — Leather',
    'hb_canopy': 'Canopy', 'hb_poster': 'Four-Poster',
    'light_chandelier': 'Chandelier', 'light_pendant': 'Pendant',
    'light_flush': 'Flush / Semi-Flush', 'light_floor': 'Floor Lamp',
    'light_table': 'Table Lamp', 'light_sconce': 'Wall Sconce',
    'light_lantern': 'Lantern',
    'lfinish_brass': 'Brass', 'lfinish_antbrass': 'Antique Brass',
    'lfinish_nickel': 'Nickel', 'lfinish_black': 'Matte Black',
    'lfinish_bronze': 'Bronze', 'lfinish_chrome': 'Chrome', 'lfinish_wood': 'Wood',
    'mshape_round': 'Round', 'mshape_oval': 'Oval', 'mshape_rectangular': 'Rectangular',
    'mshape_arched': 'Arched', 'mshape_sunburst': 'Sunburst',
    'mshape_convex': 'Convex', 'mshape_irregular': 'Irregular',
    'mtype_wall': 'Wall Mirror', 'mtype_floor': 'Floor / Leaning',
    'mtype_mantel': 'Mantel', 'mtype_pierconsole': 'Pier / Console',
    'mframe_wood': 'Wood', 'mframe_gilded': 'Gilded', 'mframe_painted': 'Painted',
    'mframe_metal': 'Metal', 'mframe_beveled': 'Beveled',
    'stool_counter': 'Counter Height', 'stool_bar': 'Bar Height',
    'mframe_rattan': 'Rattan', 'mframe_antiqued': 'Antiqued',
  };

  // ── State ────────────────────────────────────────────────────────────────
  var state = {
    allProducts: [],
    productDims: {},
    filtered: [],
    activeFilters: {},
    instockOnly: false,
    customizableOnly: false,
    priceMin: 0,
    priceMax: Infinity,
    priceMinActive: 0,
    priceMaxActive: Infinity,
    sort: 'featured',
    loaded: false,
    collectionHandle: '',
    collectionType: '',
    isStyleCollection: false,
    activeRoom: null, /* null = All rooms */
    roomTag: null,    /* room collection pre-scope (e.g. 'room:living-room') */
    centuryTag: null, /* century collection pre-scope (e.g. '18th century') */
    renderedCount: 0,
    implicitTypes: null, /* Set of types for parent collections (Lighting, Objects) — scope without chips */
    implicitInstock: false, /* In Stock collection (stock-items): stock items that are available, no chip */
  };

  // ── Room Map for Style Collections ─────────────────────────────────────
  var ROOM_MAP = [
    { label: 'Living Room', types: ['sofas', 'accent chairs', 'coffee & cocktail tables', 'center tables', 'side & end tables', 'accent tables', 'bookcases', 'bookshelves & etageres', 'cabinets'] },
    { label: 'Dining Room', types: ['dining tables', 'dining chairs', 'sideboards & buffets', 'bar carts', 'bar cabinets', 'bar stools'] },
    { label: 'Bedroom', types: ['beds', 'nightstands', 'dressers', 'chest of drawers', 'benches', 'ottomans and poufs'] },
    { label: 'Office & Study', types: ['desks', 'desk chairs', 'secretary desks'] },
    { label: 'Entry & Hall', types: ['console tables', 'mirrors', 'pedestals'] },
    { label: 'Lighting', types: ['chandeliers', 'lanterns', 'table lamps', 'floor lamps', 'lamps', 'sconces', 'candelabra', 'wall sconces'] },
    { label: 'Décor', types: ['accessories', 'decorative objects', 'chinese porcelain', 'clocks'] },
    { label: 'Kitchen', types: ['kitchen islands', 'bathroom vanities'] },
    { label: 'Game Room', types: ['game tables'] },
  ];

  function getRoomForType(type) {
    var t = (type || '').toLowerCase();
    for (var i = 0; i < ROOM_MAP.length; i++) {
      if (ROOM_MAP[i].types.indexOf(t) !== -1) return ROOM_MAP[i].label;
    }
    return null;
  }

  function getProductsForRoom(products, room) {
    if (!room) return products;
    var rm = null;
    for (var i = 0; i < ROOM_MAP.length; i++) {
      if (ROOM_MAP[i].label === room) { rm = ROOM_MAP[i]; break; }
    }
    if (!rm) return products;
    return products.filter(function (p) { return rm.types.indexOf(p.type) !== -1; });
  }

  function buildRoomPills() {
    var bar = document.getElementById('ega-room-bar');
    if (!bar || !state.isStyleCollection) return;
    bar.innerHTML = '';
    bar.style.display = '';

    /* Count per room */
    var counts = {};
    for (var i = 0; i < ROOM_MAP.length; i++) counts[ROOM_MAP[i].label] = 0;
    for (var j = 0; j < state.allProducts.length; j++) {
      var room = getRoomForType(state.allProducts[j].type);
      if (room) counts[room]++;
    }

    /* "All" pill */
    var allBtn = document.createElement('button');
    allBtn.className = 'ega-room-pill' + (state.activeRoom === null ? ' active' : '');
    allBtn.textContent = 'All';
    allBtn.onclick = function () { setActiveRoom(null); };
    bar.appendChild(allBtn);

    /* Room pills */
    for (var k = 0; k < ROOM_MAP.length; k++) {
      if (counts[ROOM_MAP[k].label] === 0) continue;
      var btn = document.createElement('button');
      btn.className = 'ega-room-pill' + (state.activeRoom === ROOM_MAP[k].label ? ' active' : '');
      btn.textContent = ROOM_MAP[k].label;
      btn.dataset.room = ROOM_MAP[k].label;
      (function (label) {
        btn.onclick = function () { setActiveRoom(label); };
      })(ROOM_MAP[k].label);
      bar.appendChild(btn);
    }
  }

  function setActiveRoom(room) {
    state.activeRoom = room;
    /* Update pill active state */
    var pills = document.querySelectorAll('.ega-room-pill');
    for (var i = 0; i < pills.length; i++) {
      if (room === null) {
        pills[i].classList.toggle('active', !pills[i].dataset.room);
      } else {
        pills[i].classList.toggle('active', pills[i].dataset.room === room);
      }
    }
    /* Rebuild sidebar with room-filtered products to update counts */
    var products = room ? getProductsForRoom(state.allProducts, room) : state.allProducts;
    buildSidebar(products);
    applyFilters();
  }

  // ── Collection type detection ────────────────────────────────────────────
  /* ── Product type → collection type mapping ─────────────────────────── */
  /* Maps lowercase product_type values to the filter "collectionType" used by TAG_GROUPS */
  var PRODUCT_TYPE_MAP = {
    'dining tables': 'dining', 'dining chairs': 'dining',
    'sofas': 'sofa',
    'accent chairs': 'seating', 'desk chairs': 'seating', 'stools': 'seating',
    'benches': 'seating', 'ottomans and poufs': 'seating',
    'beds': 'bed',
    'nightstands': 'nightstand',
    'dressers': 'storage', 'chest of drawers': 'storage', 'bookcases': 'storage',
    'bookshelves & etageres': 'storage', 'cabinets': 'storage', 'bar cabinets': 'storage',
    'sideboards & buffets': 'storage',
    'chandeliers': 'lighting', 'lanterns': 'lighting', 'floor lamps': 'lighting',
    'table lighting': 'lighting', 'sconces': 'lighting', 'candelabra': 'lighting',
    'mirrors': 'mirror',
    'desks': 'desk',
    'coffee & cocktail tables': 'coffee',
    'console tables': 'console',
    'side & end tables': 'table', 'center tables': 'table', 'accent tables': 'table',
    'accessories': 'general',
  };

  function detectCollectionType(handle) {
    var h = (handle || '').toLowerCase();
    if (h.indexOf('dining') !== -1) return 'dining';
    if (h.indexOf('sofa') !== -1 || h.indexOf('sectional') !== -1 || h.indexOf('loveseat') !== -1) return 'sofa';
    if (h.indexOf('chair') !== -1 || h.indexOf('seating') !== -1 || h.indexOf('ottoman') !== -1 || h.indexOf('bench') !== -1) return 'seating';
    if (h.indexOf('bed') !== -1 && h.indexOf('side') === -1) return 'bed';
    if (h.indexOf('nightstand') !== -1) return 'nightstand';
    if (h.indexOf('dresser') !== -1 || h.indexOf('chest') !== -1 || h.indexOf('bookcase') !== -1 ||
        h.indexOf('sideboard') !== -1 || h.indexOf('credenza') !== -1 ||
        h.indexOf('cabinet') !== -1 || h.indexOf('armoire') !== -1 || h.indexOf('storage') !== -1 ||
        h.indexOf('wardrobe') !== -1) return 'storage';
    if (h.indexOf('lighting') !== -1 || h.indexOf('lamp') !== -1 || h.indexOf('chandelier') !== -1 ||
        h.indexOf('sconce') !== -1 || h.indexOf('pendant') !== -1) return 'lighting';
    if (h.indexOf('mirror') !== -1) return 'mirror';
    if (h.indexOf('desk') !== -1) return 'desk';
    if (h.indexOf('coffee') !== -1 || h.indexOf('cocktail') !== -1) return 'coffee';
    if (h.indexOf('console') !== -1) return 'console';
    if (h.indexOf('table') !== -1) return 'table';
    return 'general';
  }

  /* Determine effective collection type — considers selected product-type filter */
  function getEffectiveCollectionType() {
    /* If the collection itself is a specific type (not style/general), use that */
    if (state.collectionType !== 'style' && state.collectionType !== 'general') {
      return state.collectionType;
    }
    /* If exactly one product type is selected in filters, derive type from it */
    var ptFilter = state.activeFilters['product-type'];
    if (ptFilter && ptFilter.size === 1) {
      var selectedType = Array.from(ptFilter)[0];
      return PRODUCT_TYPE_MAP[selectedType] || 'general';
    }
    /* Multiple or no product types selected on a style/general collection */
    return state.collectionType;
  }

  function groupVisibleForCollection(group, collectionType) {
    if (group.includeCollectionHandles) {
      return group.includeCollectionHandles.indexOf(state.collectionHandle) !== -1;
    }
    if (group.includeCollectionTypes && group.includeCollectionTypes.length) {
      return group.includeCollectionTypes.some(function (t) {
        return collectionType.indexOf(t) !== -1 || t.indexOf(collectionType) !== -1;
      });
    }
    if (group.excludeCollectionTypes && group.excludeCollectionTypes.length) {
      return !group.excludeCollectionTypes.some(function (t) {
        return collectionType.indexOf(t) !== -1 || t.indexOf(collectionType) !== -1;
      });
    }
    return true;
  }

  // ── Init ─────────────────────────────────────────────────────────────────
  function init() {
    var wrap = document.getElementById('ega-cf-wrap');
    if (!wrap) return;
    state.collectionHandle = wrap.dataset.handle || '';
    /* Re-resolve defensively (initToolbar already did it at bootstrap) */
    resolveQuickFilters();
    /* Blank-handle guard: non-collection contexts (e.g. ega-search-results renders
       #ega-cf-wrap with data-handle="" + data-search-query) have no collection to
       fetch. This filter has no search-mode implementation, so bail cleanly instead of
       GET /collections//products.json -> 404, which otherwise leaves a perpetual
       "Loading products…" spinner. Hide the spinner and stop. (Real search-results
       rendering is a separate feature, not implemented here.) */
    if (!state.collectionHandle) {
      var loadingEl = document.getElementById('ega-cf-loading');
      if (loadingEl) loadingEl.style.display = 'none';
      return;
    }
    state.collectionType = detectCollectionType(state.collectionHandle);

    /* Detect style collection from data attribute on outer wrapper */
    var pageWrap = document.getElementById('ega-cf-page-wrap');
    state.isStyleCollection = pageWrap && pageWrap.dataset.isStyle === 'true';
    if (state.isStyleCollection) state.collectionType = 'style';

    restoreFromUrl();

    // Load dimension data in parallel. Prefer the liquid-supplied URL: it carries
    // Shopify's own asset version hash, so a regenerated file busts the CDN cache by
    // itself. The old path built the URL here with a hardcoded "?v=20260330c", which
    // meant a rebuilt dimensions file was never actually served (found 2026-08-07:
    // the theme had the new file, the browser kept being handed March's).
    (function () {
      var cssLink = document.querySelector('link[href*="ega-collection-filter"]');
      var dimsUrl = wrap.dataset.dimsUrl ||
        (cssLink ? cssLink.href.replace(/ega-collection-filter\.css[^"]*/, 'product-dimensions.json') : '');
      if (dimsUrl) {
        fetch(dimsUrl)
          .then(function (r) { return r.ok ? r.json() : {}; })
          .then(function (d) { state.productDims = d; state.dimsSettled = true; if (state.loaded) { applyDerivedDimTags(); buildSidebar(state.allProducts); applyFilters(); } })
          .catch(function () { state.productDims = {}; state.dimsSettled = true; consumePendingReturn(); });
      } else {
        state.dimsSettled = true;
      }
    })();

    /* Phase 4 LCP fix (Dan 2026-07-09): the first grid page is now server-rendered in
       Liquid (snippets/ega-cf-card.liquid). On a CANONICAL collection URL (no filter/
       sort params) those server cards ARE the correct featured first paint, so we keep
       them and DEFER the full-dataset fetch to idle — it never blocks LCP; the sidebar,
       sort, infinite scroll and F8 interrupts hydrate on top once the data lands, and
       renderGrid() re-renders the identical cards seamlessly.

       On a FILTERED/SORTED deep-link (?f_style=…, ?sort=…, ?instock=1, price_*) the
       server cards are the UNFILTERED featured set — showing them would flash the wrong
       products. So we clear them and restore the loading state until the JS renders the
       correct filtered set (Dan, 2026-07-09: canonical URLs keep the server first paint;
       deep-links keep the spinner). Query params aren't readable in Liquid, so this
       decision has to live here. When no server cards exist (legacy JS template), we
       just load immediately as before. */
    armReturnCapture();

    var grid = document.getElementById('ega-cf-grid');
    var hasServerCards = grid && grid.querySelector('.ega-cf-card');
    if (hasServerCards && !hasActiveUrlState()) {
      state.serverPrerendered = true;
      /* Crawlable pagination (SEO audit 2026-09-30): on ?page=N (N > 1) the server cards
         are page N of the collection. Keep them on the first render instead of redrawing
         from product 1; any filter/sort after that renders the JS set as usual. */
      state.keepServerPage = (parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1) > 1;
      /* A pending back-navigation restore needs the dataset NOW, not at idle */
      if (pendingReturn) ensureDataset();
      else scheduleIdle(ensureDataset);
    } else {
      if (hasServerCards) {
        /* CLS fix (Sep 16 2026): clearing the server cards collapsed the grid from
           ~640px to the 'Loading' stub and pushed the SEO block up into the viewport,
           then the JS render pushed it back down: two shifts of 0.6 each, Lighthouse
           CLS 1.2 on every deep-linked collection URL (?instock=1 etc). Reserve the
           server grid's height while the dataset loads; renderGrid releases it. */
        grid.style.minHeight = grid.offsetHeight + 'px';
        grid.innerHTML = '';
        var loadingEl = document.getElementById('ega-cf-loading');
        if (loadingEl) loadingEl.style.display = '';
      }
      ensureDataset();
    }
  }

  /* True when the URL carries filter/sort state that the server-rendered (featured,
     unfiltered) first page does NOT reflect. Collection-intrinsic auto-selection
     (autoSelectFromCollection) is deliberately NOT counted — for those collections
     collection.products already IS the right membership, so no flash. */
  function hasActiveUrlState() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('sort') && params.get('sort') !== 'featured') return true;
    if (params.get('instock') === '1' || params.get('customizable') === '1') return true;
    if (params.get('price_min') || params.get('price_max')) return true;
    /* Legacy pre-filter params (applyUrlPreFilters) change membership too */
    if (params.get('style') || params.get('type')) return true;
    /* product-type / style-unified are written by updateUrl but live outside TAG_GROUPS */
    if (params.get('f_product-type') || params.get('f_style-unified')) return true;
    for (var i = 0; i < TAG_GROUPS.length; i++) {
      if (params.get('f_' + TAG_GROUPS[i].id)) return true;
    }
    return false;
  }

  // ── Dataset load (deferred off the LCP critical path) ─────────────────────
  var datasetPromise = null;
  function scheduleIdle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 1200 });
    else setTimeout(fn, 200);
  }
  function ensureDataset() {
    if (datasetPromise) return datasetPromise;
    datasetPromise = loadProducts(state.collectionHandle).then(function (products) {
      /* Keep $0 products — they may be "price on request" or custom-priced */
      state.allProducts = products;
      state.loaded = true;
      applyDerivedDimTags();
      var prices = products.map(function (p) { return p.price; }).filter(function (p) { return p > 0; });
      state.priceMin = prices.length ? Math.min.apply(null, prices) / 100 : 0;
      state.priceMax = prices.length ? Math.max.apply(null, prices) / 100 : 100000;
      if (!isFinite(state.priceMinActive)) state.priceMinActive = state.priceMin;
      if (!isFinite(state.priceMaxActive)) state.priceMaxActive = state.priceMax;

      /* Apply URL pre-filters and auto-detect from collection handle */
      applyUrlPreFilters();
      autoSelectFromCollection();

      buildSidebar(products);
      applyFilters();

      /* Set up infinite scroll */
      initInfiniteScroll();
      return products;
    }).catch(function (err) {
      console.error('[EGA Filter] Load error:', err);
      var el = document.getElementById('ega-cf-loading');
      if (el) el.textContent = 'Unable to load products.';
    });
    return datasetPromise;
  }

  // ── Product loading ───────────────────────────────────────────────────────
  var CDN_PREFIX = 'https://cdn.shopify.com/s/files/';
  var PAGE_SIZE = 48;

  function loadProducts(handle) {
    /* Table shape and dining Type tags are edited in Shopify independently of
       theme assets. Read those collections fresh on navigation so an older
       five-minute per-tab snapshot cannot resurrect corrected classifications. */
    if (QUICK_FILTERS_BY_HANDLE[handle]) return fetchAllProducts(handle);

    /* If static data asset is available, use it (instant, no API calls) */
    if (window.EGA_ALL_PRODUCTS) {
      var products = window.EGA_ALL_PRODUCTS.map(normalizeStaticProduct);
      /* Scope static dataset to the current Shopify collection when position metadata exists. */
      if (handle && handle !== 'all') {
        var scoped = products.filter(function (p) {
          return p.collectionPositions && Object.prototype.hasOwnProperty.call(p.collectionPositions, handle);
        });
        /* Some virtual/style collections do not carry collection position metadata in the static payload.
           In that case, keep the full dataset and let handle-based auto-filters scope it. */
        if (scoped.length > 0) products = scoped;
      }
      return Promise.resolve(products);
    }

    /* Fallback: fetch from collection JSON API */
    var cacheKey = 'ega_cf_v3_' + handle; /* v3: carries availLabel (2026-10-13) */
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        var data = JSON.parse(cached);
        if (data && data.ts && (Date.now() - data.ts < 5 * 60 * 1000)) return Promise.resolve(data.products);
      }
    } catch (e) {}
    return fetchAllProducts(handle).then(function (products) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products: products })); } catch (e) {}
      return products;
    });
  }

  function normalizeStaticProduct(p) {
    return {
      id: p.id, title: p.t, handle: p.h,
      url: '/products/' + p.h,
      image: p.i ? (p.i.indexOf('http') === 0 ? p.i : CDN_PREFIX + p.i) : '',
      price: p.p || 0,
      comparePrice: p.cp || 0,
      tags: p.g || [],
      createdAt: p.c || '',
      type: p.y || '',
      vendor: p.v || '',
      inStock: false, /* static snapshot predates the inventory rule; never claims In Stock */
      collectionPositions: p._pos || {},
    };
  }

  /* Availability truth (Dino, 2026-10-13): products.json carries no inventory or metafields,
     so the label every JS card shows comes from the collection.ega-avail view, which renders
     snippets/ega-availability.liquid (the same rule as ega-cf-card and the PDP). */
  function fetchAvailLabels(handle) {
    var labels = {};
    function fetchPage(page) {
      return fetch('/collections/' + handle + '?view=ega-avail&page=' + page)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          var l = (data && data.labels) || {};
          for (var k in l) if (Object.prototype.hasOwnProperty.call(l, k)) labels[k] = l[k];
          return (data && data.pages) || 1;
        });
    }
    /* Page 1 says how many pages exist; the rest load in parallel. */
    return fetchPage(1).then(function (pages) {
      var rest = [];
      for (var i = 2; i <= pages; i++) rest.push(fetchPage(i));
      return Promise.all(rest);
    }).then(function () { return labels; }).catch(function (err) {
      console.error('[EGA Filter] Availability labels unavailable:', err);
      return null;
    });
  }

  function fetchAllProducts(handle) {
    return Promise.all([fetchAllProductPages(handle), fetchAvailLabels(handle)]).then(function (res) {
      var labels = res[1];
      res[0].forEach(function (p) {
        p.availLabel = labels && labels[p.handle] ? labels[p.handle] : '';
        p.inStock = p.availLabel === 'In Stock';
      });
      return res[0];
    });
  }

  function fetchAllProductPages(handle) {
    var all = [];
    function fetchPage(page) {
      return fetch('/collections/' + handle + '/products.json?limit=250&page=' + page)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          if (!data || !data.products) throw new Error('No products in response');
          var products = data.products.map(normalizeProduct);
          all = all.concat(products);
          if (products.length === 250) return fetchPage(page + 1);
          return all;
        });
    }
    return fetchPage(1);
  }

  function normalizeProduct(p) {
    var variant = (p.variants && p.variants[0]) || {};
    var rawTags = Array.isArray(p.tags)
      ? p.tags.map(function (t) { return String(t).trim().toLowerCase(); })
      : (p.tags || '').split(',').map(function (t) { return t.trim().toLowerCase(); });
    // inStock = true if ANY variant is available
    var inStock = Array.isArray(p.variants) && p.variants.some(function (v) { return v.available; });
    return {
      id: p.id, title: p.title, handle: p.handle,
      url: '/products/' + p.handle,
      image: p.images && p.images[0] ? p.images[0].src : '',
      price: variant.price ? Math.round(parseFloat(variant.price) * 100) : 0,
      comparePrice: variant.compare_at_price ? Math.round(parseFloat(variant.compare_at_price) * 100) : 0,
      tags: rawTags,
      createdAt: p.created_at || '',
      type: (p.product_type || '').toLowerCase(),
      vendor: p.vendor || '',
      inStock: inStock,
    };
  }

  // ── Facet builder ─────────────────────────────────────────────────────────
  /* Renders every facet group into #ega-cf-sidebar-body. That container kept its
     id when it moved from the old left sidebar into the All Filters drawer, so
     the render target (and every selector below) is unchanged. */
  function buildSidebar(products) {
    var sidebar = document.getElementById('ega-cf-sidebar-body');
    if (!sidebar) return;
    sidebar.innerHTML = '';

    /* ── In Stock collection pre-scope (see autoSelectFromCollection) ── */
    if (state.implicitInstock) {
      products = products.filter(function (p) { return p.inStock; });
    }

    /* ── Scope products by room tag (Living Room, Dining Room, etc.) ── */
    if (state.roomTag) {
      products = products.filter(function (p) {
        return p.tags.indexOf(state.roomTag) !== -1;
      });
    }

    /* ── Scope products by century tag (18th, 19th, 20th century) ── */
    /* Combined: century TAG + at least one matching STYLE tag */
    if (state.centuryTag) {
      var centuryStylesForCounts = CENTURY_STYLE_MAP[state.centuryTag];
      if (centuryStylesForCounts) {
        products = products.filter(function (p) {
          var hasCenturyTag = p.tags.indexOf(state.centuryTag) !== -1;
          var hasStyleTag = centuryStylesForCounts.some(function (s) { return p.tags.indexOf(s) !== -1; });
          return hasCenturyTag && hasStyleTag;
        });
      } else {
        products = products.filter(function (p) {
          return p.tags.indexOf(state.centuryTag) !== -1;
        });
      }
    }

    /* ── Scope products to active style filter for accurate counts ── */
    var activeStyles = state.activeFilters['style-unified'] || new Set();
    var scopedProducts = products;
    if (activeStyles.size > 0) {
      // Separate centuries (AND) from individual styles (OR) — same logic as applyFilters
      var scopeCenturies = [];
      var scopeIndividualStyles = [];
      activeStyles.forEach(function (s) {
        if (CENTURY_STYLE_MAP[s]) {
          scopeCenturies.push(s);
        } else {
          scopeIndividualStyles.push(s);
        }
      });
      if (scopeCenturies.length > 0) {
        scopedProducts = scopedProducts.filter(function (p) {
          return scopeCenturies.some(function (c) { return p.tags.indexOf(c) !== -1; });
        });
      }
      if (scopeIndividualStyles.length > 0) {
        scopedProducts = scopedProducts.filter(function (p) {
          return scopeIndividualStyles.some(function (s) { return p.tags.indexOf(s) !== -1; });
        });
      }
    }

    /* ── Product Type / Browse ── */
    /* The tiered "Browse" module — links to sibling collections — was REMOVED from the
       drawer 2026-08-08 (Dino): "the drawer is for filtering. That is not filtering,
       that is confusing, and if they want another collection we have links not an inch
       away." The category rail at the top of the page does that job. buildTypeBrowseGroup
       is kept for the checkbox fallback path below, which IS a filter. */
    {
      var browseGroup = null;
      if (browseGroup) {
        sidebar.appendChild(browseGroup);
      } else {
        var typeCounts = {};
        var typeSourceProducts = state.implicitTypes && state.implicitTypes.size > 0
          ? scopedProducts.filter(function (p) { return state.implicitTypes.has(p.type); })
          : scopedProducts;
        typeSourceProducts.forEach(function (p) {
          var t = p.type || 'other';
          typeCounts[t] = (typeCounts[t] || 0) + 1;
        });
        var typeEntries = Object.keys(typeCounts)
          .filter(function (t) { return typeCounts[t] > 0; })
          .sort(function (a, b) { return typeCounts[b] - typeCounts[a]; });
        /* bar-stools mixes types Bar Stools / Counter Stools that don't track height (old counter
           stools are typed Bar Stools); the Seat Height group (stool_ tags) does that job there */
        if (typeEntries.length > 1 && state.collectionHandle !== 'bar-stools') {
          var typeActive = state.activeFilters['product-type'] || new Set();
          var typeGroup = document.createElement('div');
          var typeIsPreSelected = typeActive.size > 0;
          var isTypeCollection = typeIsPreSelected && !state.isStyleCollection && !state.roomTag && !state.centuryTag;
          typeGroup.className = isTypeCollection ? 'ega-cf-group' : 'ega-cf-group open';
          typeGroup.dataset.groupId = 'product-type';
          var typeHeader = document.createElement('button');
          typeHeader.className = 'ega-cf-group-toggle';
          typeHeader.type = 'button';
          typeHeader.innerHTML = '<span class="ega-cf-group-label">Browse by Room</span>' +
            '<svg class="ega-cf-group-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>';
          var typeBody = document.createElement('div');
          typeBody.className = 'ega-cf-group-body';
          typeHeader.setAttribute('aria-expanded', 'true');
          typeHeader.addEventListener('click', function () { toggleGroup(typeGroup); });

          /* On style/room/century collections, group types by room */
          var useRoomGrouping = state.isStyleCollection || state.roomTag || state.centuryTag || typeEntries.length > 10;
          if (useRoomGrouping) {
            var typeSet = new Set(typeEntries);
            var placed = new Set();
            ROOM_MAP.forEach(function (room) {
              /* Collect types in this room that actually have products */
              var roomTypes = room.types.filter(function (t) { return typeSet.has(t); });
              if (roomTypes.length === 0) return;
              /* Room total = sum of all types in this room */
              var roomTotal = roomTypes.reduce(function (sum, t) { return sum + (typeCounts[t] || 0); }, 0);
              /* Build a collapsible sub-section for this room */
              var roomDiv = document.createElement('div');
              roomDiv.className = 'ega-cf-room-group';
              var roomHeader = document.createElement('button');
              roomHeader.type = 'button';
              roomHeader.className = 'ega-cf-room-header';
              roomHeader.innerHTML = '<span>' + room.label + '</span><span class="ega-cf-option-count">' + roomTotal + '</span>';
              var roomBody = document.createElement('div');
              roomBody.className = 'ega-cf-room-body';
              roomBody.style.display = 'none';
              roomHeader.addEventListener('click', function () {
                var isOpen = roomBody.style.display !== 'none';
                roomBody.style.display = isOpen ? 'none' : 'block';
                roomDiv.classList.toggle('open', !isOpen);
              });
              roomTypes.sort(function (a, b) { return (typeCounts[b] || 0) - (typeCounts[a] || 0); });
              roomTypes.forEach(function (t) {
                placed.add(t);
                var label = t.split(' ').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
                var opt = document.createElement('label');
                opt.className = 'ega-cf-option';
                var cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.checked = typeActive.has(t);
                cb.setAttribute('data-group', 'product-type');
                cb.setAttribute('data-tag', t);
                cb.onchange = function () {
                  if (!state.activeFilters['product-type']) state.activeFilters['product-type'] = new Set();
                  if (cb.checked) state.activeFilters['product-type'].add(t);
                  else state.activeFilters['product-type'].delete(t);
                  if (state.activeFilters['product-type'].size === 0) delete state.activeFilters['product-type'];
                  var roomProducts = (state.isStyleCollection && state.activeRoom) ? getProductsForRoom(state.allProducts, state.activeRoom) : state.allProducts;
                  buildSidebar(roomProducts);
                  applyFilters();
                };
                opt.appendChild(cb);
                var span = document.createElement('span');
                span.textContent = label;
                opt.appendChild(span);
                var cnt = document.createElement('span');
                cnt.className = 'ega-cf-option-count';
                cnt.textContent = typeCounts[t];
                opt.appendChild(cnt);
                roomBody.appendChild(opt);
              });
              roomDiv.appendChild(roomHeader);
              roomDiv.appendChild(roomBody);
              typeBody.appendChild(roomDiv);
            });
            /* Any remaining types not in a room */
            typeEntries.filter(function (t) { return !placed.has(t); }).forEach(function (t) {
              var label = t.split(' ').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
              var opt = document.createElement('label');
              opt.className = 'ega-cf-option';
              var cb = document.createElement('input');
              cb.type = 'checkbox';
              cb.checked = typeActive.has(t);
              cb.setAttribute('data-group', 'product-type');
              cb.setAttribute('data-tag', t);
              cb.onchange = function () {
                if (!state.activeFilters['product-type']) state.activeFilters['product-type'] = new Set();
                if (cb.checked) state.activeFilters['product-type'].add(t);
                else state.activeFilters['product-type'].delete(t);
                if (state.activeFilters['product-type'].size === 0) delete state.activeFilters['product-type'];
                var roomProducts = (state.isStyleCollection && state.activeRoom) ? getProductsForRoom(state.allProducts, state.activeRoom) : state.allProducts;
                buildSidebar(roomProducts);
                applyFilters();
              };
              opt.appendChild(cb);
              var span = document.createElement('span');
              span.textContent = label;
              opt.appendChild(span);
              var cnt = document.createElement('span');
              cnt.className = 'ega-cf-option-count';
              cnt.textContent = typeCounts[t];
              opt.appendChild(cnt);
              typeBody.appendChild(opt);
            });
          } else {
            /* Flat list for small type sets (e.g., Lighting sub-types) */
            typeEntries.forEach(function (t) {
              var label = t.split(' ').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
              var opt = document.createElement('label');
              opt.className = 'ega-cf-option';
              var cb = document.createElement('input');
              cb.type = 'checkbox';
              cb.checked = typeActive.has(t);
              cb.setAttribute('data-group', 'product-type');
              cb.setAttribute('data-tag', t);
              cb.onchange = function () {
                if (!state.activeFilters['product-type']) state.activeFilters['product-type'] = new Set();
                if (cb.checked) state.activeFilters['product-type'].add(t);
                else state.activeFilters['product-type'].delete(t);
                if (state.activeFilters['product-type'].size === 0) delete state.activeFilters['product-type'];
                var roomProducts = (state.isStyleCollection && state.activeRoom) ? getProductsForRoom(state.allProducts, state.activeRoom) : state.allProducts;
                buildSidebar(roomProducts);
                applyFilters();
              };
              opt.appendChild(cb);
              var span = document.createElement('span');
              span.textContent = label;
              opt.appendChild(span);
              var cnt = document.createElement('span');
              cnt.className = 'ega-cf-option-count';
              cnt.textContent = typeCounts[t];
              opt.appendChild(cnt);
              typeBody.appendChild(opt);
            });
          }

          typeGroup.appendChild(typeHeader);
          typeGroup.appendChild(typeBody);
          sidebar.appendChild(typeGroup);
        }
      }
    }

    var effectiveType = getEffectiveCollectionType();

    /* Use style-scoped products for all filter counts below */
    var unscopedProducts = products;
    products = scopedProducts;

    /* Also scope by active product-type filter for accurate counts below */
    var activeTypes = state.activeFilters['product-type'] || new Set();
    /* Merge implicit types (from parent collections like Lighting) with any explicit user selections */
    var effectiveTypes = new Set(activeTypes);
    if (state.implicitTypes && state.implicitTypes.size > 0 && effectiveTypes.size === 0) {
      state.implicitTypes.forEach(function (t) { effectiveTypes.add(t); });
    }
    if (effectiveTypes.size > 0) {
      products = products.filter(function (p) { return effectiveTypes.has(p.type); });
    }

    /* For the Style group: scope by type + availability but NOT by style (so you see all available styles for the selected type/availability) */
    var typeOnlyScoped = unscopedProducts;
    if (effectiveTypes.size > 0) {
      typeOnlyScoped = typeOnlyScoped.filter(function (p) { return effectiveTypes.has(p.type); });
    }
    /* Apply in-stock and customizable filters to style counts so numbers reflect current narrowing */
    if (state.instockOnly) {
      typeOnlyScoped = typeOnlyScoped.filter(function (p) { return p.inStock; });
    }
    if (state.customizableOnly) {
      typeOnlyScoped = typeOnlyScoped.filter(function (p) { return p.tags.indexOf('customizable') !== -1; });
    }

    TAG_GROUPS.forEach(function (group) {
      if (!groupVisibleForCollection(group, effectiveType)) return;

      /* Availability — merged In Stock + Customizable MTO */
      if (group.type === 'custom-availability') {
        /* On the In Stock collection every product already passes, so the checkbox would be a no-op; hide it */
        var instockCount = state.implicitInstock ? 0 : products.filter(function (p) { return p.inStock; }).length;
        var customCount = products.filter(function (p) { return p.tags.indexOf('customizable') !== -1; }).length;
        if (!instockCount && !customCount) return;
        sidebar.appendChild(buildAvailabilityGroup(instockCount, customCount));
        return;
      }

      /* ── Style filter ── */
      if (group.type === 'custom-style') {
        /* On Browse by Style collections, this facet is redundant and can show misleading counts. */
        if (state.isStyleCollection || STYLE_HANDLE_MAP[state.collectionHandle]) return;
        /* Build style checkbox group */
        sidebar.appendChild(buildUnifiedStyleGroup(typeOnlyScoped));
        return;
      }

      /* Dining table: Seating capacity (from seat_* tags) */
      if (group.type === 'custom-dt-seating') {
        var seatBuckets = { 's2_4': 0, 's4_6': 0, 's6_8': 0, 's8_10': 0, 's10_plus': 0, 's_unknown': 0 };
        products.forEach(function (p) {
          if (p.tags.indexOf('seat_2-4') !== -1) seatBuckets.s2_4++;
          else if (p.tags.indexOf('seat_4-6') !== -1) seatBuckets.s4_6++;
          else if (p.tags.indexOf('seat_6-8') !== -1) seatBuckets.s6_8++;
          else if (p.tags.indexOf('seat_8-10') !== -1) seatBuckets.s8_10++;
          else if (p.tags.indexOf('seat_10-plus') !== -1) seatBuckets.s10_plus++;
          else seatBuckets.s_unknown++;
        });
        var hasAny = Object.values(seatBuckets).some(function (v) { return v > 0; });
        if (!hasAny) return;
        var seatOpts = [
          { key: 's2_4', label: '2\u20134 seats', count: seatBuckets.s2_4 },
          { key: 's4_6', label: '4\u20136 seats', count: seatBuckets.s4_6 },
          { key: 's6_8', label: '6\u20138 seats', count: seatBuckets.s6_8 },
          { key: 's8_10', label: '8\u201310 seats', count: seatBuckets.s8_10 },
          { key: 's10_plus', label: '10+ seats', count: seatBuckets.s10_plus },
        ].filter(function (o) { return o.count > 0; });
        /* No "Other" catch-all (Dino, 2026-08-06): untagged products are not a
           customer-facing choice. A group left with fewer than two real options
           filters nothing — skip it. Same rule in every bucket group below. */
        if (seatOpts.length >= 2) sidebar.appendChild(buildCheckboxGroupCustom('dt-seating', 'Seating', seatOpts));
        return;
      }

      /* Dining table: Length (from len_* tags) */
      if (group.type === 'custom-dt-length') {
        var lenBuckets = { 'l_under60': 0, 'l_60_72': 0, 'l_72_84': 0, 'l_84_96': 0, 'l_96_108': 0, 'l_108_plus': 0, 'l_unknown': 0 };
        products.forEach(function (p) {
          if (p.tags.indexOf('len_48-60in') !== -1) lenBuckets.l_under60++;
          else if (p.tags.indexOf('len_under-36in') !== -1 || p.tags.indexOf('len_36-48in') !== -1) lenBuckets.l_under60++;
          else if (p.tags.indexOf('len_60-72in') !== -1) lenBuckets.l_60_72++;
          else if (p.tags.indexOf('len_72-84in') !== -1) lenBuckets.l_72_84++;
          else if (p.tags.indexOf('len_84-96in') !== -1) lenBuckets.l_84_96++;
          else if (p.tags.indexOf('len_96-108in') !== -1) lenBuckets.l_96_108++;
          else if (p.tags.indexOf('len_108-plus') !== -1) lenBuckets.l_108_plus++;
          else lenBuckets.l_unknown++;
        });
        var lenOpts = [
          { key: 'l_under60', label: 'Less than 60\u201D', count: lenBuckets.l_under60 },
          { key: 'l_60_72', label: '60\u201D \u2013 72\u201D', count: lenBuckets.l_60_72 },
          { key: 'l_72_84', label: '72\u201D \u2013 84\u201D', count: lenBuckets.l_72_84 },
          { key: 'l_84_96', label: '84\u201D \u2013 96\u201D', count: lenBuckets.l_84_96 },
          { key: 'l_96_108', label: '96\u201D \u2013 108\u201D', count: lenBuckets.l_96_108 },
          { key: 'l_108_plus', label: '108\u201D and above', count: lenBuckets.l_108_plus },
        ].filter(function (o) { return o.count > 0; });
        if (lenOpts.length >= 2) sidebar.appendChild(buildCheckboxGroupCustom('dt-length', 'Length', lenOpts));
        return;
      }

      /* Dining table: Extension vs Fixed */
      if (group.type === 'custom-dt-extension') {
        var extCount = 0, fixedCount = 0;
        products.forEach(function (p) {
          var type = diningExtensionTypeOf(p);
          if (type === 'extension') extCount++;
          else if (type === 'fixed') fixedCount++;
        });
        if (!extCount && !fixedCount) return;
        var extOpts = [];
        if (extCount) extOpts.push({ key: 'extension', label: 'Extension', count: extCount });
        if (fixedCount) extOpts.push({ key: 'fixed', label: 'Fixed Size', count: fixedCount });
        sidebar.appendChild(buildCheckboxGroupCustom('dt-extension', 'Type', extOpts));
        return;
      }

      /* Finish / Material (mutually exclusive: Stone > Metal > Leather > Painted > Dark Wood > Light Wood > Other) */
      if (group.type === 'custom-dt-finish') {
        var finishBuckets = { stone: 0, metal: 0, leather: 0, cane: 0, linen: 0, painted: 0, dark: 0, light: 0, other: 0 };
        products.forEach(function (p) { finishBuckets[finishBucketOf(p)]++; });
        /* "Upholstered" replaces "Linen" (Dino, 2026-08-07): the bucket is every
           fabric-fronted piece, not the fibre. Seats whose frame is the story carry a
           wood override instead, so this stays the fully-upholstered pieces. */
        var finOpts = [
          { key: 'light', label: 'Light Wood', count: finishBuckets.light },
          { key: 'dark', label: 'Dark Wood', count: finishBuckets.dark },
          { key: 'painted', label: 'Painted', count: finishBuckets.painted },
          { key: 'cane', label: 'Cane & Rattan', count: finishBuckets.cane },
          { key: 'leather', label: 'Leather', count: finishBuckets.leather },
          { key: 'linen', label: 'Upholstered', count: finishBuckets.linen },
          { key: 'stone', label: 'Stone / Marble', count: finishBuckets.stone },
          { key: 'metal', label: 'Metal / Glass', count: finishBuckets.metal },
        ].filter(function (o) { return o.count > 0; });
        if (finOpts.length >= 2) sidebar.appendChild(buildCheckboxGroupCustom('dt-finish', 'Finish / Material', finOpts));
        return;
      }

      /* Upholstery — Stocked in Fabric / Stocked in Leather (stock items only) */
      if (group.type === 'custom-upholstery') {
        var uphBuckets = { fabric: 0, leather: 0 };
        products.forEach(function (p) {
          var isStock = p.tags.indexOf('stock items') !== -1;
          if (!isStock) return;
          var hasLeather = p.tags.indexOf('material__leather') !== -1;
          var hasFabric = p.tags.indexOf('seat__upholstered') !== -1 || p.tags.indexOf('finish__fabric') !== -1 || p.tags.indexOf('material__upholstered') !== -1 || p.tags.indexOf('material__velvet') !== -1 || p.tags.indexOf('material__linen') !== -1;
          if (hasLeather) uphBuckets.leather++;
          if (hasFabric || (!hasLeather && !hasFabric)) uphBuckets.fabric++;
        });
        var uphOpts = [
          { key: 'fabric', label: 'Stocked in Fabric', count: uphBuckets.fabric },
          { key: 'leather', label: 'Stocked in Leather', count: uphBuckets.leather },
        ].filter(function (o) { return o.count > 0; });
        if (uphOpts.length) sidebar.appendChild(buildCheckboxGroupCustom('upholstery', 'Upholstery', uphOpts));
        return;
      }

      /* Width — collection-aware buckets */
      if (group.type === 'custom-width') {
        var isSofaLike = ['sofa','seating','sectional'].some(function(k) { return state.collectionType.indexOf(k) !== -1; });
        // Use actual dims for smarter width bucketing (84" split ≈ loveseat+small vs standard+large)
        var under84 = 0, over84 = 0;
        products.forEach(function (p) {
          var dim = state.productDims && state.productDims[p.handle];
          if (dim && dim.w) {
            if (dim.w < 84) under84++;
            else over84++;
          } else {
            // Fallback to tags
            if (p.tags.indexOf('dim_w_over-72in') !== -1) over84++;
            else if (p.tags.indexOf('dim_w_48-72in') !== -1 || p.tags.indexOf('dim_w_24-48in') !== -1 || p.tags.indexOf('dim_w_under-24in') !== -1) under84++;
          }
        });
        if (isSofaLike && (under84 || over84)) {
          var el = document.createElement('div');
          el.className = 'ega-cf-group open';
          el.dataset.groupId = 'dimw';
          var rows = [];
          if (under84) rows.push('<label class="ega-cf-option"><input type="checkbox" data-width="under84"><span>Less than 84\u201D</span><span class="ega-cf-option-count">' + under84 + '</span></label>');
          if (over84) rows.push('<label class="ega-cf-option"><input type="checkbox" data-width="over84"><span>84\u201D and above</span><span class="ega-cf-option-count">' + over84 + '</span></label>');
          el.innerHTML = '<button class="ega-cf-group-toggle" aria-expanded="true"><span class="ega-cf-group-label">Width</span><span class="ega-cf-group-arrow">&#9660;</span></button><div class="ega-cf-group-body">' + rows.join('') + '</div>';
          el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
          el.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
            cb.addEventListener('change', function () {
              if (!state.activeFilters.dimw) state.activeFilters.dimw = new Set();
              var wkey = cb.getAttribute('data-width');
              if (cb.checked) state.activeFilters.dimw.add(wkey);
              else { state.activeFilters.dimw.delete(wkey); if (!state.activeFilters.dimw.size) delete state.activeFilters.dimw; }
              debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); scrollToGrid(); });
            });
          });
          sidebar.appendChild(el);
        } else if (state.collectionType === 'table' || state.collectionType === 'console' || state.collectionType === 'nightstand') {
          /* Side/end/console tables: width from dim_w_* tags, labeled as "Width" */
          var stBuckets = { under24: 0, '24to48': 0, other: 0 };
          products.forEach(function (p) {
            if (p.tags.indexOf('dim_w_under-24in') !== -1) stBuckets.under24++;
            else if (p.tags.indexOf('dim_w_24-48in') !== -1) stBuckets['24to48']++;
            else if (p.tags.indexOf('dim_w_48-72in') !== -1 || p.tags.indexOf('dim_w_over-72in') !== -1) stBuckets['24to48']++;
            else stBuckets.other++;
          });
          var stOpts = [
            { key: 'under24', label: 'Under 24\u201D', count: stBuckets.under24 },
            { key: '24to48', label: '24\u201D and above', count: stBuckets['24to48'] },
          ].filter(function (o) { return o.count > 0; });
          if (stOpts.length >= 2) sidebar.appendChild(buildCheckboxGroupCustom('dimw', 'Width', stOpts));
        } else if (state.collectionType === 'coffee') {
          /* Coffee tables: length from len_* tags */
          var cwBuckets = { under36: 0, '36to48': 0, '48to60': 0, '60plus': 0, other: 0 };
          products.forEach(function (p) {
            if (p.tags.indexOf('len_under-36in') !== -1) cwBuckets.under36++;
            else if (p.tags.indexOf('len_36-48in') !== -1) cwBuckets['36to48']++;
            else if (p.tags.indexOf('len_48-60in') !== -1) cwBuckets['48to60']++;
            else if (p.tags.indexOf('len_60-72in') !== -1 || p.tags.indexOf('len_72-84in') !== -1 || p.tags.indexOf('len_84-96in') !== -1 || p.tags.indexOf('len_96-108in') !== -1 || p.tags.indexOf('len_108-plus') !== -1) cwBuckets['60plus']++;
            else cwBuckets.other++;
          });
          var cwOpts = [
            { key: 'under36', label: 'Under 36\u201D', count: cwBuckets.under36 },
            { key: '36to48', label: '36\u201D \u2013 48\u201D', count: cwBuckets['36to48'] },
            { key: '48to60', label: '48\u201D \u2013 60\u201D', count: cwBuckets['48to60'] },
            { key: '60plus', label: '60\u201D and above', count: cwBuckets['60plus'] },
          ].filter(function (o) { return o.count > 0; });
          if (cwOpts.length >= 2) sidebar.appendChild(buildCheckboxGroupCustom('dimw', 'Length', cwOpts));
        } else if (!isSofaLike) {
          /* Non-sofa: show standard width prefix tags */
          var counts = {};
          products.forEach(function (p) { p.tags.forEach(function (t) { if (t.indexOf('dim_w_') === 0) counts[t] = (counts[t] || 0) + 1; }); });
          var opts = Object.keys(counts);
          if (opts.length) { opts.sort(function (a,b) { return tagLabel(a).localeCompare(tagLabel(b)); }); sidebar.appendChild(buildCheckboxGroup({ id: 'dimw', label: 'Width', type: 'prefix', prefix: 'dim_w_' }, opts, counts)); }
        }
        return;
      }

      /* Depth — collection-aware buckets */
      if (group.type === 'custom-depth') {
        var isSofaLike2 = ['sofa','seating','sectional'].some(function(k) { return state.collectionType.indexOf(k) !== -1; });
        var under36 = 0, over36 = 0;
        products.forEach(function (p) {
          if (p.tags.indexOf('dim_d_over-36in') !== -1) over36++;
          else if (p.tags.indexOf('dim_d_24-36in') !== -1 || p.tags.indexOf('dim_d_18-24in') !== -1 || p.tags.indexOf('dim_d_under-18in') !== -1) under36++;
        });
        if (isSofaLike2 && (under36 || over36)) {
          var el2 = document.createElement('div');
          el2.className = 'ega-cf-group open';
          el2.dataset.groupId = 'dimd';
          var rows2 = [];
          if (under36) rows2.push('<label class="ega-cf-option"><input type="checkbox" data-depth="under36"><span>Less than 36\u201D</span><span class="ega-cf-option-count">' + under36 + '</span></label>');
          if (over36) rows2.push('<label class="ega-cf-option"><input type="checkbox" data-depth="over36"><span>36\u201D and Above</span><span class="ega-cf-option-count">' + over36 + '</span></label>');
          el2.innerHTML = '<button class="ega-cf-group-toggle" aria-expanded="true"><span class="ega-cf-group-label">Depth</span><span class="ega-cf-group-arrow">&#9660;</span></button><div class="ega-cf-group-body">' + rows2.join('') + '</div>';
          el2.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el2); });
          el2.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
            cb.addEventListener('change', function () {
              if (!state.activeFilters.dimd) state.activeFilters.dimd = new Set();
              var dkey = cb.getAttribute('data-depth');
              if (cb.checked) state.activeFilters.dimd.add(dkey);
              else { state.activeFilters.dimd.delete(dkey); if (!state.activeFilters.dimd.size) delete state.activeFilters.dimd; }
              debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); scrollToGrid(); });
            });
          });
          sidebar.appendChild(el2);
        } else if (!isSofaLike2) {
          var counts2 = {};
          products.forEach(function (p) { p.tags.forEach(function (t) { if (t.indexOf('dim_d_') === 0) counts2[t] = (counts2[t] || 0) + 1; }); });
          var opts2 = Object.keys(counts2);
          if (opts2.length) { opts2.sort(function (a,b) { return tagLabel(a).localeCompare(tagLabel(b)); }); sidebar.appendChild(buildCheckboxGroup({ id: 'dimd', label: 'Depth', type: 'prefix', prefix: 'dim_d_' }, opts2, counts2)); }
        }
        return;
      }

      if (group.type === 'boolean') {
        var count;
        if (group.id === 'instock') {
          count = products.filter(function (p) { return p.inStock; }).length;
        } else {
          count = products.filter(function (p) { return p.tags.indexOf(group.booleanTag) !== -1; }).length;
        }
        if (!count) return;
        sidebar.appendChild(buildBooleanGroup(group, count));
        return;
      }

      var counts = group.id === 'shape' ? shapeFacetCounts(products) : {};
      if (group.id !== 'shape') {
        products.forEach(function (p) {
          p.tags.forEach(function (tag) {
            if (tag.indexOf(group.prefix) === 0) counts[tag] = (counts[tag] || 0) + 1;
          });
          if (group.extraTags) {
            Object.keys(group.extraTags).forEach(function (extraTag) {
              if (p.tags.indexOf(extraTag) !== -1) counts[extraTag] = (counts[extraTag] || 0) + 1;
            });
          }
        });
      }

      var options = Object.keys(counts);
      if (!options.length) return;
      // Custom sort for shape: common shapes first, grouped Other last
      if (group.id === 'shape') {
        /* Skip the group when fewer than two choices remain. A genuine Other
           or one of the five grouped shapes is a real choice, not a catch-all
           for unknown shapes. Old shape__other URLs still restore below. */
        if (options.length < 2) return;
        var shapeOrder = {'shape__round':1,'shape__rectangular':2,'shape__oval':3,'shape__square':4,'shape__grouped-other':100};
        options.sort(function (a, b) { return (shapeOrder[a]||50) - (shapeOrder[b]||50); });
      } else {
        options.sort(function (a, b) { return tagLabel(a).localeCompare(tagLabel(b)); });
      }
      sidebar.appendChild(buildCheckboxGroup(group, options, counts));
    });

    // New Arrivals toggle removed — redundant with "Newest" sort, and most products are evergreen MTO

    // Price range last — less emphasis, already available via sort pills
    sidebar.appendChild(buildPriceGroup());

    /* Groups now live in the All Filters drawer; refresh the toolbar quick-filter
       dropdowns (cloned from these groups) after every rebuild. */
    syncQuickFilters();
  }

  function buildPriceGroup() {
    var group = document.createElement('div');
    group.className = 'ega-cf-group open';
    group.dataset.groupId = 'price';
    group.innerHTML = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">Price</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body ega-cf-price">',
      '  <div class="ega-cf-price-inputs">',
      '    <input type="number" class="ega-cf-price-input" id="ega-cf-price-min" placeholder="Min $" min="0" step="50">',
      '    <span class="ega-cf-price-sep">to</span>',
      '    <input type="number" class="ega-cf-price-input" id="ega-cf-price-max" placeholder="Max $" min="0" step="50">',
      '  </div>',
      '  <button class="ega-cf-price-apply" id="ega-cf-price-apply">Apply</button>',
      '</div>',
    ].join('');
    group.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(group); });
    group.querySelector('#ega-cf-price-apply').addEventListener('click', function () {
      var minEl = group.querySelector('#ega-cf-price-min');
      var maxEl = group.querySelector('#ega-cf-price-max');
      state.priceMinActive = minEl.value !== '' ? parseFloat(minEl.value) : state.priceMin;
      state.priceMaxActive = maxEl.value !== '' ? parseFloat(maxEl.value) : state.priceMax;
      applyFilters(); updateUrl();
    });
    return group;
  }

  /* ── Availability group: In Stock + Customizable MTO ── */
  function buildAvailabilityGroup(instockCount, customCount) {
    var el = document.createElement('div');
    el.className = 'ega-cf-group open';
    el.dataset.groupId = 'availability';
    var instockChecked = state.instockOnly ? ' checked' : '';
    var customChecked = state.customizableOnly ? ' checked' : '';
    var rows = [];
    if (instockCount) {
      rows.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-avail="instock"' + instockChecked + '>',
        '  <span>In Stock - Ready to Ship</span>',
        '  <span class="ega-cf-option-count">' + instockCount + '</span>',
        '</label>'
      );
    }
    if (customCount) {
      rows.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-avail="custom"' + customChecked + '>',
        '  <span>Customizable MTO</span>',
        '  <span class="ega-cf-option-count">' + customCount + '</span>',
        '</label>'
      );
    }
    el.innerHTML = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">Availability</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
      rows.join(''),
      '</div>',
    ].join('');
    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    el.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
      cb.addEventListener('change', function (e) {
        var key = e.target.getAttribute('data-avail');
        if (key === 'instock') state.instockOnly = e.target.checked;
        if (key === 'custom') state.customizableOnly = e.target.checked;
        debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); scrollToGrid(); });
      });
    });
    return el;
  }

  /* ── Style group: Traditional / Modern / Rustic ── */
  function buildStyleGroup(tradCount, modernCount, rusticCount, jupeCount) {
    var el = document.createElement('div');
    el.className = 'ega-cf-group open';
    el.dataset.groupId = 'style';
    var tradChecked = state.activeFilters.style && state.activeFilters.style.has('traditional') ? ' checked' : '';
    var modernChecked = state.activeFilters.style && state.activeFilters.style.has('modern') ? ' checked' : '';
    var rusticChecked = state.activeFilters.style && state.activeFilters.style.has('rustic') ? ' checked' : '';
    var jupeChecked = state.activeFilters.style && state.activeFilters.style.has('jupe') ? ' checked' : '';
    var rows = [];
    if (jupeCount) {
      rows.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-style-group="jupe"' + jupeChecked + '>',
        '  <span>Jupe Tables</span>',
        '  <span class="ega-cf-option-count">' + jupeCount + '</span>',
        '</label>',
        '<div style="border-top:1px solid #e5e1db;margin:6px 0 4px;"></div>'
      );
    }
    if (tradCount) {
      rows.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-style-group="traditional"' + tradChecked + '>',
        '  <span>Traditional</span>',
        '  <span class="ega-cf-option-count">' + tradCount + '</span>',
        '</label>'
      );
    }
    if (modernCount) {
      rows.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-style-group="modern"' + modernChecked + '>',
        '  <span>Modern</span>',
        '  <span class="ega-cf-option-count">' + modernCount + '</span>',
        '</label>'
      );
    }
    if (rusticCount) {
      rows.push(
        '<div style="border-top:1px solid #e5e1db;margin:6px 0 4px;"></div>',
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-style-group="rustic"' + rusticChecked + '>',
        '  <span>Rustic</span>',
        '  <span class="ega-cf-option-count">' + rusticCount + '</span>',
        '</label>'
      );
    }
    el.innerHTML = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">Style</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
      rows.join(''),
      '</div>',
    ].join('');
    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    el.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        if (!state.activeFilters.style) state.activeFilters.style = new Set();
        var sg = cb.getAttribute('data-style-group');
        if (cb.checked) { state.activeFilters.style.add(sg); }
        else { state.activeFilters.style.delete(sg); }
        if (!state.activeFilters.style.size) delete state.activeFilters.style;
        debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); scrollToGrid(); });
      });
    });
    return el;
  }

  /* ── Unified Style Group: 16 individual style checkboxes ── */
  /* Century-based style grouping — maps individual style tags to era buckets */
  var CENTURY_STYLE_MAP = {
    '18th century': ['georgian', 'chippendale', 'queen anne', 'rococo'],
    '19th century': ['regency', 'empire', 'neoclassical', 'french provincial', 'country'],
    '20th century': ['art deco', 'mid-century', 'mid-century modern', 'contemporary', 'hollywood regency', 'industrial', 'organic modern']
    /* NOTE: 'rustic', 'coastal', 'transitional' removed from 20th century — too ambiguous,
       they appear on traditional pieces tagged with multiple centuries. */
  };

  var STYLE_TAGS_ORDERED = [
    { tag: '18th century', label: '18th Century' },
    { tag: '19th century', label: '19th Century' },
    { tag: '20th century', label: '20th Century' },
  ];

  function buildUnifiedStyleGroup(products) {
    var el = document.createElement('div');
    el.className = 'ega-cf-group';
    el.dataset.groupId = 'style-unified';
    var activeStyles = state.activeFilters['style-unified'] || new Set();

    /* Count products per style tag */
    /* Century counts use the actual century TAG on the product (since centuries filter via AND) */
    var styleCounts = {};
    STYLE_TAGS_ORDERED.forEach(function (s) { styleCounts[s.tag] = 0; });
    products.forEach(function (p) {
      STYLE_TAGS_ORDERED.forEach(function (s) {
        if (CENTURY_STYLE_MAP[s.tag]) {
          /* Century: count products that have this century tag */
          if (p.tags.indexOf(s.tag) !== -1) styleCounts[s.tag]++;
        } else {
          if (p.tags.indexOf(s.tag) !== -1) styleCounts[s.tag]++;
        }
      });
    });

    var rows = [];
    STYLE_TAGS_ORDERED.forEach(function (s) {
      if (styleCounts[s.tag] === 0) return;
      var checked = activeStyles.has(s.tag) ? ' checked' : '';
      rows.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-group="style-unified" data-tag="' + esc(s.tag) + '"' + checked + '>',
        '  <span>' + esc(s.label) + '</span>',
        '  <span class="ega-cf-option-count">' + styleCounts[s.tag] + '</span>',
        '</label>'
      );
    });

    if (!rows.length) return document.createDocumentFragment(); /* nothing to show */

    /* Always start Style open so users can see available styles */
    el.className = 'ega-cf-group open';

    el.innerHTML = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">Style</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
      rows.join(''),
      '</div>',
    ].join('');
    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    el.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        if (!state.activeFilters['style-unified']) state.activeFilters['style-unified'] = new Set();
        var tag = cb.getAttribute('data-tag');
        if (cb.checked) state.activeFilters['style-unified'].add(tag);
        else state.activeFilters['style-unified'].delete(tag);
        if (!state.activeFilters['style-unified'].size) delete state.activeFilters['style-unified'];
        applyFilters(); updateUrl(); updateChips(); updateClearBtn();
      });
    });
    return el;
  }

  function buildBooleanGroup(group, count) {
    var el = document.createElement('div');
    el.className = 'ega-cf-group open';
    el.dataset.groupId = group.id;
    var stateKey = group.booleanKey || 'customizableOnly';
    var checked = state[stateKey] ? ' checked' : '';
    el.innerHTML = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">' + esc(group.label) + '</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
      '<label class="ega-cf-option">',
      '  <input type="checkbox" data-group="' + esc(group.id) + '" data-boolean="' + esc(stateKey) + '"' + checked + '>',
      '  <span>' + esc(group.booleanLabel) + '</span>',
      '  <span class="ega-cf-option-count">' + count + '</span>',
      '</label>',
      '</div>',
    ].join('');
    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    el.querySelector('input[type=checkbox]').addEventListener('change', function (e) {
      state[stateKey] = e.target.checked;
      debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); });
    });
    return el;
  }

  function buildCheckboxGroup(group, options, counts) {
    var el = document.createElement('div');
    el.className = 'ega-cf-group open';
    el.dataset.groupId = group.id;
    var activeSet = state.activeFilters[group.id] || new Set();

    var html = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">' + esc(group.label) + '</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
    ];
    options.forEach(function (tag) {
      var checked = activeSet.has(tag) ? ' checked' : '';
      html.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-tag="' + esc(tag) + '" data-group="' + esc(group.id) + '"' + checked + '>',
        '  <span>' + esc(tagLabel(tag)) + '</span>',
        '  <span class="ega-cf-option-count">' + counts[tag] + '</span>',
        '</label>'
      );
    });
    html.push('</div>');
    el.innerHTML = html.join('');

    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    el.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var groupId = cb.dataset.group;
        var tag = cb.dataset.tag;
        if (!state.activeFilters[groupId]) state.activeFilters[groupId] = new Set();
        if (cb.checked) {
          state.activeFilters[groupId].add(tag);
        } else {
          state.activeFilters[groupId].delete(tag);
          if (!state.activeFilters[groupId].size) delete state.activeFilters[groupId];
        }
        debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); scrollToGrid(); });
      });
    });
    return el;
  }

  /* ── Generic custom checkbox group (for dining table filters etc.) ── */
  function buildCheckboxGroupCustom(groupId, label, options) {
    /* Remember what each synthetic key is CALLED. These groups (Seating, Length,
       Size, Upholstery, the derived dimension bins) invent their own keys —
       's10_plus', 'l_84_96', '24to48' — which are not store tags, so tagLabel's
       generic prefix-stripper had nothing sensible to do with them and the applied
       pill came out reading "S10 Plus" (Dino, 2026-08-08). The option label is
       already right here; record it rather than maintaining a second hand-written
       list that can drift out of step with these. First write wins, so a key that
       BUCKET_LABELS or an earlier group already owns keeps its meaning. */
    options.forEach(function (opt) {
      if (opt && opt.key && opt.label && !(opt.key in DYNAMIC_LABELS)) {
        DYNAMIC_LABELS[opt.key] = opt.label;
      }
    });
    var el = document.createElement('div');
    el.className = 'ega-cf-group open';
    el.dataset.groupId = groupId;
    var activeSet = state.activeFilters[groupId] || new Set();
    var html = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">' + esc(label) + '</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
    ];
    options.forEach(function (opt) {
      var checked = activeSet.has(opt.key) ? ' checked' : '';
      html.push(
        '<label class="ega-cf-option">',
        '  <input type="checkbox" data-key="' + esc(opt.key) + '" data-group="' + esc(groupId) + '"' + checked + '>',
        '  <span>' + opt.label + '</span>',
        '  <span class="ega-cf-option-count">' + opt.count + '</span>',
        '</label>'
      );
    });
    html.push('</div>');
    el.innerHTML = html.join('');
    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    el.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var gid = cb.dataset.group;
        var key = cb.dataset.key;
        if (!state.activeFilters[gid]) state.activeFilters[gid] = new Set();
        if (cb.checked) state.activeFilters[gid].add(key);
        else { state.activeFilters[gid].delete(key); if (!state.activeFilters[gid].size) delete state.activeFilters[gid]; }
        debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); scrollToGrid(); });
      });
    });
    return el;
  }

  function buildNewArrivalsToggle(count) {
    var el = document.createElement('div');
    el.className = 'ega-cf-group open';
    el.dataset.groupId = 'new';
    var checked = (state.activeFilters['new'] && state.activeFilters['new'].size) ? ' checked' : '';
    el.innerHTML = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">New Arrivals</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
      '<label class="ega-cf-option">',
      '  <input type="checkbox" data-group="new" data-tag="new"' + checked + '>',
      '  <span>New Arrivals Only</span>',
      '  <span class="ega-cf-option-count">' + count + '</span>',
      '</label>',
      '</div>',
    ].join('');
    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    el.querySelector('input[type=checkbox]').addEventListener('change', function (e) {
      if (e.target.checked) { state.activeFilters['new'] = new Set(['new']); }
      else { delete state.activeFilters['new']; }
      debounced(function () { applyFilters(); updateUrl(); updateChips(); updateClearBtn(); });
    });
    return el;
  }

  // ── Toolbar shell: quick-filter dropdowns + All Filters drawer ─────────────
  /* The engine renders every facet group into #ega-cf-sidebar-body, which now lives
     inside the drawer. The four toolbar dropdowns are thin clones of their group
     bodies: every interaction in a clone forwards to the original input, so the
     engine's own change handlers stay the single source of filter logic. */
  /* Toolbar quick-filter chips. Per-category overrides (Dino, 2026-08-06):
     each category's "money filters" go in the toolbar; everything stays in the
     drawer. Keys are engine group ids (syncQuickFilters clones by
     data-group-id, so any drawer group works here). The liquid server-renders
     the same sets keyed by collection handle — keep
     sections/ega-collection-filter-panel.liquid in sync when editing these.
     QUICK_FILTERS is resolved per-collection in init(). */
  /* Keep the quick row short. Dining collections show Shape in place of Style;
     other table collections show both Shape and their existing Style chip.
     All facet groups remain available in the drawer. */
  /* In Stock is a one-click TOGGLE, not a dropdown (Dino, 2026-09-02: "one less click and
     helps someone get to what they want quickest"). It flips state.instockOnly directly;
     Customizable stays in the drawer's Availability group. The toggle also shows on mobile,
     where the other chips collapse into Filters. Server markup: ega-collection-filter-panel.liquid. */
  var DEFAULT_QUICK_FILTERS = [
    { key: 'instock', label: 'In Stock', toggle: true },
    { key: 'style-unified', label: 'Style' },
  ];
  var DINING_TABLE_QUICK_FILTERS = [
    { key: 'instock', label: 'In Stock', toggle: true },
    { key: 'shape', label: 'Shape' },
  ];
  var TABLE_QUICK_FILTERS = [
    { key: 'instock', label: 'In Stock', toggle: true },
    { key: 'shape', label: 'Shape' },
    { key: 'style-unified', label: 'Style' },
  ];
  var QUICK_FILTERS_BY_HANDLE = {
    'dining-tables': DINING_TABLE_QUICK_FILTERS,
    'round-dining-tables': DINING_TABLE_QUICK_FILTERS,
    'expandable-dining-tables': DINING_TABLE_QUICK_FILTERS,
    'jupe-dining-tables': DINING_TABLE_QUICK_FILTERS,
    'custom-tables': DINING_TABLE_QUICK_FILTERS,
    'accent-tables': TABLE_QUICK_FILTERS,
    'center-tables-1': TABLE_QUICK_FILTERS,
    'coffee-tables': TABLE_QUICK_FILTERS,
    'consoles': TABLE_QUICK_FILTERS,
    'custom-dining-tables': TABLE_QUICK_FILTERS,
    'game-tables': TABLE_QUICK_FILTERS,
    'side-tables': TABLE_QUICK_FILTERS,
    'tables': TABLE_QUICK_FILTERS,
  };
  var QUICK_FILTERS = DEFAULT_QUICK_FILTERS;
  var quickItems = {}; /* key -> { item, chip, badge, panel } */
  var openQuickKey = null;

  /* Resolve this collection's chip set from the wrap handle. Called from BOTH
     initToolbar() and init() because the DOMContentLoaded bootstrap runs
     initToolbar() FIRST — resolving only in init() left the default set in
     place, so the server-rendered per-category chips (shape/seating/extension)
     were never bound or populated and two orphan chips were created. */
  function resolveQuickFilters() {
    var wrapEl = document.getElementById('ega-cf-wrap');
    var handle = wrapEl ? (wrapEl.dataset.handle || '') : '';
    QUICK_FILTERS = QUICK_FILTERS_BY_HANDLE[handle] || DEFAULT_QUICK_FILTERS;
    return QUICK_FILTERS;
  }

  function initToolbar() {
    resolveQuickFilters();
    var quickWrap = document.getElementById('ega-cf-quick');
    if (quickWrap) {
      QUICK_FILTERS.forEach(function (qf) {
        /* Prefer the SERVER-RENDERED item (liquid renders one per quick filter,
           keyed by data-qf, so chips paint before this script even loads —
           Dino 2026-08-06: JS-built chips "lagged in, looked broken"). These
           stay visible pre-dataset; syncQuickFilters hides any whose facet
           group doesn't exist on this collection. Falls back to JS creation
           if the template predates the server markup. */
        var item = quickWrap.querySelector('.ega-cf-quick-item[data-qf="' + qf.key + '"]');
        var chip, panel;
        if (qf.toggle) {
          /* One-click toggle: no panel, no caret. Prefer the server-rendered button. */
          if (item) {
            chip = item.querySelector('.ega-cf-quick-chip');
          } else {
            item = document.createElement('div');
            item.className = 'ega-cf-quick-item ega-cf-quick-item--toggle';
            item.setAttribute('data-qf', qf.key);
            item.style.display = 'none';
            chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'ega-cf-quick-chip ega-cf-quick-toggle';
            chip.setAttribute('aria-pressed', 'false');
            chip.innerHTML = '<span>' + esc(qf.label) + '</span>';
            item.appendChild(chip);
            quickWrap.appendChild(item);
          }
          chip.addEventListener('click', function () { setInstock(!state.instockOnly); });
          quickItems[qf.key] = { item: item, chip: chip, badge: null, panel: null, toggle: true };
          return;
        }
        if (item) {
          chip = item.querySelector('.ega-cf-quick-chip');
          panel = item.querySelector('.ega-cf-quick-panel');
        } else {
          item = document.createElement('div');
          item.className = 'ega-cf-quick-item';
          item.setAttribute('data-qf', qf.key);
          item.style.display = 'none'; /* shown once the engine renders the group */
          chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'ega-cf-quick-chip';
          chip.setAttribute('aria-expanded', 'false');
          chip.setAttribute('aria-haspopup', 'true');
            /* No count badge (Dino, 2026-08-08): the brass underline already says a
             choice was made, and the removable pills below say which. */
          chip.innerHTML = '<span>' + esc(qf.label) + '</span>' +
            '<span class="ega-cf-quick-caret" aria-hidden="true">&#9660;</span>';
          panel = document.createElement('div');
          panel.className = 'ega-cf-quick-panel';
          item.appendChild(chip);
          item.appendChild(panel);
          quickWrap.appendChild(item);
        }
        chip.addEventListener('click', function () { toggleQuick(qf.key); });
        quickItems[qf.key] = { item: item, chip: chip, badge: chip.querySelector('.ega-cf-quick-badge'), panel: panel };
      });
      /* Drift guard: if the liquid ever server-renders a chip this collection's
         QUICK_FILTERS doesn't include, remove it rather than leaving a dead
         button with an empty panel. */
      var activeKeys = QUICK_FILTERS.map(function (q) { return q.key; });
      Array.prototype.slice.call(quickWrap.querySelectorAll('.ega-cf-quick-item[data-qf]')).forEach(function (el) {
        if (activeKeys.indexOf(el.getAttribute('data-qf')) === -1) el.parentNode.removeChild(el);
      });

      /* Click outside any open dropdown closes it */
      document.addEventListener('click', function (e) {
        if (!openQuickKey) return;
        var it = quickItems[openQuickKey];
        if (it && !it.item.contains(e.target)) closeQuick();
      });
    }

    /* Drawer wiring */
    var overlay = document.getElementById('ega-cf-drawer-overlay');
    var drawer = document.getElementById('ega-cf-drawer');
    var closeBtn = document.getElementById('ega-cf-drawer-close-btn');
    var resetBtn = document.getElementById('ega-cf-drawer-reset');
    var viewBtn = document.getElementById('ega-cf-drawer-view');
    ['ega-cf-mobile-toggle', 'ega-cf-all-filters'].forEach(function (id) {
      var btn = document.getElementById(id);
      if (btn) btn.addEventListener('click', function () { openDrawer(btn); });
    });
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);
    if (viewBtn) viewBtn.addEventListener('click', closeDrawer);
    if (resetBtn) resetBtn.addEventListener('click', function () {
      /* Reset rides the engine's existing clear-all path */
      var clearBtn = document.getElementById('ega-cf-clear-all');
      if (clearBtn) clearBtn.click();
    });
    if (drawer) drawer.addEventListener('keydown', trapDrawerFocus);
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (drawerIsOpen) { closeDrawer(); return; }
      if (openQuickKey) {
        var it = quickItems[openQuickKey];
        closeQuick();
        if (it) it.chip.focus();
      }
    });
  }

  function toggleQuick(key) {
    if (openQuickKey === key) { closeQuick(); return; }
    closeQuick();
    var it = quickItems[key];
    if (!it) return;
    it.item.classList.add('open');
    it.chip.setAttribute('aria-expanded', 'true');
    openQuickKey = key;
  }

  function closeQuick() {
    if (!openQuickKey) return;
    var it = quickItems[openQuickKey];
    if (it) {
      it.item.classList.remove('open');
      it.chip.setAttribute('aria-expanded', 'false');
    }
    openQuickKey = null;
  }

  /* In Stock toggle (toolbar). Same engine path as the drawer checkbox: state, URL,
     chips, clear button, scroll. Before the dataset has loaded the URL carries the
     choice, because ensureDataset() re-reads state from the URL (applyUrlPreFilters). */
  function setInstock(on) {
    state.instockOnly = !!on;
    var cb = document.querySelector('#ega-cf-sidebar-body input[data-avail="instock"]');
    if (cb) cb.checked = state.instockOnly;
    updateQuickState();
    updateUrl();
    if (!state.loaded) { ensureDataset(); return; }
    /* No scrollToGrid() here (Dino, 2026-09-02: "the page moves down and I do NOT want
       that"). The toggle sits directly above the grid; the drawer scrolls because it is
       an overlay that closes, this is not. */
    applyFilters(); updateChips(); updateClearBtn();
  }

  /* Locate the original engine-bound input matching a cloned dropdown input */
  function findOriginalInput(cloneInput) {
    var body = document.getElementById('ega-cf-sidebar-body');
    if (!body) return null;
    var sel = 'input';
    ['data-group', 'data-tag', 'data-key', 'data-avail'].forEach(function (attr) {
      var v = cloneInput.getAttribute(attr);
      if (v !== null) sel += '[' + attr + '="' + v.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]';
    });
    if (sel === 'input') return null;
    return body.querySelector(sel);
  }

  /* Rebuild the dropdown clones from the freshly rendered drawer groups */
  function syncQuickFilters() {
    var body = document.getElementById('ega-cf-sidebar-body');
    if (!body || !Object.keys(quickItems).length) return;
    closeQuick();
    QUICK_FILTERS.forEach(function (qf) {
      var it = quickItems[qf.key];
      if (!it) return;
      if (it.toggle) {
        /* Show only where the drawer offers an In Stock option, i.e. not on the In Stock
           collection itself and not on a collection with nothing in stock. */
        var hasInstock = !state.implicitInstock && !!body.querySelector('input[data-avail="instock"]');
        it.item.style.display = hasInstock ? '' : 'none';
        return;
      }
      var group = body.querySelector('[data-group-id="' + qf.key + '"]');
      if (!group) { it.item.style.display = 'none'; it.panel.innerHTML = ''; return; }
      var groupBody = group.querySelector('.ega-cf-group-body');
      if (!groupBody) { it.item.style.display = 'none'; it.panel.innerHTML = ''; return; }
      it.item.style.display = '';
      var clone = groupBody.cloneNode(true);
      /* Cloned nodes must not duplicate ids (price inputs carry them) */
      clone.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
      it.panel.innerHTML = '';
      it.panel.appendChild(clone);
      if (qf.key === 'price') {
        bindPriceClone(clone);
      } else {
        clone.querySelectorAll('input[type="checkbox"]').forEach(function (ci) {
          ci.addEventListener('change', function () {
            var orig = findOriginalInput(ci);
            /* click() toggles the original and fires its engine handler */
            if (orig && orig.checked !== ci.checked) orig.click();
          });
        });
      }
    });
    updateQuickState();
  }

  function bindPriceClone(clone) {
    var inputs = clone.querySelectorAll('.ega-cf-price-input');
    var apply = clone.querySelector('.ega-cf-price-apply');
    var origMin = document.getElementById('ega-cf-price-min');
    var origMax = document.getElementById('ega-cf-price-max');
    if (origMin && inputs[0]) inputs[0].value = origMin.value;
    if (origMax && inputs[1]) inputs[1].value = origMax.value;
    if (!apply) return;
    apply.addEventListener('click', function () {
      var oMin = document.getElementById('ega-cf-price-min');
      var oMax = document.getElementById('ega-cf-price-max');
      var oApply = document.getElementById('ega-cf-price-apply');
      if (oMin && inputs[0]) oMin.value = inputs[0].value;
      if (oMax && inputs[1]) oMax.value = inputs[1].value;
      if (oApply) oApply.click();
    });
  }

  /* Refresh badges and re-sync clone checkbox states after any state change */
  function updateQuickState() {
    if (!Object.keys(quickItems).length) return;
    /* Generic per-group badge counts so per-category chip sets (shape,
       dt-seating, dt-extension, ...) work without a hardcoded map; availability
       and price are the two non-activeFilters state shapes. */
    function quickCount(key) {
      if (key === 'availability') return (state.instockOnly ? 1 : 0) + (state.customizableOnly ? 1 : 0);
      if (key === 'price') return (state.priceMinActive > state.priceMin || (isFinite(state.priceMaxActive) && state.priceMaxActive < state.priceMax)) ? 1 : 0;
      return state.activeFilters[key] ? state.activeFilters[key].size : 0;
    }
    QUICK_FILTERS.forEach(function (qf) {
      var it = quickItems[qf.key];
      if (!it) return;
      if (it.toggle) {
        it.chip.classList.toggle('has-active', !!state.instockOnly);
        it.chip.setAttribute('aria-pressed', state.instockOnly ? 'true' : 'false');
        return;
      }
      var n = quickCount(qf.key);
      if (it.badge) { it.badge.textContent = String(n); it.badge.hidden = true; }
      it.chip.classList.toggle('has-active', n > 0);
      it.panel.querySelectorAll('input[type="checkbox"]').forEach(function (ci) {
        var orig = findOriginalInput(ci);
        if (orig) ci.checked = orig.checked;
      });
    });
  }

  /* Toggle the active-chips row. The row is display-toggled in CSS — no height
     animation, so it reserves no space when empty and never animates 0→auto
     (a CLS source); the grid only moves on user-initiated filter changes. */
  function updateActiveRow() {
    var row = document.getElementById('ega-cf-active');
    var chips = document.getElementById('ega-cf-chips');
    if (!row || !chips) return;
    row.classList.toggle('has-filters', chips.children.length > 0);
  }

  /* ── Drawer open/close, focus trap, scroll lock ── */
  var drawerIsOpen = false;
  var drawerOpener = null;

  function openDrawer(opener) {
    var overlay = document.getElementById('ega-cf-drawer-overlay');
    var drawer = document.getElementById('ega-cf-drawer');
    if (!overlay || !drawer) return;
    closeQuick();
    drawerOpener = opener || null;
    overlay.classList.add('open');
    drawer.classList.add('open');
    document.body.style.overflow = 'hidden';
    drawerIsOpen = true;
    var closeBtn = document.getElementById('ega-cf-drawer-close-btn');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    var overlay = document.getElementById('ega-cf-drawer-overlay');
    var drawer = document.getElementById('ega-cf-drawer');
    if (overlay) overlay.classList.remove('open');
    if (drawer) drawer.classList.remove('open');
    document.body.style.overflow = '';
    drawerIsOpen = false;
    if (drawerOpener) { drawerOpener.focus(); drawerOpener = null; }
  }

  function trapDrawerFocus(e) {
    if (e.key !== 'Tab' || !drawerIsOpen) return;
    var drawer = document.getElementById('ega-cf-drawer');
    if (!drawer) return;
    var focusables = Array.prototype.filter.call(
      drawer.querySelectorAll('button, input, select, a[href], [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null && !el.disabled; }
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  // ── URL pre-filters ────────────────────────────────────────────────────────
  function applyUrlPreFilters() {
    var params = new URLSearchParams(window.location.search);

    /* Pre-select style filter */
    var styleParam = params.get('style');
    if (styleParam) {
      var styles = styleParam.toLowerCase().split(',');
      var known = STYLE_TAGS_ORDERED.map(function (s) { return s.tag; });
      styles.forEach(function (s) {
        s = s.trim().replace(/-/g, ' ');
        if (known.indexOf(s) !== -1) {
          if (!state.activeFilters['style-unified']) state.activeFilters['style-unified'] = new Set();
          state.activeFilters['style-unified'].add(s);
        }
      });
    }

    /* Pre-select product type */
    var typeParam = params.get('type');
    if (typeParam) {
      var types = typeParam.toLowerCase().split(',');
      types.forEach(function (t) {
        t = t.trim().replace(/-/g, ' ');
        if (!state.activeFilters['product-type']) state.activeFilters['product-type'] = new Set();
        state.activeFilters['product-type'].add(t);
      });
    }
  }

  /* ── Auto-detect filters from collection handle ─────────────────────────── */
  var STYLE_HANDLE_MAP = {
    'georgian': 'georgian', 'chippendale': 'chippendale', 'queen-anne': 'queen anne',
    'regency': 'regency', 'empire': 'empire', 'neoclassical': 'neoclassical',
    'french-provincial': 'french provincial', 'art-deco-furniture': 'art deco',
    'mid-century-modern-furniture': 'mid-century', 'contemporary-furniture': 'contemporary',
    'transitional-furniture': 'transitional', 'hollywood-regency': 'hollywood regency',
    'country': 'country', 'rustic': 'rustic', 'coastal': 'coastal', 'rococo': 'rococo',
    'industrial': 'industrial', 'organic-modern-furniture': 'organic modern',
    'traditional-furniture': 'georgian', 'modern-furniture': 'contemporary',
  };
  var TYPE_HANDLE_MAP = {
    'dining-tables': 'dining tables', 'dining-chairs': 'dining chairs',
    'sofas': 'sofas', 'accent-chairs': 'accent chairs', 'armchairs': 'accent chairs',
    'coffee-tables': 'coffee & cocktail tables', 'coffee-cocktail-tables': 'coffee & cocktail tables',
    'console-tables': 'console tables', 'consoles': 'console tables',
    'side-tables': 'side & end tables', 'side-end-tables': 'side & end tables',
    'center-tables': 'center tables', 'center-tables-1': 'center tables',
    'accent-tables': 'accent tables', 'beds': 'beds', 'nightstands': 'nightstands',
    'dressers': 'dressers', 'chest-of-drawers': 'chest of drawers',
    'desks': 'desks', 'desks-writing-tables': 'desks',
    'desk-chairs': 'desk chairs',
    'secretary-desks': 'secretary desks', 'secretary-desks-antique-made-to-order': 'secretary desks',
    'bookcases': 'bookcases', 'bookshelves': 'bookshelves & etageres',
    'bookshelves-etageres': 'bookshelves & etageres',
    'cabinets': 'cabinets', 'bar-cabinets': 'bar cabinets', 'bar-carts': 'bar carts',
    'bar-stools': 'bar stools', 'sideboards-buffets': 'sideboards & buffets',
    'sideboards': 'sideboards & buffets',
    'benches': 'benches', 'ottomans': 'ottomans and poufs',
    'ottomans-and-poufs': 'ottomans and poufs',
    'chandeliers': 'chandeliers', 'lanterns': 'lanterns',
    'table-lamps': 'table lamps', 'floor-lamps': 'floor lamps',
    'sconces': 'sconces', 'mirrors': 'mirrors',
    'pedestals': 'pedestals', 'pedestal': 'pedestals',
    'accessories': 'accessories', 'decorative-objects': 'decorative objects',
    'objects': 'accessories',
    'game-tables': 'game tables', 'kitchen-islands': 'kitchen islands',
    'stools': 'stools',
    'upholstered-sofas': 'sofas', 'custom-seating': 'accent chairs',
    'custom-mirrors': 'mirrors', 'custom-tables': 'dining tables',
    'custom-benches-ottomans': 'benches',
    'chippendale-dining-chairs': 'dining chairs',
    'expandable-dining-tables': 'dining tables',
    'jupe-dining-tables': 'dining tables',
    /* lighting handled separately in MULTI_TYPE_MAP below */
  };

  /* Room collections → scope by room tag */
  var ROOM_HANDLE_MAP = {
    'living-room': 'room:living-room',
    'dining-room': 'room:dining-room',
    'bedroom': 'room:bedroom',
    'entry-hall': 'room:entryway',
    'office': 'room:office',
  };

  /* Tiered browse structure for leaf type collections — parent + siblings, not 42 random links */
  var TYPE_BROWSE_FAMILIES = {
    living: {
      parentHandle: 'living-room',
      parentLabel: 'All Living Room',
      items: [
        { handle: 'sofas', label: 'Sofas' },
        { handle: 'accent-chairs', label: 'Accent Chairs' },
        { handle: 'coffee-tables', label: 'Coffee Tables' },
        { handle: 'side-tables', label: 'Side & End Tables' },
        { handle: 'ottomans-and-poufs', label: 'Ottomans & Poufs' },
        { handle: 'game-tables', label: 'Game Tables' }
      ]
    },
    dining: {
      parentHandle: 'dining-room',
      parentLabel: 'All Dining Room',
      items: [
        { handle: 'dining-tables', label: 'Dining Tables' },
        { handle: 'dining-chairs', label: 'Dining Chairs' },
        { handle: 'sideboards', label: 'Sideboards & Buffets' },
        { handle: 'bar-cabinets', label: 'Bar Cabinets' },
        { handle: 'bar-carts', label: 'Bar Carts' },
        { handle: 'bar-stools', label: 'Bar Stools' }
      ]
    },
    bedroom: {
      parentHandle: 'bedroom',
      parentLabel: 'All Bedroom',
      items: [
        { handle: 'beds', label: 'Beds' },
        { handle: 'nightstands', label: 'Nightstands' },
        { handle: 'dressers', label: 'Dressers' },
        { handle: 'chest-of-drawers', label: 'Chest of Drawers' },
        { handle: 'benches', label: 'Benches' }
      ]
    },
    office: {
      parentHandle: 'office',
      parentLabel: 'All Office',
      items: [
        { handle: 'desks-writing-tables', label: 'Desks' },
        { handle: 'desk-chairs', label: 'Desk Chairs' },
        { handle: 'bookcases', label: 'Bookcases' },
        { handle: 'bookshelves', label: 'Bookshelves & Étagères' },
        { handle: 'secretary-desks-antique-made-to-order', label: 'Secretary Desks' },
        { handle: 'cabinets', label: 'Cabinets' }
      ]
    },
    lighting: {
      parentHandle: 'lighting',
      parentLabel: 'All Lighting',
      items: [
        { handle: 'chandeliers', label: 'Chandeliers' },
        { handle: 'lanterns', label: 'Lanterns' },
        { handle: 'table-lamps', label: 'Lamps' },
        { handle: 'sconces', label: 'Sconces' }
      ]
    },
    entryway: {
      parentHandle: 'entry-hall',
      parentLabel: 'All Entryway',
      items: [
        { handle: 'consoles', label: 'Console Tables' },
        { handle: 'center-tables-1', label: 'Center Tables' },
        { handle: 'mirrors', label: 'Mirrors' },
        { handle: 'pedestal', label: 'Pedestals' }
      ]
    },
    objects: {
      parentHandle: 'objects',
      parentLabel: 'All Objects & Decor',
      items: [
        { handle: 'mirrors', label: 'Mirrors' },
        { handle: 'accessories', label: 'Accessories' },
        { handle: 'pedestal', label: 'Pedestals' }
      ]
    }
  };

  var TYPE_BROWSE_MAP = {
    /* Living Room */
    'sofas': 'living', 'upholstered-sofas': 'living',
    'accent-chairs': 'living', 'armchairs': 'living', 'custom-seating': 'living',
    'coffee-tables': 'living', 'coffee-cocktail-tables': 'living',
    'side-tables': 'living', 'side-end-tables': 'living', 'accent-tables': 'living',
    'console-tables': 'entryway', 'consoles': 'entryway',
    'ottomans-and-poufs': 'living', 'ottomans': 'living',

    /* Dining Room */
    'dining-tables': 'dining', 'expandable-dining-tables': 'dining',
    'jupe-dining-tables': 'dining', 'custom-tables': 'dining',
    'dining-chairs': 'dining', 'chippendale-dining-chairs': 'dining',
    'sideboards-buffets': 'dining', 'sideboards': 'dining',
    'bar-cabinets': 'dining', 'bar-carts': 'dining',
    'bar-stools': 'dining',
    'center-tables': 'entryway', 'center-tables-1': 'entryway',

    /* Bedroom */
    'beds': 'bedroom', 'benches': 'bedroom', 'custom-benches-ottomans': 'bedroom',
    'nightstands': 'bedroom',
    'dressers': 'bedroom',
    'chest-of-drawers': 'bedroom',

    /* Office / Study */
    'desks': 'office', 'desks-writing-tables': 'office', 'antique-desks': 'office',
    'desk-chairs': 'office',
    'bookcases': 'office', 'bookshelves': 'office', 'bookshelves-etageres': 'office',
    'secretary-desks': 'office', 'secretary-desks-antique-made-to-order': 'office',
    'cabinets': 'office',

    /* Lighting */
    'chandeliers': 'lighting',
    'lanterns': 'lighting',
    'table-lamps': 'lighting',
    'floor-lamps': 'lighting',
    'sconces': 'lighting',

    /* Objects & Decor */
    'accessories': 'objects',
    'decorative-objects': 'objects',
    'mirrors': 'entryway', 'custom-mirrors': 'entryway',
    'pedestals': 'entryway', 'pedestal': 'entryway',
    'game-tables': 'living',
    'kitchen-islands': 'objects',
    'stools': 'objects'
  };

  /* Century collections → pre-select era styles */
  var CENTURY_HANDLE_MAP = {
    '18th-century': { tag: '18th century', styles: ['georgian', 'chippendale', 'queen anne', 'rococo'] },
    '19th-century': { tag: '19th century', styles: ['regency', 'empire', 'neoclassical', 'french provincial'] },
    '20th-century': { tag: '20th century', styles: ['art deco', 'mid-century', 'mid-century modern', 'contemporary', 'hollywood regency', 'industrial', 'organic modern'] },
  };

  function buildCollectionBrowseUrl(handle) {
    try {
      var url = new URL(window.location.href);
      url.pathname = '/collections/' + handle;
      ['page', 'pt', 'style', 'instock', 'customizable', 'dimw', 'dimd', 'price_min', 'price_max', 'sort'].forEach(function (key) {
        url.searchParams.delete(key);
      });
      return url.toString();
    } catch (e) {
      return '/collections/' + handle;
    }
  }

  function buildTypeBrowseGroup() {
    var familyKey = TYPE_BROWSE_MAP[state.collectionHandle];
    if (!familyKey) return null;
    var family = TYPE_BROWSE_FAMILIES[familyKey];
    if (!family || !family.items || !family.items.length) return null;

    var el = document.createElement('div');
    el.className = 'ega-cf-group open';
    el.dataset.groupId = 'browse-types';

    /* Resolve alias handles to canonical for "Current" highlighting */
    var BROWSE_HANDLE_ALIASES = {
      'upholstered-sofas': 'sofas', 'armchairs': 'accent-chairs', 'custom-seating': 'accent-chairs',
      'coffee-cocktail-tables': 'coffee-tables', 'side-end-tables': 'side-tables', 'accent-tables': 'side-tables',
      'consoles': 'console-tables', 'ottomans': 'ottomans-and-poufs', 'custom-benches-ottomans': 'benches',
      'expandable-dining-tables': 'dining-tables', 'jupe-dining-tables': 'dining-tables', 'custom-tables': 'dining-tables',
      'chippendale-dining-chairs': 'dining-chairs', 'sideboards': 'sideboards-buffets',
      'center-tables-1': 'center-tables-1',
      'desks': 'desks-writing-tables', 'desks-writing-tables': 'desks-writing-tables', 'antique-desks': 'desks-writing-tables',
      'bookshelves': 'bookshelves-etageres',
      'secretary-desks-antique-made-to-order': 'secretary-desks',
      'custom-mirrors': 'mirrors', 'pedestal': 'pedestals'
    };
    var canonicalHandle = BROWSE_HANDLE_ALIASES[state.collectionHandle] || state.collectionHandle;

    var itemsHtml = family.items.map(function (item) {
      var isCurrent = item.handle === canonicalHandle;
      if (isCurrent) {
        return '<div class="ega-cf-browse-item ega-cf-browse-current">' +
          '<span>' + esc(item.label) + '</span>' +
        '</div>';
      }
      return '<a href="' + esc(buildCollectionBrowseUrl(item.handle)) + '" class="ega-cf-browse-item">' +
        '<span>' + esc(item.label) + '</span>' +
        '<span class="ega-cf-browse-chevron">›</span>' +
      '</a>';
    }).join('');

    el.innerHTML = [
      '<button class="ega-cf-group-toggle" aria-expanded="true">',
      '  <span class="ega-cf-group-label">Browse</span>',
      '  <span class="ega-cf-group-arrow">&#9660;</span>',
      '</button>',
      '<div class="ega-cf-group-body">',
      '  <a href="' + esc(buildCollectionBrowseUrl(family.parentHandle)) + '" class="ega-cf-browse-parent">',
      '    <span class="ega-cf-browse-back-arrow">←</span>',
      '    <span>' + esc(family.parentLabel) + '</span>',
      '  </a>',
      itemsHtml,
      '</div>'
    ].join('');

    el.querySelector('.ega-cf-group-toggle').addEventListener('click', function () { toggleGroup(el); });
    return el;
  }

  function autoSelectFromCollection() {
    var handle = state.collectionHandle;
    if (!handle || handle === 'all') return; /* "all" collection — no auto-select */

    /* In Stock collection (Dino, 2026-09-02). The server rule is TAG 'Stock Items' AND inventory > 0,
       but the static dataset scopes by _pos, which follows the TAG alone, so without this the JS
       re-render would bring back the sold-out pieces the server just excluded. Same predicate the
       In Stock filter and the card use. Implicit scope, no chip: it is the page, not a filter. */
    if (handle === 'stock-items') { state.implicitInstock = true; return; }

    return; /* 2026-09-24 Dino: no page-load scoping. The collection rule in Shopify decides what shows. */

    /* Check if it's a style collection */
    if (STYLE_HANDLE_MAP[handle]) {
      if (!state.activeFilters['style-unified']) state.activeFilters['style-unified'] = new Set();
      state.activeFilters['style-unified'].add(STYLE_HANDLE_MAP[handle]);
      return;
    }

    /* Check if it's a room collection — scope products by room tag */
    if (ROOM_HANDLE_MAP[handle]) {
      state.roomTag = ROOM_HANDLE_MAP[handle];
      return;
    }

    /* Check if it's a century collection — scope by era tag + pre-select styles */
    if (CENTURY_HANDLE_MAP[handle]) {
      var century = CENTURY_HANDLE_MAP[handle];
      state.centuryTag = century.tag;
      if (!state.activeFilters['style-unified']) state.activeFilters['style-unified'] = new Set();
      century.styles.forEach(function (s) {
        state.activeFilters['style-unified'].add(s);
      });
      return;
    }

    /* Multi-type collections (e.g. lighting = chandeliers + lanterns + lamps + sconces) */
    var MULTI_TYPE_MAP = {
      'lighting': ['chandeliers', 'lanterns', 'table lamps', 'floor lamps', 'sconces', 'candelabra'],
      'objects': ['accessories', 'decorative objects', 'chinese porcelain', 'candelabra', 'peat buckets'],
      'custom-antique-reproductions': [], /* show all with no pre-select */
      'featured-items': [],
      'new-arrivals': [],
    };
    if (MULTI_TYPE_MAP[handle]) {
      var types = MULTI_TYPE_MAP[handle];
      if (types.length > 0) {
        /* Store as implicit scope — used for product filtering but NOT shown as chips */
        state.implicitTypes = new Set(types);
      }
      return;
    }

    /* Check if it's a product-type collection. The collection's own scope is
       IMPLICIT (like the parent collections above): it filters and drives counts
       via the implicitTypes fallback in applyFilters, but renders no removable
       chip and writes no URL param — a customer landing on Sideboards shouldn't
       see "Sideboards ×" occupying a chips row above the fold, and "removing"
       the page's own scope was never a meaningful action. An explicit
       f_product-type URL param (shared/old links) still restores as a normal
       chip-bearing filter and overrides this scope. */
    if (TYPE_HANDLE_MAP[handle]) {
      state.implicitTypes = new Set([TYPE_HANDLE_MAP[handle]]);
      return;
    }
  }


  /* ── F8: editorial interrupts — Liquid renders the tiles into a <template>
     (one per metaobject in the collection's editorial_interrupts list); we
     splice tile k after product (k+1)*interval - 1. Tiles are extra grid
     rows and never consume a product slot, so counts, filters, sort, and
     infinite scroll stay correct. Deterministic on every re-render. ── */
  function interruptConfig() {
    var tpl = document.getElementById('egaCfInterrupts');
    if (!tpl) return null;
    var tiles = tpl.content ? tpl.content.querySelectorAll('.ega-cf-interrupt') : [];
    if (!tiles.length) return null;
    return { tiles: tiles, interval: parseInt(tpl.getAttribute('data-interval'), 10) || 8 };
  }
  function maybeInterrupt(grid, productIndex, cfg) {
    if (!cfg) return;
    var n = productIndex + 1;
    if (n % cfg.interval !== 0) return;
    var k = n / cfg.interval - 1;
    if (k >= cfg.tiles.length) return;
    grid.appendChild(cfg.tiles[k].cloneNode(true));
  }

  // ── Infinite scroll / pagination ──────────────────────────────────────────
  function initInfiniteScroll() {
    var grid = document.getElementById('ega-cf-grid');
    if (!grid) return;

    var sentinel = document.createElement('div');
    sentinel.id = 'ega-cf-sentinel';
    sentinel.style.cssText = 'height:1px;width:100%;';
    grid.parentNode.insertBefore(sentinel, grid.nextSibling);

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting && state.renderedCount < state.filtered.length) {
          renderNextPage();
        }
      }, { rootMargin: '600px' });
      observer.observe(sentinel);
    } else {
      /* Fallback: scroll listener */
      window.addEventListener('scroll', function () {
        if (state.renderedCount >= state.filtered.length) return;
        var rect = sentinel.getBoundingClientRect();
        if (rect.top < window.innerHeight + 600) renderNextPage();
      });
    }
  }

  function renderNextPage() {
    var grid = document.getElementById('ega-cf-grid');
    if (!grid) return;
    var start = state.renderedCount;
    var end = Math.min(start + PAGE_SIZE, state.filtered.length);
    var _icfg = interruptConfig();
    for (var i = start; i < end; i++) {
      grid.appendChild(buildCard(state.filtered[i]));
      maybeInterrupt(grid, i, _icfg);
    }
    state.renderedCount = end;
    updateCount();
  }

  // ── Filter logic ──────────────────────────────────────────────────────────

  /* ── Dimension tags derived from real measurements (2026-08-07) ──────────────
     The Width / Depth / Height / Length / Seating facets bucket by TAG, but ~151
     products were never tagged, so they vanished from those facets while still
     sitting in the grid — a collection of 240 showing 225 across its Seating
     options. Their measurements were there all along: every product carries
     custom.width/depth/height, and the theme already loads them as
     state.productDims for the PDP dimension overlay.

     So we synthesise the missing tags from the numbers. A real tag ALWAYS wins —
     we only fill a family the product has none of — so nothing anyone tagged by
     hand is overridden, and the facets stop lying about their totals.

     Boundaries are lifted from the store's OWN tagging, not invented: measuring
     the 208 dining tables that carry both a seat_ tag and dimensions gives
     2-4 seats ≤52", 4-6 = 54-72", 6-8 = 72-89", 8-10 = 92-108", 10+ = 108"+.
     The clean gaps between those ranges are what make the mapping safe. */
  /* ── Shape & bed-size corrections — Dino's photo review, 2026-08-07 ──
     Source: worksheets/shape-review-2026-08-07.csv (regenerate from it; don't
     hand-edit). 155 products the tags never carried and no measurement can settle:
     equal width and depth means round OR square, and only the photograph knows.
     The 12 multi-size beds come from their VARIANTS, not the review — a bed sold
     King/Queen/Cal-King belongs under all three, which a single-answer form
     couldn't express. Applied like the derived dimension tags: a real tag on the
     product always wins. */
  var EGA_TAG_OVERRIDES = {
    'anglo-indian-hand-carved-storage-trunk-64-inches':['shape__rectangular'],
    'antiqued-bistro-table-with-scrolled-pedestal-42-inch':['shape__round'],
    'antiqued-italian-trestle-dining-table-84-turned-pedestal-base':['shape__rectangular'],
    'antiqued-oak-bistro-table-with-turned-pedestal-33-inch':['shape__square'],
    'arched-country-wood-king-bed':['size__king'],
    'art-deco-shagreen-side-chest-in-leaf-green-on-brass':['shape__rectangular'],
    'art-deco-upholstered-bed':['size__queen','size__king','size__cal-king'],
    'art-deco-white-center-table':['shape__round'], 'biedermeier-style-walnut-table':['shape__round'],
    'black-mid-century-canopy-bed':['size__king','size__queen','size__cal-king'],
    'black-mid-century-style-bed':['size__king','size__queen','size__cal-king'],
    'blond-pine-trestle-side-table':['shape__rectangular'],
    'brass-round-cocktail-table-on-wheels-tiered-glass':['shape__round'],
    'campaign-nesting-tables-tooled-leather-iron-set-of-3':['shape__rectangular'],
    'carved-giltwood-gothic-mirror-strawberry-hill-pier-glass':['shape__rectangular'],
    'carved-queen-anne-mirror':['shape__rectangular'],
    'cerused-grey-pine-side-table-with-open-shelf':['shape__rectangular'],
    'chippendale-giltwood-mirror-rococo-style-pier-mirror':['shape__rectangular'],
    'chippendale-hall-table-with-carved-fretwork-and-marble-top':['shape__rectangular'],
    'chippendale-mahogany-side-table-with-blind-fretwork':['shape__rectangular'],
    'chippendale-overmantle-mirror':['shape__rectangular'], 'classic-empire-center-table-oatmeal':['shape__round'],
    'classic-empire-center-table-weathered-finish':['shape__round'],
    'classic-modern-frame-bed-light':['size__queen'],
    'coastal-breeze-panel-bed':['size__king','size__cal-king','size__queen'], 'coastal-sea-glass-bed':['size__king'],
    'contemporary-round-jupe-dining-table-medium-brown-oak-with-stainless-steel-trim':['shape__round'],
    'country-accent-table-with-storage':['shape__rectangular'], 'country-round-side-table':['shape__round'],
    'country-wood-king-bed-with-paneled-headboard':['size__king'], 'daylight-modern-bed':['size__king'],
    'distressed-gray-pine-trunk-side-table-with-storage':['shape__rectangular'],
    'embossed-leather-storage-box-on-faux-bamboo-iron-stand':['shape__rectangular'],
    'english-georgian-overmantle-mirror-egg-and-dart-overmantel':['shape__rectangular'],
    'english-giltwood-hall-table-in-the-george-ii-style':['shape__rectangular'],
    'english-inlaid-pedestal-center-table':['shape__round'], 'european-style-canopy-bed':['size__king'],
    'farmhouse-dining-table-in-antiqued-pine-89-inches':['shape__oval'],
    'farmhouse-dining-table-in-reclaimed-pine-with-turned-legs':['shape__rectangular'],
    'farmhouse-double-x-base-console-table-in-pine-93-inch':['shape__rectangular'],
    'finely-inlaid-mahogany-center-table':['shape__round'],
    'french-country-breakfast-table-in-warm-blond-hardwood-40-inch':['shape__round'],
    'french-country-console-table-with-turned-legs-72-inch':['shape__rectangular'],
    'french-country-petite-pedestal-table':['shape__round'], 'french-empire-style-center-table':['shape__round'],
    'george-i-hall-table-with-marble-top':['shape__rectangular'],
    'george-i-mirror-with-carved-frame':['shape__rectangular'],
    'george-i-mirror-with-carved-shells':['shape__rectangular'],
    'george-ii-claw-foot-side-table':['shape__rectangular'],
    'george-ii-giltwood-mirror-in-the-manner-of-william-kent':['shape__rectangular'],
    'george-ii-giltwood-over-mantle-shell-mirror':['shape__rectangular'],
    'george-ii-mirror-with-scroll-top-and-sconce-arms':['shape__rectangular'],
    'george-ii-walnut-and-gilt-scroll-top-mirror':['shape__rectangular'],
    'george-iii-adam-border-glass-pier-mirror':['shape__rectangular'],
    'george-iii-adam-pier-mirror-carved-giltwood':['shape__rectangular'],
    'george-iii-chippendale-mirror-with-ho-ho-bird-surmount':['shape__rectangular'],
    'george-iii-giltwood-pier-mirror-with-fluted-frame':['shape__rectangular'],
    'george-iii-mirror-in-the-hepplewhite-manner':['shape__rectangular'],
    'george-iii-mirror-with-carved-flowers':['shape__rectangular'],
    'george-iii-over-mantle-mirror':['shape__rectangular'], 'george-iii-parquetry-center-table':['shape__round'],
    'george-iii-pier-table-in-the-adam-style-with-anthemion-decoration':['shape__rectangular'],
    'george-iii-traditional-english-giltwood-rococo-mirror':['shape__rectangular'],
    'george-iv-convex-mirror':['shape__round'], 'georgian-bedside-table':['shape__rectangular'],
    'gothic-over-mantle-mirror':['shape__rectangular'],
    'gray-oak-expanding-jupe-dining-table-85-1-2':['shape__round'], 'grey-art-deco-bed-frame':['size__king'],
    'grey-painted-neo-classic-center-table':['shape__round'],
    'gustavian-cream-painted-four-poster-bed':['size__king'],
    'gustavian-grey-painted-four-poster-bed':['size__king','size__queen'],
    'hollywood-regency-round-coffee-table-in-antique-gold':['shape__round'],
    'industrial-bistro-table-with-solid-walnut-top-32-inch-round':['shape__round'],
    'industrial-console-table-with-parquet-top-and-iron-trestle':['shape__rectangular'],
    'industrial-two-tier-round-coffee-table-in-grey-wood-and-iron':['shape__round'],
    'irish-antique-style-mahogany-silver-table':['shape__rectangular'],
    'large-art-deco-round-jupe-dining-table-with-stainless-steel-trim':['shape__round'],
    'large-george-iii-adam-border-glass-pier-mirror':['shape__rectangular'],
    'large-gothic-over-mantle-mirror':['shape__rectangular'], 'light-oak-canopy-bed':['size__king'],
    'louis-xvi-mahogany-center-table':['shape__round'], 'marble-top-oak-center-table':['shape__round'],
    'mid-century-canopy-bed':['size__king','size__queen','size__cal-king'],
    'mid-century-channeled-bed':['size__king','size__cal-king','size__queen'],
    'mid-century-modern-platform-bed':['size__king'],
    'mid-century-style-bed':['size__king','size__queen','size__cal-king'], 'midnight-modern-bed':['size__king'],
    'ming-style-coffee-table-in-warm-brown-pine':['shape__square'],
    'ming-style-ebonized-pine-coffee-table':['shape__square'],
    'ming-style-low-coffee-table-in-antique-white-pine':['shape__square'],
    'ming-style-square-coffee-table-in-tan-reclaimed-pine':['shape__square'],
    'minimalist-a-frame-console-table-in-aged-pine-68-inch':['shape__rectangular'],
    'modern-framed-and-upholstered-bed':['size__king','size__cal-king','size__queen'],
    'modern-framed-and-upholstered-bed-light-oak':['size__king','size__queen'],
    'modern-industrial-bed':['size__king'],
    'modern-light-oak-and-leather-upholstered-bed':['size__king','size__queen'],
    'modern-round-drum-side-table-in-white-ash-with-open-niche':['shape__round'],
    'modern-white-ash-console-with-drawers-and-waterfall-frame':['shape__rectangular'],
    'neoclassical-accent-table-in-cast-aluminum-with-tobacco-leaf-top':['shape__round'],
    'octagonal-convex-mirror-decorated-with-black-stars':['shape__round'],
    'organic-modern-faceted-drum-cocktail-table-42-inches':['shape__round'],
    'queen-anne-gilt-frame-mirror-with-shell-decoration':['shape__rectangular'],
    'queen-anne-giltwood-wall-sconce':['shape__rectangular'],
    'queen-anne-mirror-carved-giltwood-shell':['shape__rectangular'],
    'queen-anne-pier-glass-silver-gilt-finish':['shape__rectangular'],
    'reclaimed-pine-mantel-console-table-with-stepped-molding':['shape__rectangular'],
    'regency-breakfast-table-with-inlaid-tilt-top':['shape__round'], 'regency-convex-mirror':['shape__round'],
    'regency-convex-mirror-with-ball-decoration':['shape__round'],
    'regency-convex-mirror-with-black-stars':['shape__round'],
    'regency-convex-mirror-with-carved-eagle-surmount':['shape__round'],
    'regency-convex-mirror-with-egg-and-dart-molding':['shape__round'],
    'regency-convex-mirror-with-gold-star-decoration':['shape__round'],
    'regency-convex-mirror-with-leopard-mask':['shape__round'], 'regency-ebonized-convex-mirror-1':['shape__round'],
    'regency-ebonized-octagonal-convex-mirror':['shape__round'],
    'regency-overmantle-mirror-english-reproduction-mirror':['shape__rectangular'],
    'regency-pier-glass-decorated-with-flowers':['shape__rectangular'],
    'regency-pier-mirror-with-ball-decoration':['shape__rectangular'],
    'regency-pier-mirror-with-eagle-surmount':['shape__rectangular'],
    'regency-pier-mirror-with-star-decoration':['shape__rectangular'],
    'regency-roman-carved-marble-top-center-table':['shape__round'],
    'regency-satinwood-tilt-top-center-table':['shape__round'], 'regency-serpent-convex-mirror':['shape__round'],
    'regency-style-center-table-2':['shape__round'], 'regency-style-center-table-4':['shape__round'],
    'regency-style-convex-mirror-soane-convex-gilt':['shape__round'],
    'regency-style-octagonal-convex-mirror-1':['shape__round'],
    'regency-twin-serpent-convex-mirror':['shape__round'],
    'regency-walnut-pedestal-center-table':['shape__rectangular'],
    'round-walnut-drum-coffee-table-with-storage-drawer':['shape__round'],
    'round-white-reeded-pedestal-jupe-dining-table-with-expanding-top':['shape__round'],
    'rustic-carved-leg-console-table-in-natural-pine-90-inch':['shape__rectangular'],
    'rustic-iron-console-table-with-distressed-marble-top':['shape__rectangular'],
    'rustic-oak-spanish-style-center-table':['shape__round'],
    'rustic-stacked-plank-waterfall-coffee-table-in-stone-grey':['shape__rectangular'],
    'rustic-washed-pencil-post-bed':['size__king'],
    'scandinavian-two-tier-drum-coffee-table-in-grey-pine':['shape__round'],
    'small-country-wood-coffee-table':['shape__rectangular'], 'small-gothic-pier-mirror':['shape__rectangular'],
    'stone-gray-dining-table-with-starburst-pedestal-base':['shape__rectangular'],
    'swedish-carved-trunk-coffee-table-in-antique-grey':['shape__rectangular'],
    'tall-queen-anne-mirror':['shape__rectangular'],
    'traditional-square-dining-and-game-table-in-warm-sienna':['shape__square'],
    'transitional-81-inch-dining-table-with-porcelain-calacatta-top-and-turned-trestle-base':['shape__rectangular'],
    'transitional-round-jupe-dining-table-large-light-brown-oak-with-hammered-iron-ba':['shape__round'],
    'transitional-round-jupe-dining-table-large-white-oak-with-hammered-iron-base':['shape__round'],
    'turned-geometric-side-tables-in-natural-elm-set-of-3':['shape__round'],
    'upholstered-art-deco-bed':['size__king','size__queen','size__cal-king'],
    'warm-brown-oak-expanding-jupe-dining-table-71':['shape__round'],
    'warm-brown-oak-expanding-jupe-dining-table-85':['shape__round'],
    'white-contemporary-round-drum-cocktail-table-56':['shape__round'],
    'white-drum-coffee-table-with-storage-drawer':['shape__round'],
    'white-oak-expanding-jupe-dining-table-85-1-2':['shape__round'],
    'white-reeded-expanding-jupe-dining-table-70':['shape__round'],
  };

  var DIM_W_BINS = [[24, 'dim_w_under-24in'], [48, 'dim_w_24-48in'], [72, 'dim_w_48-72in'], [Infinity, 'dim_w_over-72in']];
  var DIM_D_BINS = [[18, 'dim_d_under-18in'], [24, 'dim_d_18-24in'], [36, 'dim_d_24-36in'], [Infinity, 'dim_d_over-36in']];
  var DIM_H_BINS = [[24, 'dim_h_under-24in'], [30, 'dim_h_24-30in'], [36, 'dim_h_30-36in'], [48, 'dim_h_36-48in'], [Infinity, 'dim_h_over-48in']];
  var LEN_BINS   = [[36, 'len_under-36in'], [48, 'len_36-48in'], [60, 'len_48-60in'], [72, 'len_60-72in'],
                    [84, 'len_72-84in'], [96, 'len_84-96in'], [108, 'len_96-108in'], [Infinity, 'len_108-plus']];
  var SEAT_BINS  = [[54, 'seat_2-4'], [72, 'seat_4-6'], [92, 'seat_6-8'], [108, 'seat_8-10'], [Infinity, 'seat_10-plus']];

  function binTag(value, bins) {
    for (var i = 0; i < bins.length; i++) if (value < bins[i][0]) return bins[i][1];
    return bins[bins.length - 1][1];
  }
  function lacksFamily(tags, prefix) {
    for (var i = 0; i < tags.length; i++) if (tags[i].indexOf(prefix) === 0) return false;
    return true;
  }

  function applyDerivedDimTags() {
    if (!state.dimsSettled || !state.loaded || !state.productDims) return;
    state.allProducts.forEach(function (p) {
      if (p._dimsDerived) return;           /* idempotent — buildSidebar runs more than once */
      /* Reviewed shape / bed-size values first — these need no measurements */
      var over = EGA_TAG_OVERRIDES[p.handle];
      if (over) {
        for (var oi = 0; oi < over.length; oi++) {
          var fam = over[oi].split('__')[0] + '__';
          /* A stored shape is authoritative; adding an old photo-review shape
             beside it would make one table match two conflicting shapes. Bed
             size overrides can still list multiple legitimate variant sizes. */
          if (fam === 'shape__' && !lacksFamily(p.tags, fam)) continue;
          if (p.tags.indexOf(over[oi]) === -1) p.tags.push(over[oi]);
        }
      }
      var d = state.productDims[p.handle];
      if (!d) return;
      p._dimsDerived = true;
      var type = (p.type || '').toLowerCase();
      if (d.w && lacksFamily(p.tags, 'dim_w_')) p.tags.push(binTag(d.w, DIM_W_BINS));
      if (d.d && lacksFamily(p.tags, 'dim_d_')) p.tags.push(binTag(d.d, DIM_D_BINS));
      if (d.h && lacksFamily(p.tags, 'dim_h_')) p.tags.push(binTag(d.h, DIM_H_BINS));
      /* Length is a table idea — the longer footprint dimension. */
      if (d.w && type.indexOf('table') !== -1 && lacksFamily(p.tags, 'len_')) {
        p.tags.push(binTag(Math.max(d.w, d.d || 0), LEN_BINS));
      }
      /* Seating is DINING-table only: a chair is not "4-6 seats". */
      if (d.w && type === 'dining tables' && lacksFamily(p.tags, 'seat_')) {
        p.tags.push(binTag(Math.max(d.w, d.d || 0), SEAT_BINS));
      }
    });
  }

  function applyFilters() {
    /* Dataset not loaded yet (the server-rendered first page is showing). Kick off the
       deferred load — it calls applyFilters() again when ready — and leave the server
       cards in place rather than wiping the grid to empty. */
    if (!state.loaded) { ensureDataset(); return; }
    var products = state.allProducts.slice();

    // In Stock collection pre-scope (see autoSelectFromCollection)
    if (state.implicitInstock) {
      products = products.filter(function (p) { return p.inStock; });
    }

    // Room collection pre-scope (e.g. Living Room, Dining Room)
    if (state.roomTag) {
      products = products.filter(function (p) { return p.tags.indexOf(state.roomTag) !== -1; });
    }

    // Century collection pre-scope (e.g. 18th century)
    // Combined check: product must have the century TAG + at least one matching STYLE tag.
    // This prevents multi-century-tagged products (e.g. Regency tagged 18th+19th+20th) from polluting results,
    // AND prevents overly broad style tags (rustic, transitional) from pulling in wrong-era products.
    if (state.centuryTag) {
      var centuryStyles = CENTURY_STYLE_MAP[state.centuryTag];
      if (centuryStyles) {
        products = products.filter(function (p) {
          var hasCenturyTag = p.tags.indexOf(state.centuryTag) !== -1;
          var hasStyleTag = centuryStyles.some(function (s) { return p.tags.indexOf(s) !== -1; });
          return hasCenturyTag && hasStyleTag;
        });
      } else {
        products = products.filter(function (p) { return p.tags.indexOf(state.centuryTag) !== -1; });
      }
    }

    // Room pre-filter for style collections
    if (state.isStyleCollection && state.activeRoom) {
      products = getProductsForRoom(products, state.activeRoom);
    }

    // Product Type filter (explicit user selections OR implicit from parent collections)
    var applyTypeValues = state.activeFilters['product-type'] && state.activeFilters['product-type'].size
      ? state.activeFilters['product-type']
      : (state.implicitTypes && state.implicitTypes.size ? state.implicitTypes : null);
    if (applyTypeValues) {
      products = products.filter(function (p) { return applyTypeValues.has(p.type); });
    }

    // Unified Style filter — split centuries (AND) from individual styles (OR)
    if (state.activeFilters['style-unified'] && state.activeFilters['style-unified'].size) {
      var styleValues = state.activeFilters['style-unified'];

      // Separate century selections from individual style selections
      var centurySelections = [];
      var individualStyles = [];
      styleValues.forEach(function (s) {
        if (CENTURY_STYLE_MAP[s]) {
          centurySelections.push(s);
        } else {
          individualStyles.push(s);
        }
      });

      // Century selections act as AND — product must have the century TAG
      // This narrows results (e.g. Neoclassical + 20th Century = only neoclassical items from the 20th century)
      if (centurySelections.length > 0) {
        products = products.filter(function (p) {
          // Product must match at least one selected century tag (OR between centuries, AND with styles)
          return centurySelections.some(function (c) { return p.tags.indexOf(c) !== -1; });
        });
      }

      // Individual style selections act as OR — product must match any selected style
      if (individualStyles.length > 0) {
        products = products.filter(function (p) {
          return individualStyles.some(function (s) { return p.tags.indexOf(s) !== -1; });
        });
      }
    }

    // In Stock filter — "Stock Items" tag + available = truly in stock
    if (state.instockOnly) {
      products = products.filter(function (p) {
        return p.inStock;
      });
    }

    // Customizable boolean filter
    if (state.customizableOnly) {
      products = products.filter(function (p) { return p.tags.indexOf('customizable') !== -1; });
    }

    // Tag group filters (AND between groups, OR within group)
    Object.keys(state.activeFilters).forEach(function (groupId) {
      var values = state.activeFilters[groupId];
      if (!values || !values.size) return;

      // Product-type and style-unified already handled above
      if (groupId === 'product-type' || groupId === 'style-unified') return;

      // Style group: Trad/Modern mutually exclusive; Rustic is cross-cutting attribute
      if (groupId === 'style') {
        var hasStyleFilter = values.has('traditional') || values.has('modern');
        var hasRusticFilter = values.has('rustic');
        var hasJupeFilter = values.has('jupe');
        // Jupe alone = show only jupe
        if (hasJupeFilter && !hasStyleFilter && !hasRusticFilter) {
          products = products.filter(function (p) { return p.tags.indexOf('jupe tables') !== -1; });
          return;
        }
        products = products.filter(function (p) {
          var isModern = STYLE_MODERN.some(function (s) { return p.tags.indexOf(s) !== -1; });
          var isRustic = STYLE_RUSTIC.some(function (s) { return p.tags.indexOf(s) !== -1; });
          var isJupe = p.tags.indexOf('jupe tables') !== -1;
          // If jupe is checked along with other filters, jupe products always pass
          if (hasJupeFilter && isJupe) return true;
          // If rustic is checked (alone or with style), product must be rustic
          if (hasRusticFilter && !isRustic) return false;
          // If only rustic is checked (no trad/modern), any rustic passes
          if (hasRusticFilter && !hasStyleFilter) return true;
          // If style filters are active, check trad/modern
          if (hasStyleFilter) {
            if (values.has('modern') && isModern) return true;
            if (values.has('traditional') && !isModern) return true;
            return false;
          }
          return true;
        });
        return;
      }

      // Depth group: handle "under36"/"over36" virtual tags for sofas
      if (groupId === 'dimd' && (values.has('under36') || values.has('over36'))) {
        products = products.filter(function (p) {
          if (values.has('over36') && p.tags.indexOf('dim_d_over-36in') !== -1) return true;
          if (values.has('under36') && (p.tags.indexOf('dim_d_24-36in') !== -1 || p.tags.indexOf('dim_d_18-24in') !== -1 || p.tags.indexOf('dim_d_under-18in') !== -1)) return true;
          return false;
        });
        return;
      }

      // Width group: handle "under84"/"over84" using actual dims
      // Side table width (tag-based)
      if (groupId === 'dimw' && (state.collectionType === 'table' || state.collectionType === 'console' || state.collectionType === 'nightstand')) {
        products = products.filter(function (p) {
          if (values.has('under24') && p.tags.indexOf('dim_w_under-24in') !== -1) return true;
          if (values.has('24to48') && (p.tags.indexOf('dim_w_24-48in') !== -1 || p.tags.indexOf('dim_w_48-72in') !== -1 || p.tags.indexOf('dim_w_over-72in') !== -1)) return true;
          if (values.has('other')) {
            var hasDimW = ['dim_w_under-24in','dim_w_24-48in','dim_w_48-72in','dim_w_over-72in'].some(function(t) { return p.tags.indexOf(t) !== -1; });
            if (!hasDimW) return true;
          }
          return false;
        });
        return;
      }

      // Coffee table length (tag-based)
      if (groupId === 'dimw' && state.collectionType === 'coffee') {
        products = products.filter(function (p) {
          if (values.has('under36') && p.tags.indexOf('len_under-36in') !== -1) return true;
          if (values.has('36to48') && p.tags.indexOf('len_36-48in') !== -1) return true;
          if (values.has('48to60') && p.tags.indexOf('len_48-60in') !== -1) return true;
          if (values.has('60plus') && (p.tags.indexOf('len_60-72in') !== -1 || p.tags.indexOf('len_72-84in') !== -1 || p.tags.indexOf('len_84-96in') !== -1 || p.tags.indexOf('len_96-108in') !== -1 || p.tags.indexOf('len_108-plus') !== -1)) return true;
          if (values.has('other')) {
            var hasLen = ['len_under-36in','len_36-48in','len_48-60in','len_60-72in','len_72-84in','len_84-96in','len_96-108in','len_108-plus'].some(function(t) { return p.tags.indexOf(t) !== -1; });
            if (!hasLen) return true;
          }
          return false;
        });
        return;
      }

      if (groupId === 'dimw' && (values.has('under84') || values.has('over84'))) {
        products = products.filter(function (p) {
          var dim = state.productDims && state.productDims[p.handle];
          if (dim && dim.w) {
            if (values.has('over84') && dim.w >= 84) return true;
            if (values.has('under84') && dim.w < 84) return true;
          } else {
            // Fallback to tags
            if (values.has('over84') && p.tags.indexOf('dim_w_over-72in') !== -1) return true;
            if (values.has('under84') && (p.tags.indexOf('dim_w_48-72in') !== -1 || p.tags.indexOf('dim_w_24-48in') !== -1 || p.tags.indexOf('dim_w_under-24in') !== -1)) return true;
          }
          return false;
        });
        return;
      }

      // Dining table: Seating (calculated from width)
      if (groupId === 'dt-seating') {
        products = products.filter(function (p) {
          if (values.has('s2_4') && p.tags.indexOf('seat_2-4') !== -1) return true;
          if (values.has('s4_6') && p.tags.indexOf('seat_4-6') !== -1) return true;
          if (values.has('s6_8') && p.tags.indexOf('seat_6-8') !== -1) return true;
          if (values.has('s8_10') && p.tags.indexOf('seat_8-10') !== -1) return true;
          if (values.has('s10_plus') && p.tags.indexOf('seat_10-plus') !== -1) return true;
          if (values.has('s_unknown')) {
            var hasSeat = ['seat_2-4','seat_4-6','seat_6-8','seat_8-10','seat_10-plus'].some(function(t) { return p.tags.indexOf(t) !== -1; });
            if (!hasSeat) return true;
          }
          return false;
        });
        return;
      }

      // Dining table: Length (tag-based)
      if (groupId === 'dt-length') {
        products = products.filter(function (p) {
          if (values.has('l_under60') && (p.tags.indexOf('len_48-60in') !== -1 || p.tags.indexOf('len_under-36in') !== -1 || p.tags.indexOf('len_36-48in') !== -1)) return true;
          if (values.has('l_60_72') && p.tags.indexOf('len_60-72in') !== -1) return true;
          if (values.has('l_72_84') && p.tags.indexOf('len_72-84in') !== -1) return true;
          if (values.has('l_84_96') && p.tags.indexOf('len_84-96in') !== -1) return true;
          if (values.has('l_96_108') && p.tags.indexOf('len_96-108in') !== -1) return true;
          if (values.has('l_108_plus') && p.tags.indexOf('len_108-plus') !== -1) return true;
          if (values.has('l_unknown')) {
            var hasLen = ['len_under-36in','len_36-48in','len_48-60in','len_60-72in','len_72-84in','len_84-96in','len_96-108in','len_108-plus'].some(function(t) { return p.tags.indexOf(t) !== -1; });
            if (!hasLen) return true;
          }
          return false;
        });
        return;
      }

      // Dining table: Extension vs Fixed
      if (groupId === 'dt-extension') {
        products = products.filter(function (p) {
          return values.has(diningExtensionTypeOf(p));
        });
        return;
      }

      // Dining table: Finish
      // Finish: mutually exclusive (same priority as counting: Stone > Metal > Painted > Dark > Light > Other)
      if (groupId === 'dt-finish') {
        products = products.filter(function (p) { return values.has(finishBucketOf(p)); });
        return;
      }

      // Upholstery: Stocked in Fabric / Stocked in Leather (stock items only)
      if (groupId === 'upholstery') {
        products = products.filter(function (p) {
          var isStock = p.tags.indexOf('stock items') !== -1;
          if (!isStock) return false;
          var hasLeather = p.tags.indexOf('material__leather') !== -1;
          var hasFabric = p.tags.indexOf('seat__upholstered') !== -1 || p.tags.indexOf('finish__fabric') !== -1 || p.tags.indexOf('material__upholstered') !== -1 || p.tags.indexOf('material__velvet') !== -1 || p.tags.indexOf('material__linen') !== -1;
          var isFabric = hasFabric || (!hasLeather && !hasFabric);
          if (values.has('leather') && hasLeather) return true;
          if (values.has('fabric') && isFabric) return true;
          return false;
        });
        return;
      }

      if (groupId === 'shape') {
        products = products.filter(function (p) {
          return Array.from(values).some(function (value) { return shapeFacetMatches(p.tags, value); });
        });
        return;
      }

      products = products.filter(function (p) {
        var valArr = Array.from(values);
        for (var i = 0; i < valArr.length; i++) {
          if (p.tags.indexOf(valArr[i]) !== -1) return true;
        }
        return false;
      });
    });

    // Price filter (always include $0 / "price on request" unless user set a min price)
    products = products.filter(function (p) {
      if (p.price === 0) return state.priceMinActive === 0;
      var d = p.price / 100;
      return d >= state.priceMinActive && d <= state.priceMaxActive;
    });

    // Sort
    products = sortProducts(products, state.sort);
    state.filtered = products;
    renderGrid(products);
    updateChips();
    updateClearBtn();
    updateCount();
    consumePendingReturn();
  }

  function sortProducts(products, sort) {
    var s = products.slice();
    var handle = state.collectionHandle || '';

    /* Collection position sort: use Shopify's manual sort order for the current collection */
    function collectionPos(p) {
      if (p.collectionPositions && typeof p.collectionPositions[handle] === 'number') {
        return p.collectionPositions[handle];
      }
      return 999999; /* products not in this collection sort to the end */
    }

    switch (sort) {
      case 'price-asc':  s.sort(function (a, b) { return a.price - b.price; }); break;
      case 'price-desc': s.sort(function (a, b) { return b.price - a.price; }); break;
      case 'newest':     s.sort(function (a, b) { return new Date(b.createdAt) - new Date(a.createdAt); }); break;
      case 'title-asc':  s.sort(function (a, b) { return a.title.localeCompare(b.title); }); break;
      case 'featured':
      default:
        /* Default sort: use Shopify collection position (manual sort order) */
        if (handle) {
          s.sort(function (a, b) { return collectionPos(a) - collectionPos(b); });
        }
        break;
    }

    return s;
  }

  // ── Render grid ───────────────────────────────────────────────────────────
  /* REVERSED (Dino, round 7): breadcrumbs are canonical by product type now, so the
     round-2 collection-prefixed URLs are superseded — plain product URLs only.
     Mirrors snippets/ega-cf-card.liquid. */
  function cardUrl(p) {
    return p.url;
  }

  function buildCard(p) {
    // CDN-sized card images (Dan, 2026-07-07): ?width= param + width-descriptor
    // srcset with sizes, so the browser picks for the real card width (2-col
    // ~50vw mobile / 3-col desktop) instead of over-downloading on high-DPR
    // screens. Replaces the legacy filename-suffix crop. Guarded by
    // collection-check.mjs and the js-assets-parse source check.
    var imgSep = p.image && p.image.indexOf('?') === -1 ? '?' : '&';
    var imgSrc = p.image ? p.image + imgSep + 'width=480' : '';
    var imgSrcset = p.image ? [360, 480, 720, 960].map(function (w) {
      return p.image + imgSep + 'width=' + w + ' ' + w + 'w';
    }).join(', ') : '';
    var imgSizes = '(max-width: 767px) 50vw, 33vw';
    var isCustom = p.tags.indexOf('customizable') !== -1;
    /* Availability truth (Dino, 2026-10-13): the label comes from the server (ega-availability
       via collection.ega-avail), identical to ega-cf-card.liquid and the PDP. Without it the
       card shows no status rather than guess one. */
    var statusText = p.availLabel || '';
    var statusClass = statusText === 'In Stock' ? 'instock' : '';

    var statusLineHtml = '';
    if (statusText || isCustom) {
      statusLineHtml = '<div class="ega-cf-card-status-line">';
      if (statusText) statusLineHtml += '<span class="ega-cf-card-status ' + statusClass + '">' + esc(statusText) + '</span>';
      if (statusText && isCustom) statusLineHtml += '<span class="ega-cf-status-divider"></span>';
      if (isCustom) statusLineHtml += '<span class="ega-cf-custom-tag">Customizable</span>';
      statusLineHtml += '</div>';
    }

    var imgZoom = 1;
    p.tags.forEach(function (t) { if (t.indexOf('imgzoom_') === 0) imgZoom = parseFloat(t.replace('imgzoom_', '')) || 1; });
    var imgStyle = imgZoom > 1 ? ' style="--img-zoom:' + imgZoom + '"' : '';

    var dimsHtml = '';
    var dimsMobileHtml = '';
    var dimData = state.productDims && state.productDims[p.handle];
    if (dimData) {
      var parts = [];
      function fmtDim(v) { return v === Math.floor(v) ? v.toString() : v % 1 === 0.5 ? v.toString() : Math.round(v).toString(); }
      if (dimData.w) parts.push(fmtDim(dimData.w) + '"W');
      if (dimData.d) parts.push(fmtDim(dimData.d) + '"D');
      if (dimData.h) parts.push(fmtDim(dimData.h) + '"H');
      if (parts.length) {
        dimsHtml = '<div class="ega-cf-card-dims"><span class="ega-cf-card-dims-text">' + parts.join('  ×  ') + '</span></div>';
        dimsMobileHtml = '<span class="ega-cf-card-dims-mobile">' + parts.join(' × ') + '</span>';
      }
    }

    var card = document.createElement('article');
    card.className = 'ega-cf-card';
    card.setAttribute('data-product-handle', p.handle);
    /* Check if this product is favorited (EGA Favorites integration) */
    var favClass = (window.EGA_FAVORITES && window.EGA_FAVORITES.isFavorited(p.handle)) ? ' is-fav' : '';
    var favBtnHtml = '<button class="ega-fav-btn' + favClass + '" data-handle="' + esc(p.handle) + '" data-source="collection" aria-label="Add to favorites">' +
      '<svg class="ega-fav-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>' +
      '</svg></button>';

    // Trade-aware pricing: logged-in trade-approved customers see net price + NET PRICE label
    // Vaughan / Antiques = 20% off (smaller margin), everything else = 40% off
    // Mirrors logic in snippets/ega-trade-price.liquid + extensions/trade-auto-discount/src/run.js
    var isTrade = window.EGA_IS_TRADE === true;
    var tradePriceHtml = '';
    if (isTrade && p.price > 0) {
      var restrictedVendors = ['vaughan', 'antiques'];
      var vendorLower = (p.vendor || '').toLowerCase();
      var tradePct = restrictedVendors.indexOf(vendorLower) !== -1 ? 20 : 40;
      var tradePriceCents = Math.round(p.price * (1 - tradePct / 100));
      tradePriceHtml = '<span class="ega-cf-card-price">' + formatMoney(tradePriceCents) +
        '<span class="ega-cf-card-net-label" style="font-family:Libre Franklin,sans-serif;font-size:9px;font-weight:600;letter-spacing:0.18em;text-transform:uppercase;color:currentColor;margin-left:6px;opacity:0.75;">Net Price</span>' +
        '</span>';
    }

    var priceHtml;
    if (tradePriceHtml) {
      priceHtml = tradePriceHtml;
    } else if (p.comparePrice && p.comparePrice > p.price) {
      priceHtml = '<div class="ega-cf-card-price-wrap">' +
          '<span class="ega-cf-card-price ega-cf-card-price--sale">' + formatMoney(p.price) + '</span>' +
          '<span class="ega-cf-card-price-was">' + formatMoney(p.comparePrice) + '</span>' +
          '<span class="ega-cf-card-price-pill">Sale</span>' +
        '</div>';
    } else {
      var formattedPrice = formatMoney(p.price);
      // EGA: When price is $0, omit the entire price element to avoid empty whitespace.
      priceHtml = formattedPrice ? '<span class="ega-cf-card-price">' + formattedPrice + '</span>' : '';
    }

    card.innerHTML = [
      '<a href="' + esc(cardUrl(p)) + '" class="ega-cf-card-img-wrap">',
      imgSrc ? '  <img src="' + esc(imgSrc) + '" srcset="' + esc(imgSrcset) + '" sizes="' + imgSizes + '" alt="' + esc(p.title) + '" loading="lazy"' + imgStyle + '>' : '',
      dimsHtml,
      '</a>',
      favBtnHtml,
      '<div class="ega-cf-card-info">',
      statusLineHtml,
      '  <a href="' + esc(cardUrl(p)) + '" class="ega-cf-card-title">' + esc(p.title) + '</a>',
      '  ' + priceHtml,
      dimsMobileHtml,
      '</div>',
    ].join('');

    /* Bind the favorite button click */
    var favBtn = card.querySelector('.ega-fav-btn');
    if (favBtn) {
      favBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var handle = this.getAttribute('data-handle');
        if (window.EGA_FAVORITES) {
          window.EGA_FAVORITES.toggle(handle, 'collection');
        } else {
          /* Fallback if favorites JS hasn't loaded yet — toggle manually via localStorage */
          try {
            var favs = JSON.parse(localStorage.getItem('ega_favorites') || '[]');
            var idx = favs.indexOf(handle);
            if (idx !== -1) { favs.splice(idx, 1); this.classList.remove('is-fav'); }
            else { favs.push(handle); this.classList.add('is-fav'); this.classList.add('ega-fav-pop'); }
            localStorage.setItem('ega_favorites', JSON.stringify(favs));
          } catch (ex) {}
        }
      });
    }

    return card;
  }

  function renderGrid(products) {
    var grid = document.getElementById('ega-cf-grid');
    var empty = document.getElementById('ega-cf-empty');
    var loading = document.getElementById('ega-cf-loading');
    if (loading) loading.style.display = 'none';
    if (!products.length) {
      if (grid) { grid.innerHTML = ''; grid.style.minHeight = ''; }
      if (empty) empty.classList.add('visible');
      state.renderedCount = 0;
      return;
    }
    if (empty) empty.classList.remove('visible');
    if (!grid) return;

    /* Server page 2+ (see init): leave the server cards and pager in place once, and
       mark everything rendered so infinite scroll does not append page 1's products. */
    if (state.keepServerPage) {
      state.keepServerPage = false;
      if (grid.querySelector('.ega-cf-card')) {
        state.renderedCount = products.length;
        grid.style.minHeight = '';
        return;
      }
    }
    /* The JS grid now owns the listing; the server pager no longer matches it. */
    var pager = document.getElementById('ega-cf-pager');
    if (pager) pager.hidden = true;

    /* Paginated: render first PAGE_SIZE, infinite scroll handles the rest */
    grid.innerHTML = '';
    var _icfg = interruptConfig();
    var end = Math.min(PAGE_SIZE, products.length);
    for (var i = 0; i < end; i++) {
      grid.appendChild(buildCard(products[i]));
      maybeInterrupt(grid, i, _icfg);
    }
    state.renderedCount = end;
    /* Release the height reserved by init() for deep-linked loads (CLS fix, Sep 16 2026).
       Card frames are fixed-ratio, so the rendered height is final at this point. */
    grid.style.minHeight = '';
  }

  function updateCount() {
    var countEl = document.getElementById('ega-cf-count');
    var drawerCountEl = document.getElementById('ega-cf-drawer-count');
    if (!countEl) return;
    var total = state.filtered.length;
    countEl.textContent = total + ' product' + (total !== 1 ? 's' : '');
    /* Drawer footer CTA stays live with the current result count */
    if (drawerCountEl) drawerCountEl.textContent = 'View ' + total + ' piece' + (total !== 1 ? 's' : '');
  }

  // ── Chips & Clear ─────────────────────────────────────────────────────────
  function updateChips() {
    var container = document.getElementById('ega-cf-chips');
    if (!container) return;
    container.innerHTML = '';
    /* While the toolbar's In Stock toggle is visible it already shows the state and
       releases it, so the removable pill would be a duplicate AND a layout shift: the
       active row appearing pushed the grid down on the toggle's first click. */
    var _toggleVisible = quickItems.instock && quickItems.instock.toggle && quickItems.instock.item.style.display !== 'none';
    if (state.instockOnly && !_toggleVisible) {
      var btn1 = document.createElement('button');
      btn1.className = 'ega-cf-chip';
      btn1.innerHTML = 'In Stock <span class="ega-cf-chip__x">&times;</span>';
      btn1.addEventListener('click', function () {
        state.instockOnly = false;
        /* Scope to the drawer originals — the toolbar quick-panel CLONES match these
           attribute selectors too and sit earlier in the DOM; unchecking the clone
           leaves the original checked and updateQuickState re-syncs the stale state
           back onto the clone (chip removal then never visually clears). */
        var cb = document.querySelector('#ega-cf-sidebar-body input[data-boolean="instockOnly"], #ega-cf-sidebar-body input[data-avail="instock"]');
        if (cb) cb.checked = false;
        applyFilters(); updateUrl(); updateChips(); updateClearBtn();
      });
      container.appendChild(btn1);
    }
    if (state.customizableOnly) {
      var btn2 = document.createElement('button');
      btn2.className = 'ega-cf-chip';
      btn2.innerHTML = 'Customizable <span class="ega-cf-chip__x">&times;</span>';
      btn2.addEventListener('click', function () {
        state.customizableOnly = false;
        /* Drawer-scoped for the same clone-shadowing reason as the instock chip above */
        var cb = document.querySelector('#ega-cf-sidebar-body input[data-boolean="customizableOnly"], #ega-cf-sidebar-body input[data-avail="custom"]');
        if (cb) cb.checked = false;
        applyFilters(); updateUrl(); updateChips(); updateClearBtn();
      });
      container.appendChild(btn2);
    }
    Object.keys(state.activeFilters).forEach(function (groupId) {
      var values = state.activeFilters[groupId];
      if (!values) return;
      values.forEach(function (tag) {
        var btn = document.createElement('button');
        btn.className = 'ega-cf-chip';
        btn.innerHTML = esc(tagLabel(tag)) + ' <span class="ega-cf-chip__x">&times;</span>';
        btn.addEventListener('click', function () { removeFilter(groupId, tag); });
        container.appendChild(btn);
      });
    });
    /* Toolbar shell: show/hide the active row and refresh dropdown badges */
    updateActiveRow();
    updateQuickState();
  }

  function removeFilter(groupId, tag) {
    if (!state.activeFilters[groupId]) return;
    state.activeFilters[groupId].delete(tag);
    if (!state.activeFilters[groupId].size) delete state.activeFilters[groupId];
    /* Custom groups (dt-finish, dt-seating, etc.) key their inputs on data-key.
       Drawer-scoped: the quick-panel clones match these selectors too and sit
       earlier in the DOM — see the chip handlers in updateChips. */
    var cb = document.querySelector('#ega-cf-sidebar-body input[type=checkbox][data-group="' + groupId + '"][data-tag="' + tag + '"], ' +
      '#ega-cf-sidebar-body input[type=checkbox][data-group="' + groupId + '"][data-key="' + tag + '"]');
    if (cb) cb.checked = false;
    applyFilters(); updateUrl(); updateChips(); updateClearBtn();
  }

  function updateClearBtn() {
    var btn = document.getElementById('ega-cf-clear-all');
    if (!btn) return;
    var hasActive = state.instockOnly || state.customizableOnly || Object.keys(state.activeFilters).some(function (k) {
      return state.activeFilters[k] && state.activeFilters[k].size > 0;
    });
    var priceActive = state.priceMinActive > state.priceMin || state.priceMaxActive < state.priceMax;
    btn.classList.toggle('visible', hasActive || priceActive);
  }

  /* old updateCount removed — replaced by paginated version above */

  // ── Sort ──────────────────────────────────────────────────────────────────
  function initSort() {
    /* Toolbar sort select, all viewports */
    var sortEl = document.getElementById('ega-cf-sort');
    if (!sortEl) return;
    sortEl.value = state.sort;
    sortEl.addEventListener('change', function () {
      state.sort = sortEl.value;
      applyFilters(); updateUrl();
    });
    buildSortDropdown(sortEl);
  }

  /* Chip-styled sort dropdown (Dino, 2026-08-06: the native select read as stock
     UI next to the quick-filter chips). The native <select> stays in the DOM as
     the state carrier and no-JS face — options forward through it by setting
     .value and firing 'change', so the engine sort path above is untouched.
     Registered in quickItems under 'sort' so toggleQuick/closeQuick, the
     document outside-click handler and the Esc handler all govern it exactly
     like the quick-filter panels. */
  function buildSortDropdown(sortEl) {
    /* Prefer the server-rendered shell (#ega-cf-sort-shell in the liquid) so
       the chip paints before this script loads; fall back to JS creation. The
       panel is re-rendered from the select's options on bind either way, which
       also syncs is-current when ?sort= restored a non-default value. */
    var wrap = document.getElementById('ega-cf-sort-shell');
    var chip, panel, created = false;
    if (wrap) {
      chip = wrap.querySelector('.ega-cf-sort-chip');
      panel = wrap.querySelector('.ega-cf-sort-panel');
    } else {
      created = true;
      wrap = document.createElement('div');
      wrap.className = 'ega-cf-quick-item ega-cf-sort-item';
      chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ega-cf-quick-chip ega-cf-sort-chip';
      chip.setAttribute('aria-expanded', 'false');
      chip.setAttribute('aria-haspopup', 'listbox');
      panel = document.createElement('div');
      panel.className = 'ega-cf-quick-panel ega-cf-sort-panel';
      panel.setAttribute('role', 'listbox');
      panel.setAttribute('aria-label', 'Sort products');
    }

    function currentLabel() {
      var opt = sortEl.options[sortEl.selectedIndex];
      return opt ? opt.textContent : '';
    }
    function renderChip() {
      /* The value half is CSS-hidden on mobile — "Sort ▼" alone fits the 3-up
         toolbar (Filters · count · Sort) inside a 360px viewport */
      chip.innerHTML = '<span>Sort<span class="ega-cf-sort-chip-value">: ' + esc(currentLabel()) + '</span></span>' +
        '<span class="ega-cf-quick-caret" aria-hidden="true">▼</span>';
    }
    function renderPanel() {
      panel.innerHTML = '';
      Array.prototype.forEach.call(sortEl.options, function (opt) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ega-cf-sort-option' + (opt.value === sortEl.value ? ' is-current' : '');
        b.setAttribute('role', 'option');
        b.setAttribute('aria-selected', opt.value === sortEl.value ? 'true' : 'false');
        b.textContent = opt.textContent;
        b.addEventListener('click', function () {
          sortEl.value = opt.value;
          sortEl.dispatchEvent(new Event('change'));
          renderChip(); renderPanel();
          closeQuick();
        });
        panel.appendChild(b);
      });
    }
    renderChip(); renderPanel();
    chip.addEventListener('click', function () { toggleQuick('sort'); });
    if (created) {
      wrap.appendChild(chip);
      wrap.appendChild(panel);
      sortEl.parentNode.insertBefore(wrap, sortEl);
    }
    sortEl.classList.add('ega-cf-sort-native'); /* hidden under html.ega-cf-js in CSS */
    quickItems['sort'] = { item: wrap, chip: chip, badge: null, panel: panel };
  }

  // ── Clear all ─────────────────────────────────────────────────────────────
  function initClearAll() {
    var btn = document.getElementById('ega-cf-clear-all');
    if (!btn) return;
    btn.addEventListener('click', function () {
      state.activeFilters = {};
      state.instockOnly = false;
      state.customizableOnly = false;
      state.priceMinActive = 0; /* clearing filters must keep $0 / price-on-request tables */
      state.priceMaxActive = state.priceMax;
      document.querySelectorAll('.ega-cf-option input[type=checkbox], .ega-cf-checkbox input[type=checkbox]').forEach(function (cb) { cb.checked = false; });
      var minEl = document.getElementById('ega-cf-price-min');
      var maxEl = document.getElementById('ega-cf-price-max');
      if (minEl) minEl.value = '';
      if (maxEl) maxEl.value = '';
      applyFilters(); updateUrl(); updateChips(); updateClearBtn();
    });
  }

  // ── Back-navigation return state (M1) ─────────────────────────────────────
  /* Clicking a product card saves where the customer was: the exact URL (path +
     filter params), scroll Y, and how many cards infinite scroll had rendered.
     Returning to the SAME URL re-renders to that depth and restores the scroll
     position. The grid re-renders once more when product-dimensions.json lands
     (dims lines change card heights), so consumption waits for state.dimsSettled;
     restoration is instant scroll, never smooth — it is restoration, not motion. */
  var RETURN_KEY = 'ega_cf_return';
  var pendingReturn = null;
  try {
    var _ret = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null');
    if (_ret) sessionStorage.removeItem(RETURN_KEY);
    if (_ret && _ret.url === window.location.pathname + window.location.search) {
      pendingReturn = _ret;
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      /* If the customer starts scrolling before we restore, they win */
      var cancelReturn = function () { pendingReturn = null; };
      window.addEventListener('wheel', cancelReturn, { passive: true, once: true });
      window.addEventListener('touchmove', cancelReturn, { passive: true, once: true });
      /* Safety valve: if the dims fetch hangs, restore anyway once data is in */
      setTimeout(function () {
        if (pendingReturn && state.loaded) { state.dimsSettled = true; consumePendingReturn(); }
      }, 4000);
    }
  } catch (e) {}

  function armReturnCapture() {
    var grid = document.getElementById('ega-cf-grid');
    if (!grid || grid.dataset.egaReturnArmed) return;
    grid.dataset.egaReturnArmed = '1';
    grid.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
      if (!a) return;
      try {
        sessionStorage.setItem(RETURN_KEY, JSON.stringify({
          url: window.location.pathname + window.location.search,
          y: Math.round(window.pageYOffset),
          count: Math.max(state.renderedCount, PAGE_SIZE),
        }));
      } catch (err) {}
    });
  }

  function consumePendingReturn() {
    if (!pendingReturn || !state.loaded || !state.dimsSettled) return;
    var target = pendingReturn;
    pendingReturn = null;
    var wanted = Math.min(target.count, state.filtered.length);
    while (state.renderedCount < wanted) renderNextPage();
    requestAnimationFrame(function () {
      window.scrollTo(0, target.y);
    });
  }

  // ── URL state ─────────────────────────────────────────────────────────────
  function updateUrl() {
    var params = new URLSearchParams();
    if (state.sort && state.sort !== 'featured') params.set('sort', state.sort);
    if (state.instockOnly) params.set('instock', '1');
    if (state.customizableOnly) params.set('customizable', '1');
    Object.keys(state.activeFilters).forEach(function (groupId) {
      var values = Array.from(state.activeFilters[groupId]);
      if (values.length) params.set('f_' + groupId, values.join(','));
    });
    if (state.priceMinActive > state.priceMin) params.set('price_min', state.priceMinActive);
    if (state.priceMaxActive < state.priceMax) params.set('price_max', state.priceMaxActive);
    history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
  }

  function restoreFromUrl() {
    var params = new URLSearchParams(window.location.search);
    state.sort = params.get('sort') || 'featured';
    state.instockOnly = params.get('instock') === '1';
    state.customizableOnly = params.get('customizable') === '1';
    if (params.get('price_min')) state.priceMinActive = parseFloat(params.get('price_min'));
    if (params.get('price_max')) state.priceMaxActive = parseFloat(params.get('price_max'));
    TAG_GROUPS.forEach(function (g) {
      var val = params.get('f_' + g.id);
      if (val) state.activeFilters[g.id] = new Set(val.split(','));
    });
    /* product-type / style-unified are written by updateUrl but live outside
       TAG_GROUPS — without this a shared filtered URL silently drops them */
    ['product-type', 'style-unified'].forEach(function (id) {
      var val = params.get('f_' + id);
      if (val) state.activeFilters[id] = new Set(val.split(','));
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function toggleGroup(el) {
    el.classList.toggle('open');
    var toggle = el.querySelector('.ega-cf-group-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', String(el.classList.contains('open')));
  }

  /* Custom bucket groups key their values on short ids ('dark', 'cane'), so an
     active chip read "Dark ×" / "Cane ×" instead of the option's own wording.
     These give the chips the same words the filter list uses. */
  /* Filled by buildCheckboxGroupCustom as each group renders; see the note there. */
  var DYNAMIC_LABELS = {};
  var BUCKET_LABELS = {
    light: 'Light Wood', dark: 'Dark Wood', painted: 'Painted', cane: 'Cane & Rattan',
    leather: 'Leather', linen: 'Upholstered', stone: 'Stone / Marble', metal: 'Metal / Glass',
    extension: 'Extends', fixed: 'Fixed Size'
  };
  function tagLabel(tag) {
    return BUCKET_LABELS[tag] || TAG_LABELS[tag] || DYNAMIC_LABELS[tag] ||
      tag.replace(/^[a-z]+_+/, '').replace(/-/g, ' ').replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function esc(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    // EGA: $0 = MTO/custom; status line already says "Made to Order · Customizable".
    // Return empty string so the price slot stays clean.
    if (!cents || cents === 0) return '';
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  var _dt = null;
  function debounced(fn) { clearTimeout(_dt); _dt = setTimeout(fn, 150); }

  function scrollToGrid() {
    var grid = document.getElementById('ega-cf-grid');
    var header = document.querySelector('.ega-cf-collection-header');
    var target = header || grid;
    if (target) {
      var y = target.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    initToolbar();
    initSort();
    initClearAll();
    init();
  });

})();
